import { Box, Button, InputBase, Typography, Tab, IconButton, Tooltip } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import { LocalizationProvider, MobileTimePicker, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

import CustomAxios from "../../utils/CustomAxios";
import { SketchPicker } from "react-color";
import {
  updateInventoryform,
  updateFranchises,
  updateInventryAdmins,
  updateInventryCashiers,
  updateInventryCustomerEngagementAdmins,
  updateInventrySupervisors,
} from "../../Redux/inventory";
import { updateSelectedHub } from "../../Redux/user";
import { clearAppConfigCache } from "../../utils/getAppConfig";
import { cacheAppConfigForSW } from "../../utils/cacheAppConfigForSW";
import { InventoryTable } from "../../sub_components/inventory_table";

export function AddHub() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useRef(null);
  const { id } = useParams(); // Get hub ID from URL (undefined for create mode)

  const user_role = localStorage.getItem("role");
  const isEditMode = !!id;
  console.log('🔍 Component render - id:', id, 'isEditMode:', isEditMode, 'user_role:', user_role);

  const franchises = useSelector((state) => state.inventory.franchises);
  const supervisors = useSelector((state) => state.inventory.supervisors);
  const admins = useSelector((state) => state.inventory.admins);
  const customerEngagementAdmins = useSelector(
    (state) => state.inventory.customerEngagementAdmins
  );
  const cashiers = useSelector((state) => state.inventory.cashiers);
  const inventory = useSelector((state) => state.inventory.inventory); // Get inventory from Redux

  const [inventoryform, setInventoryForm] = useState({
    place: "",
    custom_domain: "",
    supervisor_ids: [],
    admin_ids: [],
    customer_engagement_admin_ids: [],
    cashier_ids: [],
    hub_setup: "",
    pincodes: [
      {
        pincode: "",
        latitude: "",
        longitude: "",
        hub_address: "",
        inner_radius: "",
        outter_radius: "",
        radius_increase_delivery_fee: "",
        north_latitude: "",
        north_longitude: "",
        south_latitude: "",
        south_longitude: "",
        east_latitude: "",
        east_longitude: "",
        west_latitude: "",
        west_longitude: "",
      },
    ],
    delivery_fee: "",
    self_pickup_waiver: false,
    self_pickup_waiver_percentage: "0",
    delivery_status: "",
    delivery_mini_spent: "",
    shop_open_at: null,
    shop_close_at: null,
    mobileno: "",
    email: "",
    franchise_id: "",
    hub_link: "",
    isViewOnly: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [appConfigLoading, setAppConfigLoading] = useState(false);
  const [themingUploading, setThemingUploading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasAllowedChanges, setHasAllowedChanges] = useState(false); // For merchants: only hub setup/pincode and app config changes
  const merchantHubProcessedRef = useRef(null); // Track the last processed hub ID to prevent re-processing
  const [fetching, setFetching] = useState(isEditMode); // Only fetch in edit mode
  const [currentTab, setCurrentTab] = useState("hub");
  const [prerequisitesCompleted, setPrerequisitesCompleted] = useState(false); // Track when bank details + hub setup prerequisites are completed
  const [appConfigId, setAppConfigId] = useState(null); // Store AppConfig ID for update
  const [currentInventoryId, setCurrentInventoryId] = useState(
    id ? parseInt(id, 10) : null
  );
  const [isHubApproved, setIsHubApproved] = useState(false); // Track if hub is approved
  const [currentOnboardingStatus, setCurrentOnboardingStatus] = useState('draft'); // Track current onboarding status
  const [easysplitVendorStatus, setEasysplitVendorStatus] = useState(null); // Track Cashfree vendor status
  const [vendorLoading, setVendorLoading] = useState(false); // Loading state for vendor operations
  
  // Helper function to check if hub needs approval (based on onboarding_statu
  
  // Compute these values after isHubApproved is declared
  // If merchant is viewing an approved hub (edit mode), make all fields read-only
  const isMerchantViewingApprovedHub = user_role === "merchant" && isEditMode && isHubApproved;
  // If merchant has published status, make all fields read-only
  const isMerchantWithPublishedStatus = user_role === "merchant" && currentOnboardingStatus === "published";
  // If super_admin is viewing an unapproved hub, make all fields read-only and show only approve button
  const isSuperAdminViewingUnapprovedHub = user_role === "super_admin" && isEditMode && !isHubApproved;
  // Phase 1 fields should be disabled after phase1_approved for merchants (excluding bank details)
  const isMerchantPhase1Approved = user_role === "merchant" && ['phase1_approved', 'phase2_submitted', 'published'].includes(currentOnboardingStatus);
  // Determine if phase 1 fields should be disabled based on role and approval status
  const shouldDisablePhase1Fields = isMerchantViewingApprovedHub || isMerchantWithPublishedStatus || isSuperAdminViewingUnapprovedHub || isMerchantPhase1Approved;
  // Legacy variable for backward compatibility - now used for phase 1 fields
  const shouldDisableFields = shouldDisablePhase1Fields;
  const shouldDisableDocumentUploads = user_role === "super_admin" || isMerchantPhase1Approved;
  // Bank details should be editable during phase1_approved, but read-only during phase2_submitted and published. Hub Setup & Pincode should be editable for merchants even after publishing
  const shouldDisableBankDetails = (user_role === "merchant" && currentOnboardingStatus === "published") || (user_role === "super_admin" && currentOnboardingStatus === "phase2_submitted");
  const shouldDisableHubSetup = user_role === "super_admin" && currentOnboardingStatus === "phase2_submitted";
  // App Configuration tab should be read-only for super_admin only
  const isAppConfigReadOnly = user_role === "super_admin";
  
  const getHubStatus = () => {
    const hasHubId = isEditMode || currentInventoryId; // Hub exists if we have id in URL or currentInventoryId

    if (!hasHubId) {
      return "Draft"; // No hub created yet
    } else if (isHubApproved) {
      return "Onboarded"; // Hub is approved
    } else {
      return "Submitted"; // Hub exists but not approved
    }
  };

  // Helper function to check if prerequisites (bank details + hub setup) are completed for phase1_approved
  const arePrerequisitesCompleted = () => {
    // Check if bank details are filled
    const bankDetailsFilled = bankDetails.id &&
      bankDetails.account_holder_name?.trim() &&
      bankDetails.account_number?.trim() &&
      bankDetails.confirm_account_number?.trim() &&
      bankDetails.ifsc_code?.trim();

    // Check if hub setup is selected
    const hubSetupSelected = inventoryform.hub_setup;

    // Check if pincode configuration is filled based on hub setup type
    let pincodeDetailsValid = false;
    if (hubSetupSelected && inventoryform.pincodes && inventoryform.pincodes.length > 0) {
      pincodeDetailsValid = inventoryform.pincodes.every(pincode => {
        // Common validations for all hub setups
        const hasLatLng = pincode.latitude && pincode.longitude;
        const hasAddress = pincode.hub_address?.trim();

        if (!hasLatLng || !hasAddress) {
          return false;
        }

        // Type-specific validations
        if (inventoryform.hub_setup === "pincode" || inventoryform.hub_setup === "radius_and_pincode") {
          return pincode.pincode?.trim() && /^\d{6}$/.test(pincode.pincode.trim());
        } else if (inventoryform.hub_setup === "radius" || inventoryform.hub_setup === "radius_and_pincode") {
          return pincode.inner_radius && parseFloat(pincode.inner_radius) > 0;
        } else if (inventoryform.hub_setup === "boundary") {
          return pincode.north_latitude && pincode.north_longitude &&
                 pincode.south_latitude && pincode.south_longitude &&
                 pincode.east_latitude && pincode.east_longitude &&
                 pincode.west_latitude && pincode.west_longitude;
        }
        return false;
      });
    }

    return bankDetailsFilled && hubSetupSelected && pincodeDetailsValid;
  };

  const hubStatus = getHubStatus();

  // Hold selected icon files to send along with AppConfig in one request
  const [appConfigFiles, setAppConfigFiles] = useState({
    favicon_url: null,
    brand_icon_url: null,
    app_icon_url: null,
    app_icon_192_url: null,
    app_icon_512_url: null,
    apple_touch_icon_url: null,
  });

  // Image previews for each icon type
  const [imagePreviews, setImagePreviews] = useState({
    favicon_url: null,
    brand_icon_url: null,
    app_icon_url: null,
    apple_touch_icon_url: null,
  });

  // AppConfig section (PWA + branding config)
  const [appConfig, setAppConfig] = useState({
    inventory: "",
    app_title: "",
    app_short_name: "",
    app_description: "",
    // Icon URLs will be filled after theming upload API
    favicon_url: "",
    brand_icon_url: "",
    app_icon_url: "",
    app_icon_192_url: "",
    app_icon_512_url: "",
    apple_touch_icon_url: "",
    theme_color: "",
    background_color: "",
    footer_text: "",
    // Backend-required fields with sensible defaults
    is_active: true,
    display_mode: "standalone",
    theme_colors: {},
  });

  // Bank Details section
  const [bankDetails, setBankDetails] = useState({
    id: null,
    account_holder_name: "",
    account_number: "",
    confirm_account_number: "",
    ifsc_code: "",
  });
  const [bankDetailsErrors, setBankDetailsErrors] = useState({});
  const [showThemeColorPicker, setShowThemeColorPicker] = useState(false);
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);

  // Merchant Documents section
  const [merchantDocuments, setMerchantDocuments] = useState({
    id: null,
    aadhaar_number: "",
    pan_number: "",
    gst_number: "",
    fssai_number: "",
    fssai_expiry_date: "",
  });
  const [merchantDocumentsErrors, setMerchantDocumentsErrors] = useState({});
  const [merchantDocumentFiles, setMerchantDocumentFiles] = useState({
    aadhaar_card_file: null,
    pan_card_file: null,
    fssai_document_file: null,
  });
  const [merchantDocumentPreviews, setMerchantDocumentPreviews] = useState({
    aadhaar_card_upload: null,
    pan_card_upload: null,
    fssai_document_upload: null,
  });

  const availability = [
    { name: "Active", value: true },
    { name: "Inactive", value: false },
  ];

  const inputStyles = {
    backgroundColor: "white",
    height: "32px",
    borderRadius: "2px",
    border: "1px solid",
    borderColor: "secondary.border",
    padding: "5px 12px",
    fontSize: "14px",
    color: "black",
  };

  const validationSchema = Yup.object().shape({
    // Only displayed fields are required
    place: Yup.string()
      .required("Business Name is required")
      .trim()
      .min(2, "Business Name must be at least 2 characters"),
    mobileno: Yup.string()
      .required("Mobile Number is required")
      .test("numeric", "Mobile number must contain only digits", function(value) {
        if (!value) return false;
        return /^\d+$/.test(value);
      })
      .test("length", "Mobile number must be exactly 10 digits", function(value) {
        if (!value) return false;
        return value.length === 10;
      }),
    email: Yup.string()
      .required("Email Address is required")
      .email("Please enter a valid email address")
      .trim(),
    franchise_id: Yup.number()
      .required("Business Type is required")
      .typeError("Please select a Business Type"),
    delivery_fee: Yup.string()
      .required("Delivery Charge is required")
      .test("positive", "Delivery Charge must be a positive number", function(value) {
        if (!value) return false;
        const num = parseFloat(value);
        return !isNaN(num) && num >= 0;
      }),
    delivery_status: Yup.string()
      .required("Delivery Status is required"),
    delivery_mini_spent: Yup.number()
      .required("Delivery Minimum Spent is required")
      .typeError("Delivery Minimum Spent must be a number")
      .min(0, "Delivery Minimum Spent must be 0 or greater"),
    // Fields not displayed - make optional
    supervisor_ids: Yup.array()
      .of(Yup.number().required("Each item must be a number"))
      .transform((value) => (value === "" || value === null || value === undefined ? undefined : value))
      .nullable()
      .notRequired(),
    admin_ids: Yup.array()
      .of(Yup.number().required("Each item must be a number"))
      .when("$user_role", {
        is: "super_admin",
        then: (schema) => schema.optional(),
        otherwise: (schema) => schema.optional(),
      }),
    customer_engagement_admin_ids: Yup.array()
      .of(Yup.number().required("Each item must be a number"))
      .when("$user_role", {
        is: "super_admin",
        then: (schema) => schema.optional(),
        otherwise: (schema) => schema.optional(),
      }),
    cashier_ids: Yup.array()
      .of(Yup.number().required("Each item must be a number"))
      .transform((value) => (value === "" || value === null || value === undefined ? undefined : value))
      .nullable()
      .notRequired(),
    self_pickup_waiver: Yup.boolean()
      .required("Self Pickup status is required")
      .typeError("Please select Self Pickup status"),
    self_pickup_waiver_percentage: Yup.string().when("self_pickup_waiver", {
      is: true,
      then: (schema) =>
        schema
          .required("Self Pickup Offer Percentage is required when Self Pickup is Active")
          .matches(
            /^\d+(\.\d{1,2})?$/,
            "Enter a valid number with up to 2 decimals"
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    shop_open_at: Yup.string()
      .transform((value) => (value === "" ? undefined : value))
      .nullable()
      .notRequired(),
    shop_close_at: Yup.string()
      .transform((value) => (value === "" ? undefined : value))
      .nullable()
      .notRequired(),
    pincodes: Yup.array()
      .transform((value) => (value === "" || value === null || value === undefined ? undefined : value))
      .nullable()
      .notRequired(),
  });

  const syncFormToRedux = (form) => {
    setInventoryForm(form);
    dispatch(updateInventoryform(form));
  };

  const fetchFranchises = async () => {
    try {
      const response = await CustomAxios.get("/franchise/");
      dispatch(updateFranchises(response.data));
    } catch (error) {
      console.error("Error fetching franchises:", error);
    }
  };

  // Fetch hub details and AppConfig (only in edit mode)
  const fetchHubData = async () => {
    if (!id) {
      console.log('⚠️ fetchHubData called but no id present');
      return;
    }

    try {
      console.log('🚀 fetchHubData STARTED for ID:', id, 'Role:', user_role);
      
      // For merchants: Verify the hub ID belongs to them BEFORE fetching
      if (user_role === 'merchant') {
        try {
          console.log('🏪 AddHub: Verifying hub ID belongs to merchant before fetching...');
          const merchantHubListResponse = await CustomAxios.get('inventory/view');
          const merchantHubList = merchantHubListResponse?.data || [];
          
          if (merchantHubList.length > 0) {
            const merchantHub = merchantHubList[0]; // Merchant should only have one hub
            const urlHubId = parseInt(id);
            const merchantHubId = merchantHub.id;
            
            console.log('🏪 AddHub: URL Hub ID:', urlHubId, 'Merchant Hub ID:', merchantHubId);
            
            if (urlHubId !== merchantHubId) {
              console.error('🏪 AddHub: Hub ID mismatch detected! Redirecting to merchant hub:', merchantHubId);
              
              // Redirect immediately - don't fetch wrong hub data
              navigate(`/home/add_hub/${merchantHubId}`, { replace: true });
              return; // Exit early, don't fetch wrong hub
            } else {
              console.log('🏪 AddHub: Hub ID verification passed - proceeding with fetch');
            }
          } else {
            console.error('🏪 AddHub: No hub found for merchant');
            toast.current?.show({
              severity: "info",
              summary: "Pending Approval",
              detail: "Your hub is pending approval from super admin.",
              life: 4000,
            });
            navigate('/home/add_hub', { replace: true });
            return;
          }
        } catch (verifyError) {
          console.error('🏪 AddHub: Error verifying merchant hub:', verifyError);
          // Continue anyway, but log the error
        }
      }
      
      setFetching(true);
      console.log('🏪 AddHub: Fetching hub data for ID:', id, 'Role:', user_role);

      let hubData = null;
      let hubList = []; // Initialize hubList to avoid undefined error
      
      // For merchants, ALWAYS fetch directly from API to ensure we get the correct hub
      // Don't use Redux inventory for merchants as it might have stale or incorrect data
      if (user_role === 'merchant') {
        try {
          // Get the merchant's hub ID from localStorage (set by OTP verification)
          // This ensures we use the correct hub ID instead of falling back to the first one
          const selectedHubFromStorage = localStorage.getItem('selected_hub');
          const merchantHubIdFromStorage = selectedHubFromStorage ? parseInt(selectedHubFromStorage) : null;
          const urlHubId = id ? parseInt(id) : null;
          
          // For merchants, use the inventory/view endpoint which returns only their approved hub
          const merchantHubResponse = await CustomAxios.get('inventory/view');
          const merchantHubList = merchantHubResponse?.data || [];
          
          if (merchantHubList.length > 0) {
            // Try to find hub by localStorage ID first (most reliable)
            let merchantHub = null;
            if (merchantHubIdFromStorage) {
              merchantHub = merchantHubList.find(hub => hub.id === merchantHubIdFromStorage);
            }
            
            // If not found by localStorage ID, try URL ID
            if (!merchantHub && urlHubId) {
              merchantHub = merchantHubList.find(hub => hub.id === urlHubId);
            }
            
            if (merchantHub) {
              hubData = merchantHub;
              
              // 🔍 LOG: Hub data fetched for display in form
              console.log("🔍 ADDHUB - HUB DATA FETCHED FOR FORM:", {
                hubId: hubData.id,
                hubName: hubData.place || hubData.name,
                urlId: id,
                localStorageHubId: merchantHubIdFromStorage,
                hubData: hubData,
                timestamp: new Date().toISOString()
              });
            } else {
              // If hub not found, log error but don't navigate (let useEffect handle navigation)
              console.error('🔍 ADDHUB - Merchant hub not found in API response:', {
                localStorageHubId: merchantHubIdFromStorage,
                urlHubId: urlHubId,
                availableHubIds: merchantHubList.map(h => h.id)
              });
              // Don't set hubData or navigate here - let the useEffect handle it
            }
          }
        } catch (merchantError) {
          console.error('Error fetching merchant hub:', merchantError);
        }
      } else {
        // For non-merchants (super_admin, admin, etc.), always fetch fresh data from API
        // to get the latest onboarding_status. Don't use Redux cache as it may have stale status.
        console.log('🏪 AddHub: Fetching fresh hub data from API for latest onboarding_status (non-merchant)');
        const hubListResponse = await CustomAxios.get(`inventory/view`);
        hubList = hubListResponse?.data || [];
        console.log('🏪 AddHub: Hub list received:', hubList.length, 'hubs');
        console.log('🏪 AddHub: Looking for hub with ID:', parseInt(id));
        console.log('🏪 AddHub: Available hub IDs:', hubList.map(h => h.id));
        
        hubData = hubList.find((hub) => hub.id === parseInt(id));
      }

      if (!hubData) {
        const availableIds = hubList.length > 0 ? hubList.map(h => h.id) : (inventory?.map(inv => inv.value) || []);
        console.error('🏪 AddHub: Hub not found. ID:', id, 'Available IDs:', availableIds);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Hub not found. Please ensure you have access to this hub.",
          life: 4000,
        });
        setFetching(false);
        return;
      }

      
      // For merchants: Trust the backend filter - if merchant can access the hub, it's approved
      // The backend should filter unapproved hubs for merchants, so if we get here, the hub is approved
      // Only check approval status for logging purposes
      const isApproved = hubData.is_approved === true || 
                        hubData.is_approved === "true" || 
                        hubData.is_approved === 1 || 
                        hubData.is_approved === "1" ||
                        String(hubData.is_approved).toLowerCase() === "true";
      
      console.log('🏪 AddHub: Hub approval status check:', {
        is_approved: hubData.is_approved,
        type: typeof hubData.is_approved,
        isApproved: isApproved,
        user_role: user_role,
        isEditMode: isEditMode
      });
      
      // For merchants: Don't block access - backend already filters unapproved hubs
      // If merchant can fetch the hub, they should be able to view it
      // Only log if there's a mismatch (for debugging)
      if (user_role === 'merchant' && isEditMode && !isApproved) {
        console.warn('🏪 AddHub: Warning - Merchant accessing hub with is_approved =', hubData.is_approved, 
                    'but backend allowed access. Trusting backend filter and allowing access.');
        // Don't block - trust the backend filter
      }

      if (hubData) {
        // Map supervisors, admins, etc. from the response
        const supervisorIds = hubData.supervisors 
          ? (Array.isArray(hubData.supervisors) 
              ? hubData.supervisors.map((s) => (typeof s === 'object' ? s.id : s))
              : [])
          : [];
        
        const adminIds = hubData.admins 
          ? (Array.isArray(hubData.admins) 
              ? hubData.admins.map((a) => (typeof a === 'object' ? a.id : a))
              : [])
          : [];
        
        const customerEngagementAdminIds = hubData.customer_engagement_admins 
          ? (Array.isArray(hubData.customer_engagement_admins) 
              ? hubData.customer_engagement_admins.map((c) => (typeof c === 'object' ? c.id : c))
              : [])
          : [];
        
        const cashierIds = hubData.cashiers || hubData.cashier_ids
          ? (Array.isArray(hubData.cashiers || hubData.cashier_ids) 
              ? (hubData.cashiers || hubData.cashier_ids).map((c) => (typeof c === 'object' ? c.id : c))
              : [])
          : [];

        const computedHubLink = hubData.hub_link || "";
        
        // Extract custom domain from hub_link if it exists
        let customDomain = "";
        if (computedHubLink) {
          try {
            const url = new URL(computedHubLink);
            const hostname = url.hostname;
            // Extract domain (e.g., "6tharivu.com" from "keev.6tharivu.com")
            const parts = hostname.split('.');
            if (parts.length >= 2) {
              // Get domain (last two parts: e.g., "6tharivu.com")
              const extractedDomain = parts.slice(-2).join('.');
              // Only set if it's not the default domain
              if (extractedDomain !== 'shino.in') {
                customDomain = extractedDomain;
              }
            }
          } catch (e) {
            console.error("Error parsing hub_link:", e);
          }
        }

        // console.log("🏪 Hub Data loaded:", hubData);
        // console.log("📧 Email from hubData:", hubData.email);
        // console.log("🏪 Setting inventory form with hub data...");
        console.log("🏪 Approval status from hubData:", {
          is_approved: hubData.is_approved,
          type: typeof hubData.is_approved,
          value: hubData.is_approved,
          user_role: user_role,
          isEditMode: isEditMode
        });
        console.log("🏪 Onboarding status from hubData:", {
          onboarding_status: hubData.onboarding_status,
          type: typeof hubData.onboarding_status,
          value: hubData.onboarding_status,
          fullHubData: hubData
        });
        
        // Set approval status - handle various formats
        let hubIsApproved = hubData.is_approved === true || 
                           hubData.is_approved === "true" || 
                           hubData.is_approved === 1 || 
                           hubData.is_approved === "1" ||
                           String(hubData.is_approved).toLowerCase() === "true";
        
        // console.log('🏪 AddHub: Initial hubIsApproved check:', hubIsApproved);
        
        // For merchants: If backend allows access, consider hub approved (backend filters unapproved hubs)
        // This handles cases where is_approved field might not be set correctly but merchant can access
        if (user_role === 'merchant' && isEditMode) {
          if (!hubIsApproved) {
            console.log('🏪 AddHub: Merchant can access hub, treating as approved (backend filter)');
            hubIsApproved = true; // Trust backend filter - if merchant can access, it's approved
          } else {
            console.log('🏪 AddHub: Hub is already marked as approved in data');
          }
        }
        
        setIsHubApproved(hubIsApproved);
        // console.log('🏪 AddHub: Final isHubApproved set to:', hubIsApproved, 'from value:', hubData.is_approved, 'type:', typeof hubData.is_approved);
        
        // Set onboarding status - always set it, default to 'draft' if not available
        const onboardingStatus = hubData.onboarding_status || 'draft';
        setCurrentOnboardingStatus(onboardingStatus);
        // console.log('🏪 AddHub: Onboarding status set in fetchHubData:', onboardingStatus, 'from hubData:', hubData.onboarding_status);
        
        // Set vendor status from Cashfree
        setEasysplitVendorStatus(hubData.easysplit_vendor_status || null);
        // console.log('🏪 AddHub: Vendor status set:', hubData.easysplit_vendor_status);
        
        const formData = {
          place: hubData.place || "",
          custom_domain: customDomain,
          supervisor_ids: supervisorIds,
          admin_ids: adminIds,
          customer_engagement_admin_ids: customerEngagementAdminIds,
          cashier_ids: cashierIds,
          hub_setup: hubData.hub_setup || "",
          pincodes: hubData.pincodes && hubData.pincodes.length > 0 ? hubData.pincodes : [
            {
              pincode: "",
              latitude: "",
              longitude: "",
              hub_address: "",
              inner_radius: "",
              outter_radius: "",
              radius_increase_delivery_fee: "",
              north_latitude: "",
              north_longitude: "",
              south_latitude: "",
              south_longitude: "",
              east_latitude: "",
              east_longitude: "",
              west_latitude: "",
              west_longitude: "",
            },
          ],
          delivery_fee: hubData.delivery_fee || "",
          self_pickup_waiver: hubData.self_pickup_waiver || false,
          self_pickup_waiver_percentage:
            hubData.self_pickup_waiver_percentage || "0",
          delivery_status: hubData.delivery_status === true || hubData.delivery_status === "true" ? "true" : "false",
          delivery_mini_spent: hubData.delivery_mini_spent || "",
          shop_open_at: hubData.shop_open_at || null,
          shop_close_at: hubData.shop_close_at || null,
          mobileno: hubData.mobileno || "",
          email: hubData.email || "",
          franchise_id: hubData.franchise?.id || hubData.franchise_id || "",
          hub_link: computedHubLink,
          isViewOnly: false,
        };
        
        console.log("🏪 Form data prepared:", formData);
        setInventoryForm(formData);
        console.log("📧 Email set in inventoryform:", hubData.email || "");
        console.log("🏪 Inventory form state updated successfully");

        // Ensure current inventory ID is set when editing
        if (hubData.id) {
          setCurrentInventoryId(hubData.id);
          console.log("🏪 Current inventory ID set to:", hubData.id);
          
          // Set onboarding status - always set it, default to 'draft' if not available
          const onboardingStatus = hubData.onboarding_status || 'draft';
          setCurrentOnboardingStatus(onboardingStatus);
          // console.log("🏪 AddHub: Onboarding status loaded in fetchHubData:", onboardingStatus, 'from hubData:', hubData.onboarding_status);
          
          // Set vendor status from Cashfree
          setEasysplitVendorStatus(hubData.easysplit_vendor_status || null);
          // console.log('🏪 AddHub: Vendor status loaded:', hubData.easysplit_vendor_status);
          
          // For merchants: Sync header's selected hub with the merchant's actual hub
          // Also update the 'hub' array in localStorage to ensure header shows correct hub
          if (user_role === 'merchant') {
            // console.log("🏪 AddHub: Syncing header selected hub to merchant hub:", hubData.id);
            localStorage.setItem('selected_hub', hubData.id.toString());
            localStorage.setItem('hub', JSON.stringify([hubData.id]));
            dispatch(updateSelectedHub([hubData.id]));
            // console.log("🏪 AddHub: Header and localStorage hub array updated to:", hubData.id);
          }
        }
      }

      // Fetch AppConfig for this hub
      try {
        const appConfigResponse = await CustomAxios.get(
          `app_config/?inventory=${id}&is_active=true`
        );
        const appConfigs = appConfigResponse?.data || [];
        const existingAppConfig =
          Array.isArray(appConfigs) && appConfigs.length > 0
            ? appConfigs[0]
            : null;

        if (existingAppConfig) {
          setAppConfigId(existingAppConfig.id);
          setAppConfig({
            inventory: existingAppConfig.inventory || id,
            app_title: existingAppConfig.app_title || "",
            app_short_name: existingAppConfig.app_short_name || "",
            app_description: existingAppConfig.app_description || "",
            favicon_url: existingAppConfig.favicon_url || "",
            brand_icon_url: existingAppConfig.brand_icon_url || "",
            app_icon_url: existingAppConfig.app_icon_url || "",
            app_icon_192_url: existingAppConfig.app_icon_192_url || "",
            app_icon_512_url: existingAppConfig.app_icon_512_url || "",
            apple_touch_icon_url: existingAppConfig.apple_touch_icon_url || "",
            theme_color: existingAppConfig.theme_color || "",
            background_color: existingAppConfig.background_color || "",
            footer_text: existingAppConfig.footer_text || "",
            is_active: existingAppConfig.is_active ?? true,
            display_mode: existingAppConfig.display_mode || "standalone",
            theme_colors: existingAppConfig.theme_colors || {},
          });
          // Set image previews for existing images
          setImagePreviews({
            favicon_url: existingAppConfig.favicon_url || null,
            brand_icon_url: existingAppConfig.brand_icon_url || null,
            app_icon_url: existingAppConfig.app_icon_url || null,
            apple_touch_icon_url: existingAppConfig.apple_touch_icon_url || null,
          });
        } else {
          // No AppConfig exists yet; set inventory ID so user can create one
          setAppConfig((prev) => ({ ...prev, inventory: id }));
        }
      } catch (appConfigError) {
        console.error("Error fetching AppConfig:", appConfigError);
        // Don't show error toast for AppConfig as it's optional
        // Set inventory ID so user can create one if needed
        setAppConfig((prev) => ({ ...prev, inventory: id }));
      }

      // Fetch Bank Details for this hub
      await fetchBankDetails(id);

      // Fetch Merchant Documents for this hub
      try {
        const merchantDocsResponse = await CustomAxios.get(
          `merchant-documents/?inventory_id=${id}`
        );
        const merchantDocsList = merchantDocsResponse?.data || [];
        const existingMerchantDocs =
          Array.isArray(merchantDocsList) && merchantDocsList.length > 0
            ? merchantDocsList[0]
            : null;

        if (existingMerchantDocs) {
          console.log("📄 Merchant Documents loaded:", existingMerchantDocs);
          setMerchantDocuments({
            id: existingMerchantDocs.id,
            aadhaar_number: existingMerchantDocs.aadhaar_number || "",
            pan_number: existingMerchantDocs.pan_number || "",
            gst_number: existingMerchantDocs.gst_number || "",
            fssai_number: existingMerchantDocs.fssai_number || "",
            fssai_expiry_date: existingMerchantDocs.fssai_expiry_date || "",
          });
          console.log("📄 Merchant Documents state set:", {
            aadhaar_number: existingMerchantDocs.aadhaar_number || "",
            pan_number: existingMerchantDocs.pan_number || "",
            gst_number: existingMerchantDocs.gst_number || "",
            fssai_number: existingMerchantDocs.fssai_number || "",
          });
          // Set document previews for existing documents
          setMerchantDocumentPreviews({
            aadhaar_card_upload: existingMerchantDocs.aadhaar_card_upload || null,
            pan_card_upload: existingMerchantDocs.pan_card_upload || null,
            fssai_document_upload: existingMerchantDocs.fssai_document_upload || null,
          });
        } else {
          // Reset to empty state if no merchant documents exist
          setMerchantDocuments({
            id: null,
            aadhaar_number: "",
            pan_number: "",
            gst_number: "",
            fssai_number: "",
            fssai_expiry_date: "",
          });
          setMerchantDocumentPreviews({
            aadhaar_card_upload: null,
            pan_card_upload: null,
            fssai_document_upload: null,
          });
        }
      } catch (merchantDocsError) {
        console.error("Error fetching merchant documents:", merchantDocsError);
        // Don't show error toast for merchant documents as it's optional
        setMerchantDocuments({
          id: null,
          aadhaar_number: "",
          pan_number: "",
          gst_number: "",
          fssai_number: "",
          fssai_expiry_date: "",
        });
        setMerchantDocumentPreviews({
          aadhaar_card_upload: null,
          pan_card_upload: null,
          fssai_document_upload: null,
        });
      }
    } catch (error) {
      console.error("❌ Error fetching hub data:", error);
      console.error("❌ Error details:", error.response?.data || error.message);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load hub details",
        life: 4000,
      });
    } finally {
      console.log("🏪 fetchHubData completed, setting fetching to false");
      setFetching(false);
    }
  };

  const getDetails = async () => {
    try {
      const res = await CustomAxios.get("get_users/");
      const users = res?.data?.users || [];

      if (user_role === "super_admin") {
        const adminOptions = users
          .filter((item) => item.role === "admin")
          .map((item) => ({
            name: `${item.first_name} ${item.last_name}`,
            value: item.id,
          }));

        const supervisorOptions = users
          .filter((item) => item.role === "supervisor")
          .map((item) => ({
            name: `${item.first_name} ${item.last_name}`,
            value: item.id,
          }));

        const customerEngagementAdminOptions = users
          .filter((item) => item.role === "customer_engagement_admin")
          .map((item) => ({
            name: `${item.first_name} ${item.last_name}`,
            value: item.id,
          }));

        const cashierOptions = users
          .filter((item) => item.role === "cashier")
          .map((item) => ({
            name: `${item.first_name} ${item.last_name}`,
            value: item.id,
          }));

        dispatch(updateInventryAdmins(adminOptions || []));
        dispatch(updateInventrySupervisors(supervisorOptions || []));
        dispatch(
          updateInventryCustomerEngagementAdmins(
            customerEngagementAdminOptions || []
          )
        );
        dispatch(updateInventryCashiers(cashierOptions || []));
      } else {
        const supervisorOptions = users
          .filter((item) => item.role === "supervisor")
          .map((item) => ({
            name: `${item.first_name} ${item.last_name}`,
            value: item.id,
          }));

        dispatch(updateInventryAdmins([]));
        dispatch(updateInventrySupervisors(supervisorOptions || []));
        dispatch(updateInventryCustomerEngagementAdmins([]));
        dispatch(updateInventryCashiers([]));
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  useEffect(() => {
    console.log('🔄 useEffect triggered with id:', id, 'user_role:', user_role);
    syncFormToRedux(inventoryform);
    fetchFranchises();
    getDetails();
    
    // For merchants, don't call fetchHubData here - let the merchant verification useEffect handle it
    // This prevents fetching wrong hub data before verification completes
    if (id && user_role !== 'merchant') {
      console.log('🔄 ID present (non-merchant), setting fetching to true and calling fetchHubData');
      // Set fetching to true immediately when id is present to show loading state
      setFetching(true);
      fetchHubData().catch((error) => {
        console.error('❌ Error in fetchHubData promise:', error);
        setFetching(false);
      });
    } else if (id && user_role === 'merchant') {
      // For merchants, fetchHubData will be called after verification in the merchant useEffect
      console.log('🔄 ID present (merchant), waiting for merchant verification before fetching');
    } else {
      console.log('🔄 No ID, resetting fetching to false');
      // Reset fetching when no id (create mode)
      setFetching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // For merchants: automatically fetch and show their approved hub, and verify ID matches
  useEffect(() => {
    const fetchMerchantApprovedHub = async () => {
      // Only process if we haven't processed this specific ID yet
      const currentId = id ? parseInt(id) : null;
      if (user_role === 'merchant' && merchantHubProcessedRef.current !== currentId) {
        try {
          // Check if merchant has a hub assigned from OTP verification
          // If they navigated to /home/add_hub without ID, they don't have one assigned
          const selectedHubFromStorage = localStorage.getItem('selected_hub');
          const hubFromStorage = localStorage.getItem('hub');
          
          // If both are empty/null/empty array, merchant doesn't have a hub assigned - don't redirect
          const hasNoHub = (!selectedHubFromStorage || 
                            selectedHubFromStorage === '' || 
                            selectedHubFromStorage === '[]' ||
                            selectedHubFromStorage === 'null') &&
                           (!hubFromStorage || 
                            hubFromStorage === '[]' || 
                            hubFromStorage === 'null' ||
                            hubFromStorage === '');
          
          if (hasNoHub && !id) {
            console.log('🔍 ADDHUB - Merchant has no hub assigned from OTP, staying on create page');
            merchantHubProcessedRef.current = null; // Mark as processed (null means no hub)
            // Merchant is on create page without a hub - this is correct, don't fetch or redirect
            return;
          }
          
          const response = await CustomAxios.get('inventory/view');
          const hubList = response?.data || [];
          
          if (hubList && hubList.length > 0) {
            // Get the merchant's hub ID from localStorage (set by OTP verification)
            const merchantHubIdFromStorage = selectedHubFromStorage ? parseInt(selectedHubFromStorage) : null;
            const urlHubId = id ? parseInt(id) : null;
            
            // Try to find the hub that matches the ID from localStorage or URL
            // This ensures we use the correct hub ID (35) instead of just taking the first one (1)
            let approvedHub = null;
            
            if (merchantHubIdFromStorage) {
              approvedHub = hubList.find(hub => hub.id === merchantHubIdFromStorage);
            }
            
            // If not found by localStorage ID, try URL ID
            if (!approvedHub && urlHubId) {
              approvedHub = hubList.find(hub => hub.id === urlHubId);
            }
            
            // If still not found, fall back to first hub (but log a warning)
            if (!approvedHub) {
              console.warn('🔍 ADDHUB - Merchant hub ID not found in API response, using first hub');
              approvedHub = hubList[0];
            }
            
            // Check if we're already on the correct page - if so, don't navigate again
            const currentHubId = approvedHub.id;
            const isAlreadyOnCorrectPage = id && parseInt(id) === currentHubId;
            
            // 🔍 LOG: Merchant hub fetched in AddHub component
            console.log("🔍 ADDHUB - MERCHANT HUB FETCHED:", {
              hubId: approvedHub.id,
              hubName: approvedHub.place || approvedHub.name,
              urlId: id,
              localStorageHubId: merchantHubIdFromStorage,
              matches: parseInt(id) === approvedHub.id,
              isAlreadyOnCorrectPage: isAlreadyOnCorrectPage,
              hubData: approvedHub,
              timestamp: new Date().toISOString()
            });
            
            // Sync header and localStorage IMMEDIATELY for merchant's actual hub
            localStorage.setItem('selected_hub', approvedHub.id.toString());
            localStorage.setItem('hub', JSON.stringify([approvedHub.id]));
            dispatch(updateSelectedHub([approvedHub.id]));
            
            // Mark as processed with the current hub ID to prevent re-running for this ID
            merchantHubProcessedRef.current = approvedHub.id;
            
            // If no ID in URL, navigate to merchant's hub (only once)
            if (!id) {
              navigate(`/home/add_hub/${approvedHub.id}`, { replace: true });
            } 
            // If ID in URL doesn't match merchant's hub, redirect to correct hub (only once)
            else if (!isAlreadyOnCorrectPage) {
              navigate(`/home/add_hub/${approvedHub.id}`, { replace: true });
            } else {
              // ID matches and we're already on the correct page
              // Use the hub data we already have to populate the form directly
              // This is more reliable than calling fetchHubData again
              setFetching(true);
              
              // Populate form directly with the approvedHub data we already fetched
              try {
                const hubData = approvedHub;
                
                // Map supervisors, admins, etc. from the response
                const supervisorIds = hubData.supervisors 
                  ? (Array.isArray(hubData.supervisors) 
                      ? hubData.supervisors.map((s) => (typeof s === 'object' ? s.id : s))
                      : [])
                  : [];
                
                const adminIds = hubData.admins 
                  ? (Array.isArray(hubData.admins) 
                      ? hubData.admins.map((a) => (typeof a === 'object' ? a.id : a))
                      : [])
                  : [];
                
                const customerEngagementAdminIds = hubData.customer_engagement_admins 
                  ? (Array.isArray(hubData.customer_engagement_admins) 
                      ? hubData.customer_engagement_admins.map((c) => (typeof c === 'object' ? c.id : c))
                      : [])
                  : [];
                
                const cashierIds = hubData.cashiers || hubData.cashier_ids
                  ? (Array.isArray(hubData.cashiers || hubData.cashier_ids) 
                      ? (hubData.cashiers || hubData.cashier_ids).map((c) => (typeof c === 'object' ? c.id : c))
                      : [])
                  : [];

                const computedHubLink = hubData.hub_link || "";
                
                // Extract custom domain from hub_link if it exists
                let customDomain = "";
                if (computedHubLink) {
                  try {
                    const url = new URL(computedHubLink);
                    const hostname = url.hostname;
                    const parts = hostname.split('.');
                    if (parts.length >= 2) {
                      const extractedDomain = parts.slice(-2).join('.');
                      if (extractedDomain !== 'shino.in') {
                        customDomain = extractedDomain;
                      }
                    }
                  } catch (e) {
                    console.error("Error parsing hub_link:", e);
                  }
                }

                // Set approval status
                let hubIsApproved = hubData.is_approved === true || 
                                 hubData.is_approved === "true" || 
                                 hubData.is_approved === 1 || 
                                 hubData.is_approved === "1" ||
                                 String(hubData.is_approved).toLowerCase() === "true";
                
                setIsHubApproved(hubIsApproved);
                setCurrentInventoryId(hubData.id);
                
                // Set onboarding status - always set it, default to 'draft' if not available
                const onboardingStatus = hubData.onboarding_status || 'draft';
                setCurrentOnboardingStatus(onboardingStatus);
                console.log('🏪 AddHub: Onboarding status set in populateForm (merchant direct):', {
                  onboardingStatus: onboardingStatus,
                  fromHubData: hubData.onboarding_status,
                  hubId: hubData.id,
                });
                
                // Set vendor status from Cashfree
                setEasysplitVendorStatus(hubData.easysplit_vendor_status || null);
                console.log('🏪 AddHub: Vendor status set in populateForm:', {
                  vendorStatus: hubData.easysplit_vendor_status,
                  hubId: hubData.id,
                  fullHubData: hubData
                });

                const formData = {
                  place: hubData.place || "",
                  custom_domain: customDomain,
                  supervisor_ids: supervisorIds,
                  admin_ids: adminIds,
                  customer_engagement_admin_ids: customerEngagementAdminIds,
                  cashier_ids: cashierIds,
                  hub_setup: hubData.hub_setup || "",
                  pincodes: hubData.pincodes && hubData.pincodes.length > 0 ? hubData.pincodes : [
                    {
                      pincode: "",
                      latitude: "",
                      longitude: "",
                      hub_address: "",
                      inner_radius: "",
                      outter_radius: "",
                      radius_increase_delivery_fee: "",
                      north_latitude: "",
                      north_longitude: "",
                      south_latitude: "",
                      south_longitude: "",
                      east_latitude: "",
                      east_longitude: "",
                      west_latitude: "",
                      west_longitude: "",
                    },
                  ],
                  delivery_fee: hubData.delivery_fee || "",
                  self_pickup_waiver: hubData.self_pickup_waiver || false,
                  self_pickup_waiver_percentage: hubData.self_pickup_waiver_percentage || "0",
                  delivery_status: hubData.delivery_status === true || hubData.delivery_status === "true" ? "true" : "false",
                  delivery_mini_spent: hubData.delivery_mini_spent || "",
                  shop_open_at: hubData.shop_open_at || null,
                  shop_close_at: hubData.shop_close_at || null,
                  mobileno: hubData.mobileno || "",
                  email: hubData.email || "",
                  franchise_id: hubData.franchise?.id || hubData.franchise_id || "",
                  hub_link: computedHubLink,
                  isViewOnly: false,
                };
                
                console.log("🔍 ADDHUB - Populating form with hub data:", formData);
                setInventoryForm(formData);
                
                // Also fetch AppConfig for this hub
                try {
                  const appConfigResponse = await CustomAxios.get(
                    `app_config/?inventory=${hubData.id}&is_active=true`
                  );
                  const appConfigs = appConfigResponse?.data || [];
                  const existingAppConfig =
                    Array.isArray(appConfigs) && appConfigs.length > 0
                      ? appConfigs[0]
                      : null;

                  if (existingAppConfig) {
                    setAppConfigId(existingAppConfig.id);
                    setAppConfig({
                      inventory: existingAppConfig.inventory || hubData.id,
                      app_title: existingAppConfig.app_title || "",
                      app_short_name: existingAppConfig.app_short_name || "",
                      app_description: existingAppConfig.app_description || "",
                      favicon_url: existingAppConfig.favicon_url || "",
                      brand_icon_url: existingAppConfig.brand_icon_url || "",
                      app_icon_url: existingAppConfig.app_icon_url || "",
                      app_icon_192_url: existingAppConfig.app_icon_192_url || "",
                      app_icon_512_url: existingAppConfig.app_icon_512_url || "",
                      apple_touch_icon_url: existingAppConfig.apple_touch_icon_url || "",
                      theme_color: existingAppConfig.theme_color || "",
                      background_color: existingAppConfig.background_color || "",
                      footer_text: existingAppConfig.footer_text || "",
                      is_active: existingAppConfig.is_active ?? true,
                      display_mode: existingAppConfig.display_mode || "standalone",
                      theme_colors: existingAppConfig.theme_colors || {},
                    });
                    // Set image previews for existing images
                    setImagePreviews({
                      favicon_url: existingAppConfig.favicon_url || null,
                      brand_icon_url: existingAppConfig.brand_icon_url || null,
                      app_icon_url: existingAppConfig.app_icon_url || null,
                      apple_touch_icon_url: existingAppConfig.apple_touch_icon_url || null,
                    });
                    console.log("🔍 ADDHUB - AppConfig found and populated:", existingAppConfig.id);
                  } else {
                    // No AppConfig exists yet; set inventory ID so user can create one
                    setAppConfig((prev) => ({ ...prev, inventory: hubData.id }));
                    console.log("🔍 ADDHUB - No AppConfig found, setting inventory ID:", hubData.id);
                  }
                } catch (appConfigError) {
                  console.error('Error fetching AppConfig:', appConfigError);
                  // Don't show error toast for AppConfig as it's optional
                  // Set inventory ID so user can create one if needed
                  setAppConfig((prev) => ({ ...prev, inventory: hubData.id }));
                }
                
                // Fetch Bank Details for this hub
                await fetchBankDetails(hubData.id);
                
                // Fetch Merchant Documents for this hub using inventory_id from OTP verification
                try {
                  const merchantDocsResponse = await CustomAxios.get(
                    `merchant-documents/?inventory_id=${hubData.id}`
                  );
                  const merchantDocsList = merchantDocsResponse?.data || [];
                  const existingMerchantDocs =
                    Array.isArray(merchantDocsList) && merchantDocsList.length > 0
                      ? merchantDocsList[0]
                      : null;

                  if (existingMerchantDocs) {
                    console.log("🔍 ADDHUB - Merchant Documents loaded from OTP inventory_id:", existingMerchantDocs);
                    setMerchantDocuments({
                      id: existingMerchantDocs.id,
                      aadhaar_number: existingMerchantDocs.aadhaar_number || "",
                      pan_number: existingMerchantDocs.pan_number || "",
                      gst_number: existingMerchantDocs.gst_number || "",
                      fssai_number: existingMerchantDocs.fssai_number || "",
                      fssai_expiry_date: existingMerchantDocs.fssai_expiry_date || "",
                    });
                    // Set document previews for existing documents
                    setMerchantDocumentPreviews({
                      aadhaar_card_upload: existingMerchantDocs.aadhaar_card_upload || null,
                      pan_card_upload: existingMerchantDocs.pan_card_upload || null,
                      fssai_document_upload: existingMerchantDocs.fssai_document_upload || null,
                    });
                  } else {
                    console.log("🔍 ADDHUB - No merchant documents found for inventory_id:", hubData.id);
                    // Reset to empty state if no merchant documents exist
                    setMerchantDocuments({
                      id: null,
                      aadhaar_number: "",
                      pan_number: "",
                      gst_number: "",
                      fssai_number: "",
                      fssai_expiry_date: "",
                    });
                    setMerchantDocumentPreviews({
                      aadhaar_card_upload: null,
                      pan_card_upload: null,
                      fssai_document_upload: null,
                    });
                  }
                } catch (merchantDocsError) {
                  console.error("🔍 ADDHUB - Error fetching merchant documents:", merchantDocsError);
                  // Don't show error toast for merchant documents as it's optional
                  setMerchantDocuments({
                    id: null,
                    aadhaar_number: "",
                    pan_number: "",
                    gst_number: "",
                    fssai_number: "",
                    fssai_expiry_date: "",
                  });
                  setMerchantDocumentPreviews({
                    aadhaar_card_upload: null,
                    pan_card_upload: null,
                    fssai_document_upload: null,
                  });
                }
                
                setFetching(false);
              } catch (error) {
                console.error('Error populating form with hub data:', error);
                setFetching(false);
              }
            }
          } else {
            merchantHubProcessedRef.current = null; // Mark as processed (no hub found)
            if (!id) {
              toast.current?.show({
                severity: "info",
                summary: "Pending Approval",
                detail: "Your hub is pending approval from super admin. You will be able to view it once approved.",
                life: 5000,
              });
            } else {
              navigate('/home/add_hub', { replace: true });
            }
          }
        } catch (error) {
          console.error('Error fetching merchant hub:', error);
          merchantHubProcessedRef.current = currentId; // Mark as processed even on error to prevent retry loop
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to fetch hub details. Please try again.",
            life: 4000,
          });
        }
      }
    };

    fetchMerchantApprovedHub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_role, id]);

  // Populate mobile number from localStorage after OTP verification (only for new hub creation)
  useEffect(() => {
    if (!id && !inventoryform.mobileno) {
      const storedMobileno = localStorage.getItem("mobileno");
      if (storedMobileno) {
        const updatedForm = {
          ...inventoryform,
          mobileno: storedMobileno,
        };
        setInventoryForm(updatedForm);
        syncFormToRedux(updatedForm);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch bank details whenever inventory ID is available (from URL id or currentInventoryId)
  useEffect(() => {
    const parsedId = id ? parseInt(id, 10) : null;
    const inventoryId = (parsedId && !isNaN(parsedId)) ? parsedId : currentInventoryId;
    
    // console.log('🏦 useEffect: Bank details check - id:', id, 'parsedId:', parsedId, 'currentInventoryId:', currentInventoryId, 'final inventoryId:', inventoryId);
    
    if (inventoryId && !isNaN(inventoryId)) {
      // console.log('🏦 useEffect: Fetching bank details for inventory ID:', inventoryId);
      fetchBankDetails(inventoryId);
    } else {
      // console.log('🏦 useEffect: No valid inventory ID, resetting bank details');
      // Reset bank details if no inventory ID
      setBankDetails({
        id: null,
        account_holder_name: "",
        account_number: "",
        confirm_account_number: "",
        ifsc_code: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentInventoryId]);

  const handleChangeInventory = (e) => {
    const name = e.target?.name || e.originalEvent?.target?.name;
    const value =
      e.target?.value !== undefined ? e.target.value : e.value;

    let newForm = { ...inventoryform };

    if (name === "supervisor_ids") {
      newForm = { ...newForm, supervisor_ids: value };
    } else if (name === "admin_ids") {
      newForm = { ...newForm, admin_ids: value };
    } else if (name === "custom_domain") {
      newForm = { ...newForm, custom_domain: value };
    } else if (name === "customer_engagement_admin_ids") {
      newForm = { ...newForm, customer_engagement_admin_ids: value };
    } else if (name === "cashier_ids") {
      newForm = { ...newForm, cashier_ids: value };
    } else if (name === "franchise_id") {
      const cleanedPincodes = (newForm.pincodes || []).map((p) => ({
        ...p,
        inner_radius: "",
        outter_radius: "",
        radius_increase_delivery_fee: "",
        north_latitude: "",
        north_longitude: "",
        south_latitude: "",
        south_longitude: "",
        east_latitude: "",
        east_longitude: "",
        west_latitude: "",
        west_longitude: "",
      }));
      newForm = {
        ...newForm,
        franchise_id: value,
        hub_setup: value === 2 ? "" : newForm.hub_setup,
        pincodes: value === 2 ? cleanedPincodes : newForm.pincodes,
      };
    } else {
      newForm = { ...newForm, [name]: value };
    }

    syncFormToRedux(newForm);
    setHasUnsavedChanges(true);

    // For merchants with published status, only allow changes to hub setup to enable update button
    if (user_role === 'merchant' && currentOnboardingStatus === 'published' && name === 'hub_setup') {
      setHasAllowedChanges(true);
    }
  };

  const handlePincodeChange = (e, index) => {
    const { name, value } = e.target;
    const updated = [...inventoryform.pincodes];
    updated[index] = {
      ...updated[index],
      [name]: value,
    };
    const newForm = { ...inventoryform, pincodes: updated };
    syncFormToRedux(newForm);
    setHasUnsavedChanges(true);

    // For merchants with published status, pincode changes enable the update button
    if (user_role === 'merchant' && currentOnboardingStatus === 'published') {
      setHasAllowedChanges(true);
    }
  };

  const handleAppConfigChange = (e) => {
    const { name, value } = e.target;
    setAppConfig((prev) => ({ ...prev, [name]: value }));
    setHasUnsavedChanges(true);

    // For merchants with published status, app config changes enable the update button
    if (user_role === 'merchant' && currentOnboardingStatus === 'published') {
      setHasAllowedChanges(true);
    }
  };

  const handleBankDetailsChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Auto-uppercase IFSC code and remove spaces/special characters
    if (name === "ifsc_code") {
      processedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    }
    // Only allow digits for account number
    else if (name === "account_number" || name === "confirm_account_number") {
      processedValue = value.replace(/\D/g, '');
    }
    // Account holder name: allow only letters, spaces, and common name characters (., -)
    else if (name === "account_holder_name") {
      processedValue = value.replace(/[^a-zA-Z\s.\-]/g, '');
    }
    
    setBankDetails((prev) => ({ ...prev, [name]: processedValue }));
    setHasUnsavedChanges(true);
    // Clear error for this field when user starts typing
    if (bankDetailsErrors[name]) {
      setBankDetailsErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleMerchantDocumentsChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    if (name === "pan_number") {
      processedValue = value.toUpperCase().slice(0, 10);
    }
    else if (name === "aadhaar_number") {
      processedValue = value.replace(/\D/g, '').slice(0, 12); // Remove all non-digit characters and limit to 12
    }
    else if (name === "gst_number") {
      processedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15); // Only alphanumeric, uppercase, limit to 15
    }
    else if (name === "fssai_number") {
      processedValue = value.replace(/\D/g, '').slice(0, 14); // Remove all non-digit characters and limit to 14
    }
    
    setMerchantDocuments((prev) => ({ ...prev, [name]: processedValue }));
    setHasUnsavedChanges(true);
    // Clear error for this field when user starts typing
    if (merchantDocumentsErrors[name]) {
      setMerchantDocumentsErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle document file upload for merchant documents
  const handleMerchantDocumentFileChange = (e, documentType) => {
    const file = e.target.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setMerchantDocumentFiles((prev) => ({
        ...prev,
        [documentType]: file,
      }));
      setMerchantDocumentPreviews((prev) => ({
        ...prev,
        [documentType === "aadhaar_card_file" ? "aadhaar_card_upload" :
          documentType === "pan_card_file" ? "pan_card_upload" :
          "fssai_document_upload"]: previewUrl,
      }));
      // Clear error for this upload field
      if (merchantDocumentsErrors[documentType]) {
        setMerchantDocumentsErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[documentType];
          return newErrors;
        });
      }
      setHasUnsavedChanges(true);
    }
  };

  // Handle document removal for merchant documents
  const handleRemoveMerchantDocument = (documentType) => {
    // Revoke object URL to prevent memory leak
    const previewKey = documentType === "aadhaar_card_file" ? "aadhaar_card_upload" :
      documentType === "pan_card_file" ? "pan_card_upload" :
      "fssai_document_upload";
    
    if (merchantDocumentPreviews[previewKey] && merchantDocumentPreviews[previewKey].startsWith('blob:')) {
      URL.revokeObjectURL(merchantDocumentPreviews[previewKey]);
    }
    setMerchantDocumentFiles((prev) => ({
      ...prev,
      [documentType]: null,
    }));
    setMerchantDocumentPreviews((prev) => ({
      ...prev,
      [previewKey]: null,
    }));
  };

  // Handle image upload for AppConfig icons
  const handleIconImageChange = (e, iconType) => {
    const file = e.target.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setAppConfigFiles((prev) => ({
        ...prev,
        [iconType]: file,
      }));
      setImagePreviews((prev) => ({
        ...prev,
        [iconType]: previewUrl,
      }));
      setHasUnsavedChanges(true);

      // For merchants with published status, icon changes enable the update button
      if (user_role === 'merchant' && currentOnboardingStatus === 'published') {
        setHasAllowedChanges(true);
      }
    }
  };

  // Handle image removal for AppConfig icons
  const handleRemoveIconImage = (iconType) => {
    // Revoke object URL to prevent memory leak
    if (imagePreviews[iconType] && imagePreviews[iconType].startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviews[iconType]);
    }
    setAppConfigFiles((prev) => ({
      ...prev,
      [iconType]: null,
    }));
    setImagePreviews((prev) => ({
      ...prev,
      [iconType]: null,
    }));
    // Also clear from appConfig if it's an existing image (not a new upload)
    if (appConfig[iconType]) {
      setAppConfig((prev) => ({
        ...prev,
        [iconType]: "",
      }));
    }
    setHasUnsavedChanges(true);

    // For merchants with published status, icon removal enables the update button
    if (user_role === 'merchant' && currentOnboardingStatus === 'published') {
      setHasAllowedChanges(true);
    }
  };

  const handleTabChange = (e, newValue) => {
  
    // Prevent switching to App Configuration tab until a hub has been created or in phase 1
    if (newValue === "appConfig") {
      if (!currentInventoryId) {
        return;
      }
      // Block switching to App Configuration tab in phase 1 (draft or phase1_submitted)
      if (currentOnboardingStatus === 'draft' || currentOnboardingStatus === 'phase1_submitted') {
        return;
      }
    }
    console.log("Setting tab to:", newValue);
    setCurrentTab(newValue);
  };

  const handleSaveAppConfig = async (suppressToasts = false) => {
    try {
      setAppConfigLoading(true);
      console.log("🔵 handleSaveAppConfig called - suppressToasts:", suppressToasts);
      console.log("📋 Current appConfig state:", appConfig);
      console.log("📁 Current appConfigFiles:", appConfigFiles);

      // Derive required backend-only fields
      const themeColors =
        appConfig.theme_colors &&
        typeof appConfig.theme_colors === "object" &&
        Object.keys(appConfig.theme_colors).length > 0
          ? appConfig.theme_colors
          : {
              background: appConfig.background_color || "#ffffff",
              text: "#000000",
              primary: appConfig.theme_color || "#1976d2",
              primaryHover: "#115293",
              textPrimary: "#000000",
              textSecondary: "#555555",
              selected: "#e0f2ff",
              hoverBackground: "#f5f5f5",
              border: "#e0e0e0",
              chipSelected: appConfig.theme_color || "#1976d2",
            };

      // Ensure inventory is set correctly - use id, currentInventoryId, or appConfig.inventory
      // Convert to number if it's a string representation of a number
      const getInventoryId = () => {
        const idValue = id || currentInventoryId;
        if (idValue) return typeof idValue === 'string' ? parseInt(idValue, 10) : idValue;
        const appConfigInventory = appConfig.inventory;
        if (appConfigInventory) {
          if (typeof appConfigInventory === 'string' && appConfigInventory.trim()) {
            const parsed = parseInt(appConfigInventory, 10);
            return isNaN(parsed) ? appConfigInventory : parsed;
          }
          return typeof appConfigInventory === 'number' ? appConfigInventory : appConfigInventory;
        }
        return null;
      };
      
      const inventoryIdForPayload = getInventoryId();
      
      const payload = {
        ...appConfig,
        // Ensure inventory is set from available sources
        inventory: inventoryIdForPayload,
        // Fallbacks for required fields that don't have explicit inputs
        is_active: appConfig.is_active ?? true,
        display_mode: appConfig.display_mode || "standalone",
        theme_colors: themeColors,
      };

      // Basic frontend validation so all required fields are present
      const requiredTextFields = [
        "app_title",
        "app_short_name",
        "app_description",
        "theme_color",
        "background_color",
      ];

      const fieldErrors = {};
      let hasValidationErrors = false;


      // Validate inventory separately since it can be a number or string
      // A valid inventory is: a non-zero number, or a non-empty string
      const isValidInventory = payload.inventory !== null &&
                               payload.inventory !== undefined &&
                               payload.inventory !== "" &&
                               (typeof payload.inventory === "number" ? payload.inventory > 0 : payload.inventory.trim() !== "");
      if (!isValidInventory) {
        fieldErrors.inventory = "Inventory is required";
        hasValidationErrors = true;
      }

      requiredTextFields.forEach((field) => {
        const value = payload[field];
        const isEmpty = !value || (typeof value === "string" && !value.trim());
        console.log(`🔍 Field "${field}": value="${value}", isEmpty=${isEmpty}`);
        if (isEmpty) {
          // Create user-friendly field names
          const fieldNames = {
            app_title: "App Title",
            app_short_name: "App Short Name",
            app_description: "App Description",
            theme_color: "Theme Color",
            background_color: "Background Color"
          };
          fieldErrors[field] = `${fieldNames[field] || field} is required`;
          hasValidationErrors = true;
        }
      });

      if (!appConfigId) {
        // console.log("🆕 Creating new AppConfig - checking icon files...");
        if (!appConfigFiles.favicon_url) {
          fieldErrors.favicon_url = "Favicon is required";
          hasValidationErrors = true;
        }
        if (!appConfigFiles.brand_icon_url) {
          fieldErrors.brand_icon_url = "Brand Icon is required";
          hasValidationErrors = true;
        }
        if (!appConfigFiles.app_icon_url) {
          fieldErrors.app_icon_url = "App Icon is required";
          hasValidationErrors = true;
        }
        if (!appConfigFiles.apple_touch_icon_url) {
          fieldErrors.apple_touch_icon_url = "Apple Touch Icon is required";
          hasValidationErrors = true;
        }
      } 

      if (hasValidationErrors) {
        // Set field errors for display under each field
        setErrors(fieldErrors);

        if (!suppressToasts) {
          toast.current.show({
            severity: "error",
            summary: "Validation Error",
            detail: "Please fill all the required fields",
            life: 4000,
          });
        }
        setAppConfigLoading(false);
        return false;
      }

      setErrors({});
      // Build multipart payload so we can send both files and JSON fields
      const formData = new FormData();

      Object.entries(payload).forEach(([key, value]) => {
        if (value === undefined || value === null) return;

        // Serialize objects (like theme_colors) as JSON
        if (typeof value === "object" && !Array.isArray(value)) {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }
      });

      // Attach icon files (if chosen)
      Object.entries(appConfigFiles).forEach(([key, file]) => {
        if (file) {
          formData.append(key, file);
        }
      });

      let response;
      if (appConfigId) {
        // Update existing AppConfig
        response = await CustomAxios.patch(`app_config/${appConfigId}/`, formData);
      } else {
        // Create new AppConfig
        response = await CustomAxios.post("app_config/", formData);
        if (response?.data?.id) {
          setAppConfigId(response.data.id);
        }
      }

      // Clear cache and refresh for service worker
      const inventoryId = response?.data?.inventory || id || appConfig.inventory || currentInventoryId;
      
      // If phase1 is approved, update onboarding_status to phase2_submitted with all inventory fields
      if (inventoryId && currentOnboardingStatus === 'phase1_approved') {
        try {
          console.log('📝 Updating onboarding_status to phase2_submitted for inventory:', inventoryId);
          
          const mapHubSetupForBackend = (value) => {
            if (!value) return "";
            return value;
          };

          const payload = {
            ...inventoryform,
            hub_setup:
              inventoryform?.franchise_id === 2
                ? "pincode"
                : mapHubSetupForBackend(inventoryform?.hub_setup),
            onboarding_status: 'phase2_submitted'
          };
          
          await CustomAxios.put(`inventory/update/${inventoryId}/`, payload);
          setCurrentOnboardingStatus('phase2_submitted');
          console.log('✅ Onboarding status updated to phase2_submitted');
          
          // Refresh hub data to reflect the new status
          try {
            await fetchHubData();
          } catch (error) {
            console.error('Error refreshing hub data after phase2 submission:', error);
          }
        } catch (statusError) {
          console.error('Error updating onboarding status:', statusError);
          // Show error toast if status update fails
          toast.current?.show({
            severity: "warning",
            summary: "Warning",
            detail: "App config saved, but failed to update onboarding status. Please try again.",
            life: 4000,
          });
        }
      }
      
      if (inventoryId) {
        clearAppConfigCache(inventoryId);
        cacheAppConfigForSW();
      }

      const detail = currentOnboardingStatus === 'phase1_approved'
        ? "App configuration submitted for Phase 2 approval"
        : isEditMode
        ? "App configuration updated successfully"
        : "App configuration saved successfully";

      if (!suppressToasts) {
        console.log("📢 Attempting to show success toast...");
        try {
          if (toast.current && typeof toast.current.show === 'function') {
            toast.current.show({
              severity: "success",
              summary: "Success",
              detail,
              life: 3000,
            });
            console.log("✅ Success toast shown successfully");
          } else {
            console.error("❌ Success toast component not available");
          }
        } catch (toastError) {
          console.error("❌ Error showing success toast:", toastError);
        }
      }

      // Don't reset form for updates - only reset for new creations
      // Keep the current values in the form after update
      // The reset was causing filled fields to become empty after updates

      // Revoke object URLs and clear previews
      Object.values(imagePreviews).forEach((preview) => {
        if (preview && preview.startsWith('blob:')) {
          URL.revokeObjectURL(preview);
        }
      });
      setImagePreviews({
        favicon_url: null,
        brand_icon_url: null,
        app_icon_url: null,
        apple_touch_icon_url: null,
      });
    } catch (error) {
      console.error("❌ Error in handleSaveAppConfig:", error);
      let detail = "Failed to save app configuration";
      if (error?.response?.data) {
        // Try to surface useful backend validation errors
        if (typeof error.response.data === "string") {
          detail = error.response.data;
        } else if (error.response.data.detail) {
          detail = error.response.data.detail;
        } else if (error.response.data.non_field_errors) {
          detail = error.response.data.non_field_errors.join(", ");
        }
      }

      console.log("📢 Attempting to show error toast for catch block...");
      try {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail,
          life: 4000,
        });
        console.log("✅ Error toast shown successfully");
      } catch (toastError) {
        console.error("❌ Failed to show error toast:", toastError);
      }
    } finally {
      setAppConfigLoading(false);
    }
  };

  const handleAddPincode = () => {
    const newForm = {
      ...inventoryform,
      pincodes: [
        ...inventoryform.pincodes,
        {
          pincode: "",
          latitude: "",
          longitude: "",
          hub_address: "",
          inner_radius: "",
          outter_radius: "",
          radius_increase_delivery_fee: "",
          north_latitude: "",
          north_longitude: "",
          south_latitude: "",
          south_longitude: "",
          east_latitude: "",
          east_longitude: "",
          west_latitude: "",
          west_longitude: "",
        },
      ],
    };
    syncFormToRedux(newForm);
  };

  const handleRemoveItem = (index) => {
    const updatedPin = inventoryform.pincodes.filter((_, i) => i !== index);
    const newForm = { ...inventoryform, pincodes: updatedPin };
    syncFormToRedux(newForm);
  };

  const handleSaveMerchantDocuments = async (inventoryId, suppressToasts = false) => {
    try {
      // Validate all merchant document fields (all required)
      const errors = {};
      if (merchantDocuments.aadhaar_number?.trim()) {
        if (merchantDocuments.aadhaar_number.trim().length !== 12 || !/^\d+$/.test(merchantDocuments.aadhaar_number.trim())) {
          errors.aadhaar_number = "Aadhaar number must be exactly 12 digits";
        }
      }

      if (merchantDocuments.pan_number?.trim()) {
        const pan = merchantDocuments.pan_number.trim().toUpperCase();
        if (pan.length !== 10) {
          errors.pan_number = "PAN number must be exactly 10 characters";
        } else if (!/^[A-Z]{5}\d{4}[A-Z]$/.test(pan)) {
          errors.pan_number = "Invalid PAN format. Expected: ABCDE1234F";
        }
      }

      // Validate document uploads (all required)
      if (!merchantDocumentFiles.aadhaar_card_file && !merchantDocumentPreviews.aadhaar_card_upload) {
        errors.aadhaar_card_file = "Aadhaar Card Upload is required";
      }
      if (!merchantDocumentFiles.pan_card_file && !merchantDocumentPreviews.pan_card_upload) {
        errors.pan_card_file = "PAN Card Upload is required";
      }
      if (!merchantDocumentFiles.fssai_document_file && !merchantDocumentPreviews.fssai_document_upload) {
        errors.fssai_document_file = "FSSAI Document Upload is required";
      }

      if (Object.keys(errors).length > 0) {
        setMerchantDocumentsErrors(errors);
        return false;
      }

      setMerchantDocumentsErrors({});

      // Build FormData for file uploads
      const formData = new FormData();
      formData.append('inventory', inventoryId);
      if (merchantDocuments.aadhaar_number?.trim()) {
        formData.append('aadhaar_number', merchantDocuments.aadhaar_number.trim());
      }
      if (merchantDocuments.pan_number?.trim()) {
        formData.append('pan_number', merchantDocuments.pan_number.trim().toUpperCase());
      }
      if (merchantDocuments.gst_number?.trim()) {
        formData.append('gst_number', merchantDocuments.gst_number.trim());
      }
      if (merchantDocuments.fssai_number?.trim()) {
        formData.append('fssai_number', merchantDocuments.fssai_number.trim());
      }
      if (merchantDocuments.fssai_expiry_date) {
        formData.append('fssai_expiry_date', merchantDocuments.fssai_expiry_date);
      }

      // Add file uploads if new files are selected
      if (merchantDocumentFiles.aadhaar_card_file) {
        formData.append('aadhaar_card_file', merchantDocumentFiles.aadhaar_card_file);
      }
      if (merchantDocumentFiles.pan_card_file) {
        formData.append('pan_card_file', merchantDocumentFiles.pan_card_file);
      }
      if (merchantDocumentFiles.fssai_document_file) {
        formData.append('fssai_document_file', merchantDocumentFiles.fssai_document_file);
      }

      // If updating, include existing URLs if no new files are uploaded
      if (merchantDocuments.id) {
        if (!merchantDocumentFiles.aadhaar_card_file && merchantDocumentPreviews.aadhaar_card_upload && !merchantDocumentPreviews.aadhaar_card_upload.startsWith('blob:')) {
          formData.append('aadhaar_card_upload', merchantDocumentPreviews.aadhaar_card_upload);
        }
        if (!merchantDocumentFiles.pan_card_file && merchantDocumentPreviews.pan_card_upload && !merchantDocumentPreviews.pan_card_upload.startsWith('blob:')) {
          formData.append('pan_card_upload', merchantDocumentPreviews.pan_card_upload);
        }
        if (!merchantDocumentFiles.fssai_document_file && merchantDocumentPreviews.fssai_document_upload && !merchantDocumentPreviews.fssai_document_upload.startsWith('blob:')) {
          formData.append('fssai_document_upload', merchantDocumentPreviews.fssai_document_upload);
        }
      }

      let savedMerchantDocuments;
      if (merchantDocuments.id) {
        // Update existing merchant documents
        const response = await CustomAxios.put(`merchant-documents/${merchantDocuments.id}/`, formData);
        savedMerchantDocuments = response.data;
      } else {
        // Create new merchant documents
        const response = await CustomAxios.post(`merchant-documents/`, formData);
        savedMerchantDocuments = response.data;
        // Update state with the new ID so subsequent saves will update instead of create
        if (savedMerchantDocuments?.id) {
          setMerchantDocuments((prev) => ({ ...prev, id: savedMerchantDocuments.id }));
        }
      }

      // Update previews with the URLs from the response
      if (savedMerchantDocuments) {
        setMerchantDocumentPreviews({
          aadhaar_card_upload: savedMerchantDocuments.aadhaar_card_upload || null,
          pan_card_upload: savedMerchantDocuments.pan_card_upload || null,
          fssai_document_upload: savedMerchantDocuments.fssai_document_upload || null,
        });
        // Clear file selections after successful save
        setMerchantDocumentFiles({
          aadhaar_card_file: null,
          pan_card_file: null,
          fssai_document_file: null,
        });
      }

      return true;
    } catch (error) {
      console.error("Error saving merchant documents:", error);
      const serverErrors = {};
      if (error?.response?.data) {
        Object.keys(error.response.data).forEach((key) => {
          if (Array.isArray(error.response.data[key]) && error.response.data[key].length > 0) {
            serverErrors[key] = error.response.data[key][0];
          } else if (typeof error.response.data[key] === "string") {
            serverErrors[key] = error.response.data[key];
          }
        });
      }
      setMerchantDocumentsErrors(serverErrors);
      return false;
    }
  };

  const handleCombinedUpdate = async () => {
    if (!hasUnsavedChanges) return;

    try {
      setLoading(true);
      let successCount = 0;
      let totalAttempts = 0;
      const failedSections = [];

      // Save inventory/hub details
      if (currentInventoryId) {
        totalAttempts++;
        try {
          const inventorySaved = await handleSaveInventory(null, true); 
          if (inventorySaved) {
            successCount++;
          } else {
            failedSections.push("Hub Details");
          }
        } catch (error) {
          console.error("Error saving inventory:", error);
          failedSections.push("Hub Details");
        }
      }

      // Save app configuration
      if (currentInventoryId && currentTab === "appConfig") {
        totalAttempts++;
        try {
          const appConfigSaved = await handleSaveAppConfig(true); // suppress toasts
          if (appConfigSaved) {
            successCount++;
          } else {
            failedSections.push("App Configuration");
          }
        } catch (error) {
          console.error("Error saving app config:", error);
          failedSections.push("App Configuration");
        }
      }

      // Save bank details
      if (currentInventoryId) {
        totalAttempts++;
        try {
          const bankDetailsSaved = await handleSaveBankDetails(currentInventoryId, true); // suppress toasts
          if (bankDetailsSaved) {
            successCount++;
          } else {
            failedSections.push("Bank Details");
          }
        } catch (error) {
          console.error("Error saving bank details:", error);
          failedSections.push("Bank Details");
        }
      }

      // Save merchant documents
      if (currentInventoryId) {
        totalAttempts++;
        try {
          const merchantDocsSaved = await handleSaveMerchantDocuments(currentInventoryId, true); // suppress toasts
          if (merchantDocsSaved) {
            successCount++;
          } else {
            failedSections.push("Merchant Documents");
          }
        } catch (error) {
          console.error("Error saving merchant documents:", error);
          failedSections.push("Merchant Documents");
        }
      }

      // Always reset unsaved changes state since we attempted to save
      setHasUnsavedChanges(false);
      setHasAllowedChanges(false);

      if (successCount === totalAttempts) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "All changes saved successfully",
          life: 3000,
        });
      } else if (successCount > 0) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Updated successfully",
          life: 3000,
        });
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Save Failed",
          detail: "No changes could be saved. Please check validation errors and try again.",
          life: 5000,
        });
      }
    } catch (error) {
      console.error("Error in combined update:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save changes. Please try again.",
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch bank details for a given inventory ID
  const fetchBankDetails = async (inventoryId) => {
    // Convert to number if it's a string
    const numericId = typeof inventoryId === 'string' ? parseInt(inventoryId, 10) : inventoryId;
    
    if (!numericId || isNaN(numericId)) {
      console.log('⚠️ fetchBankDetails called but no valid inventoryId present:', inventoryId);
      return;
    }

    try {
      console.log('🏦 Fetching bank details for inventory ID:', numericId, '(original:', inventoryId, 'type:', typeof inventoryId, ')');
      const apiUrl = `bank-details/?inventory_id=${numericId}`;
      console.log('🏦 Bank details API URL:', apiUrl);
      const bankDetailsResponse = await CustomAxios.get(apiUrl);
      console.log('🏦 Bank details API response status:', bankDetailsResponse?.status);
      console.log('🏦 Bank details API response data:', bankDetailsResponse?.data);
      const bankDetailsList = bankDetailsResponse?.data || [];
      const existingBankDetails =
        Array.isArray(bankDetailsList) && bankDetailsList.length > 0
          ? bankDetailsList[0]
          : null;

      if (existingBankDetails) {
        console.log('🏦 Bank details found:', existingBankDetails);
        setBankDetails({
          id: existingBankDetails.id,
          account_holder_name: existingBankDetails.account_holder_name || "",
          account_number: existingBankDetails.account_number || "",
          confirm_account_number: existingBankDetails.confirm_account_number || "",
          ifsc_code: existingBankDetails.ifsc_code || "",
        });
      } else {
        console.log('🏦 No bank details found for inventory ID:', inventoryId);
        // Reset to empty state if no bank details exist
        setBankDetails({
          id: null,
          account_holder_name: "",
          account_number: "",
          confirm_account_number: "",
          ifsc_code: "",
        });
      }
    } catch (bankError) {
      console.error("❌ Error fetching bank details:", bankError);
      console.error("❌ Error details:", {
        message: bankError?.message,
        response: bankError?.response?.data,
        status: bankError?.response?.status,
        url: bankError?.config?.url,
      });
      // Don't show error toast for bank details as it's optional
      setBankDetails({
        id: null,
        account_holder_name: "",
        account_number: "",
        confirm_account_number: "",
        ifsc_code: "",
      });
    }
  };

  const handleSaveBankDetails = async (inventoryId, suppressToasts = false) => {
    try {
      // Check if any bank details are filled in
      const hasBankDetails = 
        bankDetails.account_holder_name?.trim() ||
        bankDetails.account_number?.trim() ||
        bankDetails.confirm_account_number?.trim() ||
        bankDetails.ifsc_code?.trim();

      // If no bank details are provided, skip saving (bank details are optional)
      if (!hasBankDetails) {
        return true;
      }

      // Validate format only when fields are provided (all fields are optional)
      const errors = {};

      // Validate Account Holder Name format if provided
      if (bankDetails.account_holder_name?.trim()) {
        const accountHolderName = bankDetails.account_holder_name.trim();
        if (!/^[a-zA-Z\s.\-]+$/.test(accountHolderName)) {
          errors.account_holder_name = "Account Holder Name must contain only letters, spaces, dots, and hyphens";
        } else if (accountHolderName.length < 2) {
          errors.account_holder_name = "Account Holder Name must be at least 2 characters";
        } else if (accountHolderName.length > 100) {
          errors.account_holder_name = "Account Holder Name must not exceed 100 characters";
        }
      }

      // Validate Account Number format if provided
      if (bankDetails.account_number?.trim()) {
        const accountNumber = bankDetails.account_number.trim();
        if (!/^\d+$/.test(accountNumber)) {
          errors.account_number = "Account Number must contain only digits";
        } else if (accountNumber.length < 9) {
          errors.account_number = "Account Number must be at least 9 digits";
        } else if (accountNumber.length > 18) {
          errors.account_number = "Account Number must not exceed 18 digits";
        }
      }

      // Validate Confirm Account Number format if provided
      if (bankDetails.confirm_account_number?.trim()) {
        const confirmAccountNumber = bankDetails.confirm_account_number.trim();
        if (!/^\d+$/.test(confirmAccountNumber)) {
          errors.confirm_account_number = "Confirm Account Number must contain only digits";
        } else if (confirmAccountNumber.length < 9) {
          errors.confirm_account_number = "Confirm Account Number must be at least 9 digits";
        } else if (confirmAccountNumber.length > 18) {
          errors.confirm_account_number = "Confirm Account Number must not exceed 18 digits";
        }
      }

      // Validate account numbers match (only if both are provided)
      if (
        bankDetails.account_number?.trim() &&
        bankDetails.confirm_account_number?.trim() &&
        bankDetails.account_number.trim() !== bankDetails.confirm_account_number.trim()
      ) {
        errors.confirm_account_number = "Account numbers do not match";
      }

      // Validate IFSC code format (11 characters: 4 letters + 0 + 6 alphanumeric)
      if (bankDetails.ifsc_code?.trim()) {
        const ifsc = bankDetails.ifsc_code.trim().toUpperCase();
        if (ifsc.length !== 11 || !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
          errors.ifsc_code = "Invalid IFSC code format. Expected format: ABCD0123456";
        }
      }

      if (Object.keys(errors).length > 0) {
        setBankDetailsErrors(errors);
        return false;
      }

      setBankDetailsErrors({});

      const payload = {
        inventory: inventoryId,
      };
      
      // Only include fields that are filled
      if (bankDetails.account_holder_name?.trim()) {
        payload.account_holder_name = bankDetails.account_holder_name.trim();
      }
      if (bankDetails.account_number?.trim()) {
        payload.account_number = bankDetails.account_number.trim();
      }
      if (bankDetails.confirm_account_number?.trim()) {
        payload.confirm_account_number = bankDetails.confirm_account_number.trim();
      }
      if (bankDetails.ifsc_code?.trim()) {
        payload.ifsc_code = bankDetails.ifsc_code.trim().toUpperCase();
      }
      // created_by and updated_by will be set automatically by the backend from the authenticated user

      let savedBankDetails;
      if (bankDetails.id) {
        // Update existing bank details
        const response = await CustomAxios.put(`bank-details/${bankDetails.id}/`, payload);
        savedBankDetails = response.data;
      } else {
        // Create new bank details
        const response = await CustomAxios.post(`bank-details/`, payload);
        savedBankDetails = response.data;
        // Update state with the new ID so subsequent saves will update instead of create
        if (savedBankDetails?.id) {
          setBankDetails((prev) => ({ ...prev, id: savedBankDetails.id }));
        }
      }

      return true;
    } catch (error) {
      console.error("Error saving bank details:", error);
      const serverErrors = {};
      if (error?.response?.data) {
        Object.keys(error.response.data).forEach((key) => {
          if (Array.isArray(error.response.data[key]) && error.response.data[key].length > 0) {
            serverErrors[key] = error.response.data[key][0];
          } else if (typeof error.response.data[key] === "string") {
            serverErrors[key] = error.response.data[key];
          }
        });
      }
      setBankDetailsErrors(serverErrors);
      return false;
    }
  };

  const handleCreateVendor = async () => {
    if (!id) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please save the hub first before creating vendor",
        life: 4000,
      });
      return;
    }

    setVendorLoading(true);
    try {
      const response = await CustomAxios.post('easy_split/create_vendor/', {
        inventory_id: id
      });
      
      if (response.status === 200 || response.status === 201) {
        const status = response.data?.status || response.data?.data?.status;
        setEasysplitVendorStatus(status);
        
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: `Vendor onboarding initiated. Status: ${status || 'PENDING'}`,
          life: 4000,
        });
        
        // Optionally fetch updated status after a delay
        setTimeout(() => {
          handleGetVendorStatus();
        }, 2000);
      }
    } catch (error) {
      console.error("Error creating vendor:", error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || "Failed to create vendor";
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: errorMsg,
        life: 4000,
      });
    } finally {
      setVendorLoading(false);
    }
  };

  // Handle Get Vendor Status
  const handleGetVendorStatus = async () => {
    if (!id) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Hub ID is required",
        life: 4000,
      });
      return;
    }

    setVendorLoading(true);
    try {
      const response = await CustomAxios.get(`easy_split/get_vendor/${id}/`);
      
      if (response.status === 200) {
        const status = response.data?.status || response.data?.data?.status;
        setEasysplitVendorStatus(status);
        
        toast.current?.show({
          severity: "info",
          summary: "Status Updated",
          detail: `Vendor Status: ${status || 'Unknown'}`,
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error fetching vendor status:", error);
      const errorMsg = error.response?.data?.error || "Failed to fetch vendor status";
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: errorMsg,
        life: 4000,
      });
    } finally {
      setVendorLoading(false);
    }
  };

  const handleSaveInventory = async (e, suppressToasts = false) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      setLoading(true);
      console.log("🔵 Starting validation and save process...");
      console.log("📋 Form data:", inventoryform);
      
      // Validate required fields
      const formErrors = {};
      
      if (!inventoryform.delivery_fee || inventoryform.delivery_fee === "") {
        formErrors.delivery_fee = "Delivery Charge is required";
      }
      
      if (!inventoryform.delivery_mini_spent || inventoryform.delivery_mini_spent === "") {
        formErrors.delivery_mini_spent = "Delivery Mini Spent is required";
      }
      
      if (inventoryform.delivery_status === null || inventoryform.delivery_status === undefined || inventoryform.delivery_status === "") {
        formErrors.delivery_status = "Delivery Status is required";
      }
      
      if (!inventoryform.place || inventoryform.place.trim() === "") {
        formErrors.place = "Business Name is required";
      }
      
      if (!inventoryform.franchise_id || inventoryform.franchise_id === "") {
        formErrors.franchise_id = "Business Type is required";
      }
      
      if (inventoryform.self_pickup_waiver === null || inventoryform.self_pickup_waiver === undefined) {
        formErrors.self_pickup_waiver = "Self Pickup is required";
      }
      
      // Validate Self Pickup Offer Percentage when Self Pickup is active
      if (inventoryform.self_pickup_waiver === true || inventoryform.self_pickup_waiver === "true") {
        if (!inventoryform.self_pickup_waiver_percentage || inventoryform.self_pickup_waiver_percentage === "") {
          formErrors.self_pickup_waiver_percentage = "Self Pickup Offer Percentage is required";
        }
      }
      
      if (!inventoryform.email || inventoryform.email.trim() === "") {
        formErrors.email = "Email Address is required";
      } else {
        // Email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(inventoryform.email.trim())) {
          formErrors.email = "Please enter a valid email address";
        }
      }
      
      // Validate merchant documents (all required)
      const merchantDocsErrors = {};
      if (!merchantDocuments.aadhaar_number?.trim()) {
        merchantDocsErrors.aadhaar_number = "Aadhaar Number is required";
      } else if (!/^\d+$/.test(merchantDocuments.aadhaar_number.trim())) {
        merchantDocsErrors.aadhaar_number = "Aadhaar number must contain only digits";
      } else if (merchantDocuments.aadhaar_number.trim().length !== 12) {
        merchantDocsErrors.aadhaar_number = "Aadhaar number must be exactly 12 digits";
      }
      
      if (!merchantDocuments.pan_number?.trim()) {
        merchantDocsErrors.pan_number = "PAN Number is required";
      } else {
        const pan = merchantDocuments.pan_number.trim().toUpperCase();
        if (pan.length !== 10) {
          merchantDocsErrors.pan_number = "PAN number must be exactly 10 characters";
        } else if (!/^[A-Z]{5}\d{4}[A-Z]$/.test(pan)) {
          merchantDocsErrors.pan_number = "Invalid PAN format. Expected format: ABCDE1234F";
        }
      }
      
      if (!merchantDocuments.gst_number?.trim()) {
        merchantDocsErrors.gst_number = "GST Number is required";
      } else {
        const gstNumber = merchantDocuments.gst_number.trim().toUpperCase();
        // GST number must be exactly 15 characters
        if (gstNumber.length !== 15) {
          merchantDocsErrors.gst_number = "GST number must be exactly 15 characters";
        } else if (!/^[A-Z0-9]{15}$/.test(gstNumber)) {
          merchantDocsErrors.gst_number = "GST number must contain only alphanumeric characters";
        } else {
          // Validate GST format: 2 digits (state) + 10 chars (PAN format) + 3 chars
          // Format: [0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}
          const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
          if (!gstRegex.test(gstNumber)) {
            merchantDocsErrors.gst_number = "Invalid GST number format. Expected format: 27AAAAA0000A1Z5";
          }
        }
      }
      
      if (!merchantDocuments.fssai_number?.trim()) {
        merchantDocsErrors.fssai_number = "FSSAI Number is required";
      } else {
        const fssaiNumber = merchantDocuments.fssai_number.trim();
        // FSSAI number must contain only digits
        if (!/^\d+$/.test(fssaiNumber)) {
          merchantDocsErrors.fssai_number = "FSSAI number must contain only digits";
        } else if (fssaiNumber.length !== 14) {
          merchantDocsErrors.fssai_number = "FSSAI number must be exactly 14 digits";
        }
      }

      // Validate document uploads (all required)
      if (!merchantDocumentFiles.aadhaar_card_file && !merchantDocumentPreviews.aadhaar_card_upload) {
        merchantDocsErrors.aadhaar_card_file = "Aadhaar Card Upload is required";
      }
      if (!merchantDocumentFiles.pan_card_file && !merchantDocumentPreviews.pan_card_upload) {
        merchantDocsErrors.pan_card_file = "PAN Card Upload is required";
      }
      if (!merchantDocumentFiles.fssai_document_file && !merchantDocumentPreviews.fssai_document_upload) {
        merchantDocsErrors.fssai_document_file = "FSSAI Document Upload is required";
      }
      
      // Set all errors and return if any validation fails
      if (Object.keys(formErrors).length > 0 || Object.keys(merchantDocsErrors).length > 0) {
        setErrors(formErrors);
        setMerchantDocumentsErrors(merchantDocsErrors);
        if (Object.keys(merchantDocsErrors).length > 0 && !suppressToasts) {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "please fill all the details",
            life: 5000,
          });
        }
        setLoading(false);
        return false;
      }
      
      await validationSchema.validate(inventoryform, {
        abortEarly: false,
        context: {
          user_role,
          hub_setup: inventoryform?.hub_setup,
          franchise_id: inventoryform?.franchise_id,
        },
      });
      console.log("✅ Validation passed");
      setErrors({});
      setMerchantDocumentsErrors({});

      const mapHubSetupForBackend = (value) => {
        if (!value) return "";
        return value;
      };

      // Function to generate hub link from place and custom_domain
      const generateHubLink = (place, customDomain) => {
        if (!place || !place.trim()) {
          return null;
        }
        
        // Create slug from place name (lowercase, replace spaces with hyphens, remove special chars)
        const slug = place
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
          .replace(/\s+/g, '-') // Replace spaces with hyphens
          .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
          .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
        
        if (!slug) {
          return null;
        }
        
        // Use custom domain if provided, otherwise default to shino.in
        let domain = 'shino.in';
        if (customDomain && customDomain.trim()) {
          domain = customDomain.trim();
          // Remove protocol if user entered it
          domain = domain.replace(/^https?:\/\//, '').replace(/\/$/, '').trim();
        }
        
        return `https://${slug}.${domain}`;
      };

      const payload = {
        ...inventoryform,
        hub_setup:
          inventoryform?.franchise_id === 2
            ? "pincode"
            : mapHubSetupForBackend(inventoryform?.hub_setup),
      };

      // If super_admin is updating, determine which phase to approve based on current status
      if (isEditMode && user_role === 'super_admin') {
        payload.is_approved = true;
        
        // Determine which phase to approve based on current onboarding_status
        if (currentOnboardingStatus === 'phase1_submitted') {
          payload.onboarding_status = 'phase1_approved';
          console.log('✅ Super admin approving Phase 1: phase1_submitted -> phase1_approved');
        } else if (currentOnboardingStatus === 'phase2_submitted') {
          payload.onboarding_status = 'published';
          console.log('✅ Super admin approving Phase 2: phase2_submitted -> published');
          
          // Generate hub_link when publishing
          if (!payload.hub_link || !payload.hub_link.trim()) {
            const generatedHubLink = generateHubLink(
              payload.place,
              payload.custom_domain
            );
            if (generatedHubLink) {
              payload.hub_link = generatedHubLink;
              console.log('🔗 Generated hub_link:', generatedHubLink);
            } else {
              console.warn('⚠️ Could not generate hub_link: place is empty or invalid');
            }
          } else {
            console.log('🔗 Using existing hub_link:', payload.hub_link);
          }
        } else if (currentOnboardingStatus === 'draft') {
          // If status is still draft, approve it as phase1_approved (for backward compatibility)
          payload.onboarding_status = 'phase1_approved';
          console.log('✅ Super admin approving draft hub: draft -> phase1_approved');
        }
      }

      // If merchant is submitting and paying, set onboarding_status to 'phase1_submitted'
      // Only set if current status is 'draft' (not if already submitted or approved)
      if (user_role === 'merchant' && currentOnboardingStatus === 'draft') {
        payload.onboarding_status = 'phase1_submitted';
        console.log('📝 Merchant submitting: Setting onboarding_status to phase1_submitted');
      }

      let response;
      let inventoryId = null;
      if (isEditMode) {
        // Update existing hub
        console.log("🔄 Updating hub with ID:", id);
        console.log("📤 Payload:", payload);
        response = await CustomAxios.put(
          `inventory/update/${id}/`,
          payload,
          {}
        );
        console.log("✅ Update response:", response);
        inventoryId = id;
        
        // Update approval status if hub was approved
        if (user_role === 'super_admin' && payload.is_approved === true) {
          setIsHubApproved(true);
        }

        // Save bank details after hub is updated
        // Bank details are optional, so we don't fail the whole operation if they fail
        try {
          const bankDetailsSaved = await handleSaveBankDetails(inventoryId);
          if (!bankDetailsSaved) {
            // Bank details validation failed, but inventory was updated successfully
            toast.current.show({
              severity: "warn",
              summary: "Warning",
              detail: "Hub updated successfully, but bank details validation failed. Please update bank details.",
              life: 4000,
            });
          }
        } catch (bankError) {
          // Log error but don't fail the whole operation
          console.error("Error saving bank details:", bankError);
          toast.current.show({
            severity: "warn",
            summary: "Warning",
            detail: "Hub updated successfully, but there was an error saving bank details. Please update bank details later.",
            life: 4000,
          });
        }

        // Save merchant documents after hub is updated
        // Merchant documents are optional, so we don't fail the whole operation if they fail
        try {
          const merchantDocsSaved = await handleSaveMerchantDocuments(inventoryId);
          if (!merchantDocsSaved) {
            // Merchant documents validation failed, but inventory was updated successfully
            toast.current.show({
              severity: "warn",
              summary: "Warning",
              detail: "Hub updated successfully, but merchant documents validation failed. Please update merchant documents.",
              life: 4000,
            });
          }
        } catch (merchantDocsError) {
          // Log error but don't fail the whole operation
          console.error("Error saving merchant documents:", merchantDocsError);
          toast.current.show({
            severity: "warn",
            summary: "Warning",
            detail: "Hub updated successfully, but there was an error saving merchant documents. Please update merchant documents later.",
            life: 4000,
          });
        }
        // Update local state to reflect the new onboarding_status
        if (payload.onboarding_status) {
          setCurrentOnboardingStatus(payload.onboarding_status);
          // Update isHubApproved based on onboarding_status
          if (payload.onboarding_status === 'phase1_approved' || payload.onboarding_status === 'published') {
            setIsHubApproved(true);
          }
        }
        
        // Update hub_link in local state if it was generated
        if (payload.hub_link && payload.onboarding_status === 'published') {
          setInventoryForm((prev) => ({
            ...prev,
            hub_link: payload.hub_link,
          }));
          console.log('🔗 Updated local state with hub_link:', payload.hub_link);
        }

        // Refresh hub data from backend to get latest status
        if (isEditMode) {
          try {
            await fetchHubData();
          } catch (error) {
            console.error('Error refreshing hub data:', error);
          }
        }

        if (!suppressToasts) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: user_role === 'super_admin'
              ? "Hub approved for onboarding successfully"
              : "Hub submitted successfully",
            life: 3000,
          });
        }
        setLoading(false);

        return true;
      } else {
        // Create new hub
        try {
          console.log("🆕 Creating new hub");
          console.log("📤 Payload:", payload);
          response = await CustomAxios.post(`inventory/create/`, payload, {});
          console.log("✅ Create response:", response);
        } catch (createError) {
          console.error("❌ Error creating inventory:", createError);
          console.error("📤 Payload sent:", payload);
          throw createError; // Re-throw to be caught by outer catch block
        }

        // Get inventory ID from response
        const hubLink = response?.data?.hub_link || null;
        let newInventoryId = response?.data?.id || null;
        
        console.log("📦 Inventory create response:", response?.data);
        console.log("🆔 New Inventory ID from response:", newInventoryId);
        console.log("🔗 Hub link from response:", hubLink);

        // If ID not in response, try to get it from hub_link by fetching the hub list
        if (!newInventoryId && hubLink) {
          try {
            console.log("🔍 Fetching hub list to find ID by hub_link...");
            const hubListResponse = await CustomAxios.get(`inventory/view`);
            const hubList = hubListResponse?.data || [];
            const createdHub = hubList.find((hub) => hub.hub_link === hubLink);
            
            if (createdHub && createdHub.id) {
              newInventoryId = createdHub.id;
              console.log("✅ Found inventory ID from hub list:", newInventoryId);
            } else {
              console.warn("⚠️ Could not find hub in list by hub_link:", hubLink);
            }
          } catch (fetchError) {
            console.error("❌ Error fetching hub list to get ID:", fetchError);
          }
        }

        inventoryId = newInventoryId;
        console.log("💾 Final inventoryId for bank details and merchant docs:", inventoryId);
        
        if (!inventoryId) {
          console.error("❌ CRITICAL: No inventory ID available! Bank details and merchant documents cannot be saved.");
        }

        // Save bank details after hub is created (if inventory ID is available)
        // Bank details are optional, so we don't fail the whole operation if they fail
        if (inventoryId) {
          console.log("🏦 Starting to save bank details...");
          try {
            const bankDetailsSaved = await handleSaveBankDetails(inventoryId);
            console.log("🏦 Bank details save result:", bankDetailsSaved);
            if (!bankDetailsSaved) {
              // Bank details validation failed, but inventory was created successfully
              toast.current.show({
                severity: "warn",
                summary: "Warning",
                detail: "Hub created successfully, but bank details validation failed. Please update bank details later.",
                life: 4000,
              });
            }
          } catch (bankError) {
            // Log error but don't fail the whole operation
            console.error("Error saving bank details:", bankError);
            toast.current.show({
              severity: "warn",
              summary: "Warning",
              detail: "Hub created successfully, but there was an error saving bank details. Please update bank details later.",
              life: 4000,
            });
          }

          // Save merchant documents after hub is created
          // Merchant documents are optional, so we don't fail the whole operation if they fail
          try {
            console.log("📄 Starting to save merchant documents...");
            const merchantDocsSaved = await handleSaveMerchantDocuments(inventoryId);
            console.log("📄 Merchant documents save result:", merchantDocsSaved);
            if (!merchantDocsSaved) {
              // Merchant documents validation failed, but inventory was created successfully
              toast.current.show({
                severity: "warn",
                summary: "Warning",
                detail: "Hub created successfully, but merchant documents validation failed. Please update merchant documents later.",
                life: 4000,
              });
            }
          } catch (merchantDocsError) {
            // Log error but don't fail the whole operation
            console.error("Error saving merchant documents:", merchantDocsError);
            toast.current.show({
              severity: "warn",
              summary: "Warning",
              detail: "Hub created successfully, but there was an error saving merchant documents. Please update merchant documents later.",
              life: 4000,
            });
          }
        }

        // Show success toast message
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Form submitted successfully",
          life: 3000,
        });

        // Hub link generation and tab switching - commented out
        // Stay on this page and show the generated hub_link instead of redirecting
        // setInventoryForm((prev) => ({
        //   ...prev,
        //   hub_link: hubLink || prev.hub_link,
        // }));

        // Store the created inventory ID so App Configuration can be enabled
        if (newInventoryId) {
          setCurrentInventoryId(newInventoryId);
          setAppConfig((prev) => ({
            ...prev,
            inventory: newInventoryId,
          }));
          
          // Update onboarding status if merchant submitted
          if (user_role === 'merchant' && payload.onboarding_status) {
            setCurrentOnboardingStatus(payload.onboarding_status);
            console.log('📝 Merchant: Updated onboarding status to:', payload.onboarding_status);
          }
          
          // Refresh hub data from backend to get latest status (for merchants)
          // Since we just created the hub, fetch it directly from API
          if (user_role === 'merchant' && newInventoryId) {
            try {
              console.log('🔄 Fetching hub data after creation for merchant...');
              const merchantHubResponse = await CustomAxios.get('inventory/view');
              const merchantHubList = merchantHubResponse?.data || [];
              const createdHub = merchantHubList.find(hub => hub.id === newInventoryId);
              
              if (createdHub && createdHub.onboarding_status) {
                setCurrentOnboardingStatus(createdHub.onboarding_status);
                console.log('✅ Merchant: Onboarding status refreshed from API:', createdHub.onboarding_status);
              }
            } catch (error) {
              console.error('Error refreshing hub data after creation:', error);
            }
          }
          
          // Switch to App Configuration tab after successful creation - commented out
          // setCurrentTab("appConfig");
        } else {
          console.warn("⚠️ No inventoryId available - bank details and merchant documents will not be saved!");
          console.warn("⚠️ Response data:", response?.data);
        }

        setLoading(false);
        return true;
      }
    } catch (validationErrors) {
      console.error("❌ Error in handleSaveInventory:", validationErrors);
      if (validationErrors.name === "ValidationError") {
        const formErrors = {};
        validationErrors.inner.forEach((error) => {
          formErrors[error.path] = error.message;
        });
        console.error("❌ Validation errors:", formErrors);
        setErrors(formErrors);
        
        // Create user-friendly field name mapping
        const fieldNameMap = {
          place: "Business Name",
          mobileno: "Mobile Number",
          email: "Email Address",
          franchise_id: "Business Type",
          supervisor_ids: "Supervisors",
          admin_ids: "Admins",
          customer_engagement_admin_ids: "Customer Engagement Admins",
          cashier_ids: "Cashiers",
          delivery_fee: "Delivery Charge",
          delivery_status: "Delivery Status",
          delivery_mini_spent: "Delivery Minimum Spent",
          self_pickup_waiver: "Self Pickup Waiver",
          self_pickup_waiver_percentage: "Offer Percentage",
          shop_open_at: "Shop Opening Time",
          shop_close_at: "Shop Closing Time",
          pincodes: "Pincodes",
        };
        
        // Build error messages with field names
        const errorList = Object.entries(formErrors).map(([field, message]) => {
          const fieldName = fieldNameMap[field] || field;
          return `${fieldName}: ${message}`;
        });
        
        // Show toast notification for validation errors
        const errorMessage = errorList.length > 0 
          ? `Please fix the following errors: ${errorList.join("; ")}`
          : "Please fill in all required fields";
        
        toast.current?.show({
          severity: "error",
          summary: "Validation Failed",
          detail: errorMessage,
          life: 8000,
        });
      } else if (validationErrors?.response?.data) {
        const serverErrors = validationErrors.response.data;
        const formErrors = {};
        Object.keys(serverErrors).forEach((key) => {
          if (Array.isArray(serverErrors[key]) && serverErrors[key].length > 0) {
            formErrors[key] = serverErrors[key][0];
          } else if (typeof serverErrors[key] === "string") {
            formErrors[key] = serverErrors[key];
          }
        });
        setErrors(formErrors);
      } else {
        console.error("Submit error:", validationErrors);
        console.error("Error details:", {
          message: validationErrors?.message,
          response: validationErrors?.response,
          request: validationErrors?.request,
        });
        if (!suppressToasts) {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: validationErrors?.message || `Failed to create hub: ${validationErrors?.response?.statusText || "Unknown error"}`,
            life: 3000,
          });
        }
      }
      setLoading(false);
      return false;
    }
  };
  console.log("🏪 AddHub: currentOnboardingStatus:", currentOnboardingStatus);

  // Debug: Log current state
  console.log('🎨 Render - fetching:', fetching, 'id:', id, 'isEditMode:', isEditMode, 'user_role:', user_role);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        width: "100%",
        padding: "30px",
      }}
    >
      <Toast ref={toast} />

      {/* Show Hub List table for super_admin when no id (vendor management page) */}
      {user_role === 'super_admin' && !id && (
        <Box sx={{ backgroundColor: "white", borderRadius: "8px", padding: "24px" }}>
          <InventoryTable />
        </Box>
      )}

      {/* Show form when editing a hub or when not super_admin */}
      {(!(user_role === 'super_admin' && !id)) && (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: "700",
                lineHeight: "24px",
              }}
            >
              Vendor Application Details
            </Typography>
          </Box>

          {fetching && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
              }}
            >
              <Typography>Loading hub details...</Typography>
            </Box>
          )}

          {!fetching && (
        <>
          {/* Hub form with tabs */}
          <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <TabContext value={currentTab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}> 
            <TabList
              onChange={handleTabChange}
              aria-label="hub configuration tabs"
            >
              <Tab label="Hub Details" value="hub" />
              <Tab
                label="App Configuration"
                value="appConfig"
                disabled={
                  !currentInventoryId ||
                  currentOnboardingStatus === 'draft' ||
                  currentOnboardingStatus === 'phase1_submitted' ||
                  (user_role === 'super_admin' && currentOnboardingStatus === 'phase1_approved') ||
                  // Disable when prerequisites not completed during phase1_approved for merchants
                  (currentOnboardingStatus === 'phase1_approved' && user_role === 'merchant' && !prerequisitesCompleted)
                }
              />
            </TabList>
          </Box>

          <TabPanel value="hub" sx={{ p: 0, pt: 3 }}>
        <Box>
        {/* Personal Details Section */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", mb: 3 }}>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: "600",
              lineHeight: "24px",
              color: "#333",
            }}
          >
            Personal Details
          </Typography>
          
          <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minWidth: "200px" }}>
              <Typography
                sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "20px", color: "#333" }}
              >
                Mobile Number <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <InputBase
                value={inventoryform.mobileno}
                name="mobileno"
                onChange={handleChangeInventory}
                placeholder="Enter mobile number"
                disabled={shouldDisableFields || (!isEditMode && !!inventoryform.mobileno)}
                sx={{
                  backgroundColor: "white",
                  width: "100%",
                  height: "40px",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                  padding: "10px 12px",
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "20px",
                  color: "#333",
                  "&:focus": {
                    borderColor: "#1976d2",
                    outline: "none",
                  },
                  "&:disabled": {
                    backgroundColor: "#f5f5f5",
                    cursor: "not-allowed",
                  },
                }}
              />
              {errors.mobileno && (
                <Typography
                  sx={{
                    color: "#d32f2f",
                    fontSize: "12px",
                    fontWeight: "400",
                    lineHeight: "20px",
                  }}
                >
                  {errors.mobileno}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minWidth: "200px" }}>
              <Typography
                sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "20px", color: "#333" }}
              >
                Email Address <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <InputBase
                value={inventoryform.email || ""}
                name="email"
                onChange={handleChangeInventory}
                placeholder="Enter email address"
                disabled={shouldDisableFields}
                sx={{
                  backgroundColor: "white",
                  width: "100%",
                  height: "40px",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                  padding: "10px 12px",
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "20px",
                  color: "#333",
                  "&:focus": {
                    borderColor: "#1976d2",
                    outline: "none",
                  },
                }}
              />
              {errors.email && (
                <Typography
                  sx={{
                    color: "#d32f2f",
                    fontSize: "12px",
                    fontWeight: "400",
                    lineHeight: "20px",
                  }}
                >
                  {errors.email}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minWidth: "200px" }}>
              <Typography
                sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "20px", color: "#333" }}
              >
                Aadhaar Number <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <InputBase
                value={merchantDocuments.aadhaar_number || ""}
                name="aadhaar_number"
                onChange={handleMerchantDocumentsChange}
                placeholder="12 digit Aadhaar number"
                maxLength={12}
                disabled={shouldDisableFields}
                sx={{
                  backgroundColor: "white",
                  width: "100%",
                  height: "40px",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                  padding: "10px 12px",
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "20px",
                  color: "#333",
                  "&:focus": {
                    borderColor: "#1976d2",
                    outline: "none",
                  },
                }}
              />
              {merchantDocumentsErrors.aadhaar_number && (
                <Typography
                  sx={{
                    color: "#d32f2f",
                    fontSize: "12px",
                    fontWeight: "400",
                    lineHeight: "20px",
                  }}
                >
                  {merchantDocumentsErrors.aadhaar_number}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Merchant Details Section */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", mb: 3 }}>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: "600",
              lineHeight: "24px",
              color: "#333",
            }}
          >
            Merchant Details
          </Typography>
          
          <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minWidth: "200px" }}>
              <Typography
                sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "20px", color: "#333" }}
              >
                Business Name <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <InputBase
                value={inventoryform.place}
                sx={{
                  backgroundColor: "white",
                  width: "100%",
                  height: "40px",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                  padding: "10px 12px",
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "20px",
                  color: "#333",
                  "&:focus": {
                    borderColor: "#1976d2",
                    outline: "none",
                  },
                }}
                placeholder="Enter business name"
                name="place"
                onChange={handleChangeInventory}
                disabled={shouldDisableFields}
              />
              {errors.place && (
                <Typography
                  sx={{
                    color: "#d32f2f",
                    fontSize: "12px",
                    fontWeight: "400",
                    lineHeight: "20px",
                  }}
                >
                  {errors.place}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minWidth: "200px" }}>
              <Typography
                sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "20px", color: "#333" }}
              >
                Business Type <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <Dropdown
                onChange={handleChangeInventory}
                value={inventoryform.franchise_id}
                options={franchises}
                name="franchise_id"
                optionLabel="name"
                optionValue="id"
                style={{ zIndex: "1000", width: "100%", height: "40px" }}
                placeholder="Select business type"
                className="w-full"
                disabled={shouldDisableFields}
              />
              {errors.franchise_id && (
                <Typography
                  sx={{
                    color: "#d32f2f",
                    fontSize: "12px",
                    fontWeight: "400",
                    lineHeight: "20px",
                  }}
                >
                  {errors.franchise_id}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minWidth: "200px" }}>
              <Typography
                sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "20px", color: "#333" }}
              >
                Delivery Charge <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <InputBase
                type="number"
                step="0.01"
                min="0"
                value={inventoryform.delivery_fee}
                sx={{
                  backgroundColor: "white",
                  width: "100%",
                  height: "40px",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                  padding: "10px 12px",
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "20px",
                  color: "#333",
                  "&:focus": {
                    borderColor: "#1976d2",
                    outline: "none",
                  },
                }}
                placeholder="Enter Delivery Charge"
                name="delivery_fee"
                onChange={handleChangeInventory}
                disabled={shouldDisableFields}
              />
              {errors.delivery_fee && (
                <Typography
                  sx={{
                    color: "#d32f2f",
                    fontSize: "12px",
                    fontWeight: "400",
                    lineHeight: "20px",
                  }}
                >
                  {errors.delivery_fee}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minWidth: "200px" }}>
              <Typography
                sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "20px", color: "#333" }}
              >
                Delivery Mini Spent <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <InputBase
                type="number"
                step="0.01"
                min="0"
                value={inventoryform.delivery_mini_spent}
                sx={{
                  backgroundColor: "white",
                  width: "100%",
                  height: "40px",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                  padding: "10px 12px",
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "20px",
                  color: "#333",
                  "&:focus": {
                    borderColor: "#1976d2",
                    outline: "none",
                  },
                }}
                placeholder="Enter Delivery Minimum Spent"
                name="delivery_mini_spent"
                onChange={handleChangeInventory}
                disabled={shouldDisableFields}
              />
              {errors.delivery_mini_spent && (
                <Typography
                  sx={{
                    color: "#d32f2f",
                    fontSize: "12px",
                    fontWeight: "400",
                    lineHeight: "20px",
                  }}
                >
                  {errors.delivery_mini_spent}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minWidth: "200px" }}>
              <Typography
                sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "20px", color: "#333" }}
              >
                Delivery Status <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <Dropdown
                onChange={handleChangeInventory}
                value={inventoryform.delivery_status}
                options={availability}
                name="delivery_status"
                optionLabel="name"
                style={{ zIndex: "1000", width: "100%", height: "40px" }}
                placeholder="Select Availability"
                className="w-full"
                disabled={shouldDisableFields}
              />
              {errors.delivery_status && (
                <Typography
                  sx={{
                    color: "#d32f2f",
                    fontSize: "12px",
                    fontWeight: "400",
                    lineHeight: "20px",
                  }}
                >
                  {errors.delivery_status}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minWidth: "200px" }}>
              <Typography
                sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "20px", color: "#333" }}
              >
                PAN Number <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <InputBase
                value={merchantDocuments.pan_number || ""}
                name="pan_number"
                onChange={handleMerchantDocumentsChange}
                placeholder="ABCDE1234F"
                maxLength={10}
                disabled={shouldDisableFields}
                sx={{
                  backgroundColor: "white",
                  width: "100%",
                  height: "40px",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                  padding: "10px 12px",
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "20px",
                  color: "#333",
                  "&:focus": {
                    borderColor: "#1976d2",
                    outline: "none",
                  },
                }}
              />
              {merchantDocumentsErrors.pan_number && (
                <Typography
                  sx={{
                    color: "#d32f2f",
                    fontSize: "12px",
                    fontWeight: "400",
                    lineHeight: "20px",
                  }}
                >
                  {merchantDocumentsErrors.pan_number}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minWidth: "200px" }}>
              <Typography
                sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "20px", color: "#333" }}
              >
                GST Number <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <InputBase
                value={merchantDocuments.gst_number || ""}
                name="gst_number"
                onChange={handleMerchantDocumentsChange}
                placeholder="GST number"
                maxLength={15}
                disabled={shouldDisableFields}
                sx={{
                  backgroundColor: "white",
                  width: "100%",
                  height: "40px",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                  padding: "10px 12px",
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "20px",
                  color: "#333",
                  "&:focus": {
                    borderColor: "#1976d2",
                    outline: "none",
                  },
                }}
              />
              {merchantDocumentsErrors.gst_number && (
                <Typography
                  sx={{
                    color: "#d32f2f",
                    fontSize: "12px",
                    fontWeight: "400",
                    lineHeight: "20px",
                  }}
                >
                  {merchantDocumentsErrors.gst_number}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minWidth: "200px" }}>
              <Typography
                sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "20px", color: "#333" }}
              >
                FSSAI Number <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <InputBase
                value={merchantDocuments.fssai_number || ""}
                name="fssai_number"
                onChange={handleMerchantDocumentsChange}
                placeholder="FSSAI number"
                maxLength={14}
                disabled={shouldDisableFields}
                sx={{
                  backgroundColor: "white",
                  width: "100%",
                  height: "40px",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                  padding: "10px 12px",
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "20px",
                  color: "#333",
                  "&:focus": {
                    borderColor: "#1976d2",
                    outline: "none",
                  },
                }}
              />
              {merchantDocumentsErrors.fssai_number && (
                <Typography
                  sx={{
                    color: "#d32f2f",
                    fontSize: "12px",
                    fontWeight: "400",
                    lineHeight: "20px",
                  }}
                >
                  {merchantDocumentsErrors.fssai_number}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minWidth: "200px" }}>
              <Typography
                sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "20px", color: "#333" }}
              >
                Self Pickup <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              <Dropdown
                onChange={(e) => {
                  handleChangeInventory({
                    target: { name: "self_pickup_waiver", value: e.value },
                  });
                }}
                value={
                  inventoryform.self_pickup_waiver === true || inventoryform.self_pickup_waiver === "true" 
                    ? true 
                    : inventoryform.self_pickup_waiver === false || inventoryform.self_pickup_waiver === "false"
                    ? false
                    : null
                }
                options={availability}
                name="self_pickup_waiver"
                optionLabel="name"
                optionValue="value"
                style={{ zIndex: "1000", width: "100%", height: "40px" }}
                placeholder="Select status"
                className="w-full"
                disabled={shouldDisableFields}
              />
              {errors.self_pickup_waiver && (
                <Typography
                  sx={{
                    color: "#d32f2f",
                    fontSize: "12px",
                    fontWeight: "400",
                    lineHeight: "20px",
                  }}
                >
                  {errors.self_pickup_waiver}
                </Typography>
              )}
            </Box>

            {inventoryform.self_pickup_waiver && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1, minWidth: "200px" }}>
                <Typography
                  sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "20px", color: "#333" }}
                >
                  Self Pickup Offer Percentage <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                <InputBase
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={inventoryform.self_pickup_waiver_percentage || ""}
                  sx={{
                    backgroundColor: "white",
                    width: "100%",
                    height: "40px",
                    borderRadius: "4px",
                    border: "1px solid #e0e0e0",
                    padding: "10px 12px",
                    fontSize: "14px",
                    fontWeight: "400",
                    lineHeight: "20px",
                    color: "#333",
                    "&:focus": {
                      borderColor: "#1976d2",
                      outline: "none",
                    },
                  }}
                  placeholder="Enter offer percentage"
                  name="self_pickup_waiver_percentage"
                  onChange={handleChangeInventory}
                  disabled={shouldDisableFields}
                />
                {errors.self_pickup_waiver_percentage && (
                  <Typography
                    sx={{
                      color: "#d32f2f",
                      fontSize: "12px",
                      fontWeight: "400",
                      lineHeight: "20px",
                    }}
                  >
                    {errors.self_pickup_waiver_percentage}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Document Upload Section */}
          <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap", mt: 3, alignItems: "flex-start" }}>
            <Box sx={{ minWidth: "150px", flex: 1, display: "flex", flexDirection: "column" }}>
              <Typography sx={{ fontSize: "12px", mb: 1 }}>
                Aadhaar Card Upload <span style={{ color: "red" }}>*</span>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: "center" }}>
                <Box
                  sx={{
                    width: '104px',
                    height: '104px',
                    border: '1px solid',
                    borderColor: 'secondary.border',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    cursor: 'pointer',
                    backgroundColor: 'background.upload',
                    overflow: 'hidden',
                    '&:hover .overlay': { display: 'block', },
                    '&:hover .delete-button': { display: 'flex', },
                  }}>
                  {merchantDocumentPreviews.aadhaar_card_upload ? (
                    <>
                      <img
                        src={merchantDocumentPreviews.aadhaar_card_upload}
                        alt="Aadhaar card"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', transition: 'opacity 0.3s', }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <Box className="overlay" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#00000080', display: 'none', borderRadius: '8px', }} />
                      {!shouldDisableDocumentUploads && (
                        <Tooltip title="Delete" arrow>
                          <IconButton className="delete-button" onClick={() => handleRemoveMerchantDocument('aadhaar_card_file')} sx={{ position: 'absolute', top: "50%", left: "50%", display: 'none', transform: 'translate(-50%, -50%)', }}>
                            <DeleteOutlineIcon sx={{ color: "white" }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  ) : (
                    <>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        id="upload-aadhaar"
                        style={{ position: 'absolute', width: '104px', height: '104px', opacity: 0, cursor: 'pointer', top: 0, left: 0, zIndex: 1 }}
                        onChange={(e) => handleMerchantDocumentFileChange(e, 'aadhaar_card_file')}
                        disabled={shouldDisableDocumentUploads}
                      />
                      <label htmlFor="upload-aadhaar" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', zIndex: 0 }}>
                        <AddIcon fontSize="large" />
                        <Typography sx={{ fontSize: "12px", fontWeight: "400", lineHeight: "22px" }}>Upload</Typography>
                      </label>
                    </>
                  )}
                </Box>
              </Box>
              {merchantDocumentsErrors.aadhaar_card_file && (
                <Typography sx={{ fontSize: "12px", color: "red", mt: 0.5 }}>
                  {merchantDocumentsErrors.aadhaar_card_file}
                </Typography>
              )}
            </Box>

            <Box sx={{ minWidth: "150px", flex: 1, display: "flex", flexDirection: "column" }}>
              <Typography sx={{ fontSize: "12px", mb: 1 }}>
                PAN Card Upload <span style={{ color: "red" }}>*</span>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: "center" }}>
                <Box
                  sx={{
                    width: '104px',
                    height: '104px',
                    border: '1px solid',
                    borderColor: 'secondary.border',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    cursor: 'pointer',
                    backgroundColor: 'background.upload',
                    overflow: 'hidden',
                    '&:hover .overlay': { display: 'block', },
                    '&:hover .delete-button': { display: 'flex', },
                  }}>
                  {merchantDocumentPreviews.pan_card_upload ? (
                    <>
                      <img
                        src={merchantDocumentPreviews.pan_card_upload}
                        alt="PAN card"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', transition: 'opacity 0.3s', }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <Box className="overlay" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#00000080', display: 'none', borderRadius: '8px', }} />
                      {!shouldDisableDocumentUploads && (
                        <Tooltip title="Delete" arrow>
                          <IconButton className="delete-button" onClick={() => handleRemoveMerchantDocument('pan_card_file')} sx={{ position: 'absolute', top: "50%", left: "50%", display: 'none', transform: 'translate(-50%, -50%)', }}>
                            <DeleteOutlineIcon sx={{ color: "white" }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  ) : (
                    <>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        id="upload-pan"
                        style={{ position: 'absolute', width: '104px', height: '104px', opacity: 0, cursor: 'pointer', top: 0, left: 0, zIndex: 1 }}
                        onChange={(e) => handleMerchantDocumentFileChange(e, 'pan_card_file')}
                        disabled={shouldDisableDocumentUploads}
                      />
                      <label htmlFor="upload-pan" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', zIndex: 0 }}>
                        <AddIcon fontSize="large" />
                        <Typography sx={{ fontSize: "12px", fontWeight: "400", lineHeight: "22px" }}>Upload</Typography>
                      </label>
                    </>
                  )}
                </Box>
              </Box>
              {merchantDocumentsErrors.pan_card_file && (
                <Typography sx={{ fontSize: "12px", color: "red", mt: 0.5 }}>
                  {merchantDocumentsErrors.pan_card_file}
                </Typography>
              )}
            </Box>

            <Box sx={{ minWidth: "150px", flex: 1, display: "flex", flexDirection: "column" }}>
              <Typography sx={{ fontSize: "12px", mb: 1 }}>
                FSSAI Document Upload <span style={{ color: "red" }}>*</span>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: "center" }}>
                <Box
                  sx={{
                    width: '104px',
                    height: '104px',
                    border: '1px solid',
                    borderColor: 'secondary.border',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    cursor: 'pointer',
                    backgroundColor: 'background.upload',
                    overflow: 'hidden',
                    '&:hover .overlay': { display: 'block', },
                    '&:hover .delete-button': { display: 'flex', },
                  }}>
                  {merchantDocumentPreviews.fssai_document_upload ? (
                    <>
                      <img
                        src={merchantDocumentPreviews.fssai_document_upload}
                        alt="FSSAI document"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', transition: 'opacity 0.3s', }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <Box className="overlay" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#00000080', display: 'none', borderRadius: '8px', }} />
                      {!shouldDisableDocumentUploads && (
                        <Tooltip title="Delete" arrow>
                          <IconButton className="delete-button" onClick={() => handleRemoveMerchantDocument('fssai_document_file')} sx={{ position: 'absolute', top: "50%", left: "50%", display: 'none', transform: 'translate(-50%, -50%)', }}>
                            <DeleteOutlineIcon sx={{ color: "white" }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  ) : (
                    <>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        id="upload-fssai"
                        style={{ position: 'absolute', width: '104px', height: '104px', opacity: 0, cursor: 'pointer', top: 0, left: 0, zIndex: 1 }}
                        onChange={(e) => handleMerchantDocumentFileChange(e, 'fssai_document_file')}
                        disabled={shouldDisableDocumentUploads}
                      />
                      <label htmlFor="upload-fssai" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', zIndex: 0 }}>
                        <AddIcon fontSize="large" />
                        <Typography sx={{ fontSize: "12px", fontWeight: "400", lineHeight: "22px" }}>Upload</Typography>
                      </label>
                    </>
                  )}
                </Box>
              </Box>
              {merchantDocumentsErrors.fssai_document_file && (
                <Typography sx={{ fontSize: "12px", color: "red", mt: 0.5 }}>
                  {merchantDocumentsErrors.fssai_document_file}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Bank Details Section - Show for merchants when phase1_approved, and for super_admin when phase2_submitted or published */}
        {((user_role === 'merchant' && (currentOnboardingStatus === 'phase1_approved' || currentOnboardingStatus === 'phase2_submitted' || currentOnboardingStatus === 'published')) || 
          (user_role === 'super_admin' && isEditMode && (currentOnboardingStatus === 'phase2_submitted' || currentOnboardingStatus === 'published'))) && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", mb: 3 }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: "600",
                lineHeight: "24px",
                color: "#333",
              }}
            >
              Bank Details
            </Typography>
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                padding: "16px",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: "16px",
                  mb: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ minWidth: "150px", flex: 1 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: "500", mb: 1, color: "#333" }}>
                    Account Holder Name <span style={{ color: "#d32f2f" }}>*</span>
                  </Typography>
                  <InputBase
                    value={bankDetails.account_holder_name}
                    name="account_holder_name"
                    onChange={handleBankDetailsChange}
                    placeholder="Account Holder Name"
                    disabled={shouldDisableBankDetails}
                    sx={{
                      backgroundColor: "white",
                      width: "100%",
                      height: "40px",
                      borderRadius: "4px",
                      border: "1px solid #e0e0e0",
                      padding: "10px 12px",
                      fontSize: "14px",
                      "&:focus": {
                        borderColor: "#1976d2",
                        outline: "none",
                      },
                    }}
                  />
                  {bankDetailsErrors.account_holder_name && (
                    <Typography sx={{ fontSize: "12px", color: "red", mt: 0.5 }}>
                      {bankDetailsErrors.account_holder_name}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ minWidth: "150px", flex: 1 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: "500", mb: 1, color: "#333" }}>
                    Account Number <span style={{ color: "#d32f2f" }}>*</span>
                  </Typography>
                  <InputBase
                    value={bankDetails.account_number}
                    name="account_number"
                    onChange={handleBankDetailsChange}
                    placeholder="Account Number"
                    disabled={shouldDisableBankDetails}
                    sx={{
                      backgroundColor: "white",
                      width: "100%",
                      height: "40px",
                      borderRadius: "4px",
                      border: "1px solid #e0e0e0",
                      padding: "10px 12px",
                      fontSize: "14px",
                      "&:focus": {
                        borderColor: "#1976d2",
                        outline: "none",
                      },
                    }}
                  />
                  {bankDetailsErrors.account_number && (
                    <Typography sx={{ fontSize: "12px", color: "red", mt: 0.5 }}>
                      {bankDetailsErrors.account_number}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: "16px",
                  mb: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ minWidth: "150px", flex: 1 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: "500", mb: 1, color: "#333" }}>
                    Confirm Account Number <span style={{ color: "#d32f2f" }}>*</span>
                  </Typography>
                  <InputBase
                    value={bankDetails.confirm_account_number}
                    name="confirm_account_number"
                    onChange={handleBankDetailsChange}
                    placeholder="Confirm Account Number"
                    disabled={shouldDisableBankDetails}
                    sx={{
                      backgroundColor: "white",
                      width: "100%",
                      height: "40px",
                      borderRadius: "4px",
                      border: "1px solid #e0e0e0",
                      padding: "10px 12px",
                      fontSize: "14px",
                      "&:focus": {
                        borderColor: "#1976d2",
                        outline: "none",
                      },
                    }}
                  />
                  {bankDetailsErrors.confirm_account_number && (
                    <Typography sx={{ fontSize: "12px", color: "red", mt: 0.5 }}>
                      {bankDetailsErrors.confirm_account_number}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ minWidth: "150px", flex: 1 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: "500", mb: 1, color: "#333" }}>
                    IFSC Code <span style={{ color: "#d32f2f" }}>*</span>
                  </Typography>
                  <InputBase
                    value={bankDetails.ifsc_code}
                    name="ifsc_code"
                    onChange={handleBankDetailsChange}
                    placeholder="IFSC Code"
                    disabled={shouldDisableBankDetails}
                    sx={{
                      backgroundColor: "white",
                      width: "100%",
                      height: "40px",
                      borderRadius: "4px",
                      border: "1px solid #e0e0e0",
                      padding: "10px 12px",
                      fontSize: "14px",
                      "&:focus": {
                        borderColor: "#1976d2",
                        outline: "none",
                      },
                    }}
                  />
                  {bankDetailsErrors.ifsc_code && (
                    <Typography sx={{ fontSize: "12px", color: "red", mt: 0.5 }}>
                      {bankDetailsErrors.ifsc_code}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Hub Setup and Pincode Section - Show for merchants when phase1_approved, and for super_admin when phase2_submitted or published */}
        {((user_role === 'merchant' && (currentOnboardingStatus === 'phase1_approved' || currentOnboardingStatus === 'phase2_submitted' || currentOnboardingStatus === 'published')) || 
          (user_role === 'super_admin' && isEditMode && (currentOnboardingStatus === 'phase2_submitted' || currentOnboardingStatus === 'published'))) && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", mb: 3 }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: "600",
                lineHeight: "24px",
                color: "#333",
              }}
            >
              Hub Setup & Pincode Configuration
            </Typography>
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                padding: "16px",
                width: "100%",
              }}
            >
              {/* Hub Setup Dropdown */}
              {inventoryform?.franchise_id !== 2 && (
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: "500", mb: 1, color: "#333" }}>
                    Hub Setup <span style={{ color: "#d32f2f" }}>*</span>
                  </Typography>
                  <Dropdown
                    onChange={(e) =>
                      handleChangeInventory({
                        target: { name: "hub_setup", value: e.value },
                      })
                    }
                    value={inventoryform.hub_setup}
                    options={[
                      { label: "Pincode", value: "pincode" },
                      { label: "Radius", value: "radius" },
                      { label: "Radius & Pincode", value: "radius_and_pincode" },
                      { label: "Boundary", value: "boundary" },
                    ]}
                    name="hub_setup"
                    optionLabel="label"
                    optionValue="value"
                    style={{ zIndex: "1000", width: "100%" }}
                    placeholder="Select hub setup type"
                    className="w-full md:w-20rem"
                    disabled={shouldDisableHubSetup}
                  />
                  {errors.hub_setup && (
                    <Typography sx={{ fontSize: "12px", color: "red", mt: 0.5 }}>
                      {errors.hub_setup}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Pincode Details - same structure as inventory.js hub setup */}
              <Typography sx={{ fontSize: "14PX", fontWeight: "500", lineHeight: "24px", mt: 2 }}>
                Pincode Details <span style={{ color: "#d32f2f" }}>*</span>
              </Typography>
              {inventoryform?.pincodes?.map((item, index) => (
                <Box
                  key={index}
                  sx={{ border: "1px solid #e0e0e0", borderRadius: "4px", padding: "16px", mb: 2, position: "relative" }}
                >
                  <Typography sx={{ fontSize: "14px", fontWeight: "600", mb: 2 }}>Location {index + 1}</Typography>

                  {/* Basic Info Row (Pincode) - show for 'pincode' or 'radius_and_pincode', and ALWAYS for FoodFix (franchise 2) */}
                  {((inventoryform?.franchise_id !== 2) && (inventoryform?.hub_setup === "pincode" || inventoryform?.hub_setup === "radius_and_pincode")) || (inventoryform?.franchise_id === 2) ? (
                    <Box sx={{ display: "flex", gap: "16px", mb: 2, flexWrap: "wrap" }}>
                      <Box sx={{ minWidth: "150px", flex: 1 }}>
                        <Typography sx={{ fontSize: "12px", mb: 1 }}>Pincode</Typography>
                        <InputBase
                          value={item.pincode || ""}
                          name="pincode"
                          disabled={shouldDisableHubSetup}
                          onChange={(e) => handlePincodeChange(e, index)}
                          placeholder="Pincode"
                          type="tel"
                          maxLength={6}
                          inputProps={{
                            maxLength: 6,
                            pattern: "[0-9]*",
                          }}
                          onKeyPress={(e) => {
                            // Only allow numeric characters
                            if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          sx={inputStyles}
                        />
                        {errors?.[`pincodes[${index}].pincode`] && (
                          <Typography sx={{ color: "red", fontSize: "10px", mt: 0.5 }}>
                            {errors[`pincodes[${index}].pincode`]}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ minWidth: "150px", flex: 1 }}>
                        <Typography sx={{ fontSize: "12px", mb: 1 }}>Latitude</Typography>
                        <InputBase
                          type="number"
                          step="any"
                          value={item.latitude || ""}
                          name="latitude"
                          disabled={shouldDisableHubSetup}
                          onChange={(e) => handlePincodeChange(e, index)}
                          placeholder="Latitude"
                          sx={inputStyles}
                        />
                        {errors?.[`pincodes[${index}].latitude`] && (
                          <Typography sx={{ color: "red", fontSize: "10px", mt: 0.5 }}>
                            {errors[`pincodes[${index}].latitude`]}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ minWidth: "150px", flex: 1 }}>
                        <Typography sx={{ fontSize: "12px", mb: 1 }}>Longitude</Typography>
                        <InputBase
                          type="number"
                          step="any"
                          value={item.longitude || ""}
                          name="longitude"
                          disabled={shouldDisableHubSetup}
                          onChange={(e) => handlePincodeChange(e, index)}
                          placeholder="Longitude"
                          sx={inputStyles}
                        />
                        {errors?.[`pincodes[${index}].longitude`] && (
                          <Typography sx={{ color: "red", fontSize: "10px", mt: 0.5 }}>
                            {errors[`pincodes[${index}].longitude`]}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ) : null}

                  {/* Hub Lat/Lon for 'radius' or 'boundary' (no pincode) */}
                  {inventoryform?.franchise_id !== 2 && (inventoryform?.hub_setup === "radius" || inventoryform?.hub_setup === "boundary") && (
                    <Box sx={{ display: "flex", gap: "16px", mb: 2, flexWrap: "wrap" }}>
                      <Box sx={{ minWidth: "150px", flex: 1 }}>
                        <Typography sx={{ fontSize: "12px", mb: 1 }}>Latitude</Typography>
                        <InputBase
                          type="number"
                          step="any"
                          value={item.latitude || ""}
                          name="latitude"
                          disabled={shouldDisableHubSetup}
                          onChange={(e) => handlePincodeChange(e, index)}
                          placeholder="Latitude"
                          sx={inputStyles}
                        />
                        {errors?.[`pincodes[${index}].latitude`] && (
                          <Typography sx={{ color: "red", fontSize: "10px", mt: 0.5 }}>
                            {errors[`pincodes[${index}].latitude`]}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ minWidth: "150px", flex: 1 }}>
                        <Typography sx={{ fontSize: "12px", mb: 1 }}>Longitude</Typography>
                        <InputBase
                          type="number"
                          step="any"
                          value={item.longitude || ""}
                          name="longitude"
                          disabled={shouldDisableHubSetup}
                          onChange={(e) => handlePincodeChange(e, index)}
                          placeholder="Longitude"
                          sx={inputStyles}
                        />
                        {errors?.[`pincodes[${index}].longitude`] && (
                          <Typography sx={{ color: "red", fontSize: "10px", mt: 0.5 }}>
                            {errors[`pincodes[${index}].longitude`]}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Hub Address - show for franchise 2 or for any hub setup type */}
                  {(inventoryform?.franchise_id === 2 || ["pincode", "radius_and_pincode", "radius", "boundary"].includes(inventoryform?.hub_setup)) && (
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={{ fontSize: "12px", mb: 1 }}>Hub Address</Typography>
                      <InputBase
                        value={item.hub_address || ""}
                        name="hub_address"
                        disabled={shouldDisableHubSetup}
                        onChange={(e) => handlePincodeChange(e, index)}
                        placeholder="Hub Address"
                        sx={{ ...inputStyles, width: "100%" }}
                      />
                      {errors?.[`pincodes[${index}].hub_address`] && (
                        <Typography sx={{ color: "red", fontSize: "10px", mt: 0.5 }}>
                          {errors[`pincodes[${index}].hub_address`]}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Radius Configuration - show for 'radius' and 'radius_and_pincode' when not FoodFix (inner radius only, same as inventory) */}
                  {inventoryform?.franchise_id !== 2 && (inventoryform?.hub_setup === "radius" || inventoryform?.hub_setup === "radius_and_pincode") && (
                    <>
                      <Typography sx={{ fontSize: "13px", fontWeight: "500", mb: 1, color: "#666" }}>Radius Configuration</Typography>
                      <Box sx={{ display: "flex", gap: "16px", mb: 2, flexWrap: "wrap" }}>
                        <Box sx={{ minWidth: "150px", flex: 1 }}>
                          <Typography sx={{ fontSize: "12px", mb: 1 }}>Radius (km)</Typography>
                          <InputBase
                            type="number"
                            step="0.01"
                            value={item.inner_radius || ""}
                            name="inner_radius"
                            disabled={shouldDisableHubSetup}
                            onChange={(e) => handlePincodeChange(e, index)}
                            placeholder="Inner Radius"
                            sx={inputStyles}
                          />
                          {errors?.[`pincodes[${index}].inner_radius`] && (
                            <Typography sx={{ color: "red", fontSize: "10px", mt: 0.5 }}>
                              {errors[`pincodes[${index}].inner_radius`]}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </>
                  )}

                  {/* Boundary Coordinates - show only for 'boundary' when not FoodFix */}
                  {inventoryform?.franchise_id !== 2 && inventoryform?.hub_setup === "boundary" && (
                    <>
                      <Typography sx={{ fontSize: "13px", fontWeight: "500", mb: 1, color: "#666" }}>Boundary Coordinates</Typography>
                      <Box sx={{ display: "flex", gap: "16px", mb: 1, flexWrap: "wrap" }}>
                        <Box sx={{ minWidth: "150px", flex: 1 }}>
                          <Typography sx={{ fontSize: "12px", mb: 1 }}>North Latitude</Typography>
                          <InputBase type="number" step="any" value={item.north_latitude || ""} name="north_latitude" disabled={shouldDisableHubSetup} onChange={(e) => handlePincodeChange(e, index)} placeholder="North Lat" sx={inputStyles} />
                        </Box>
                        <Box sx={{ minWidth: "150px", flex: 1 }}>
                          <Typography sx={{ fontSize: "12px", mb: 1 }}>North Longitude</Typography>
                          <InputBase type="number" step="any" value={item.north_longitude || ""} name="north_longitude" disabled={shouldDisableHubSetup} onChange={(e) => handlePincodeChange(e, index)} placeholder="North Long" sx={inputStyles} />
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", gap: "16px", mb: 1, flexWrap: "wrap" }}>
                        <Box sx={{ minWidth: "150px", flex: 1 }}>
                          <Typography sx={{ fontSize: "12px", mb: 1 }}>South Latitude</Typography>
                          <InputBase type="number" step="any" value={item.south_latitude || ""} name="south_latitude" disabled={shouldDisableHubSetup} onChange={(e) => handlePincodeChange(e, index)} placeholder="South Lat" sx={inputStyles} />
                        </Box>
                        <Box sx={{ minWidth: "150px", flex: 1 }}>
                          <Typography sx={{ fontSize: "12px", mb: 1 }}>South Longitude</Typography>
                          <InputBase type="number" step="any" value={item.south_longitude || ""} name="south_longitude" disabled={shouldDisableHubSetup} onChange={(e) => handlePincodeChange(e, index)} placeholder="South Long" sx={inputStyles} />
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", gap: "16px", mb: 1, flexWrap: "wrap" }}>
                        <Box sx={{ minWidth: "150px", flex: 1 }}>
                          <Typography sx={{ fontSize: "12px", mb: 1 }}>East Latitude</Typography>
                          <InputBase type="number" step="any" value={item.east_latitude || ""} name="east_latitude" disabled={shouldDisableHubSetup} onChange={(e) => handlePincodeChange(e, index)} placeholder="East Lat" sx={inputStyles} />
                        </Box>
                        <Box sx={{ minWidth: "150px", flex: 1 }}>
                          <Typography sx={{ fontSize: "12px", mb: 1 }}>East Longitude</Typography>
                          <InputBase type="number" step="any" value={item.east_longitude || ""} name="east_longitude" disabled={shouldDisableHubSetup} onChange={(e) => handlePincodeChange(e, index)} placeholder="East Long" sx={inputStyles} />
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", gap: "16px", mb: 1, flexWrap: "wrap" }}>
                        <Box sx={{ minWidth: "150px", flex: 1 }}>
                          <Typography sx={{ fontSize: "12px", mb: 1 }}>West Latitude</Typography>
                          <InputBase type="number" step="any" value={item.west_latitude || ""} name="west_latitude" disabled={shouldDisableHubSetup} onChange={(e) => handlePincodeChange(e, index)} placeholder="West Lat" sx={inputStyles} />
                        </Box>
                        <Box sx={{ minWidth: "150px", flex: 1 }}>
                          <Typography sx={{ fontSize: "12px", mb: 1 }}>West Longitude</Typography>
                          <InputBase type="number" step="any" value={item.west_longitude || ""} name="west_longitude" disabled={shouldDisableHubSetup} onChange={(e) => handlePincodeChange(e, index)} placeholder="West Long" sx={inputStyles} />
                        </Box>
                      </Box>
                    </>
                  )}

                  {/* Remove Button - same as inventory (Tooltip + IconButton) */}
                  <Tooltip title="Remove this location" arrow>
                    <IconButton
                      onClick={() => handleRemoveItem(index)}
                      sx={{ position: "absolute", top: "8px", right: "8px", color: "error.main" }}
                      disabled={shouldDisableHubSetup}
                    >
                      <CancelOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}

              <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
                <Button variant="outlined" sx={{ border: "2px dotted", borderColor: "secondary.border", width: "100%" }} onClick={handleAddPincode} disabled={shouldDisableHubSetup}>
                  <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px", color: "black" }}>Add Pincode</Typography>
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Hub Setup - Commented Out */}
        {/* {inventoryform?.franchise_id !== 2 && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              mb: 3,
            }}
          >
            <Typography
              sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}
            >
              Hub Setup
            </Typography>
            <Dropdown
              onChange={(e) =>
                handleChangeInventory({
                  target: { name: "hub_setup", value: e.value },
                })
              }
              value={inventoryform.hub_setup}
              options={[
                { label: "Pincode", value: "pincode" },
                { label: "Radius", value: "radius" },
                { label: "Radius & Pincode", value: "radius_and_pincode" },
                { label: "Boundary", value: "boundary" },
              ]}
              name="hub_setup"
              optionLabel="label"
              optionValue="value"
              style={{ zIndex: "1000" }}
              placeholder="Select hub setup type"
              className="w-full md:w-20rem"
            />
            {errors.hub_setup && (
              <Typography
                style={{
                  color: "red",
                  fontSize: "12PX",
                  fontWeight: "400",
                  lineHeight: "22px",
                }}
              >
                {errors.hub_setup}
              </Typography>
            )}
          </Box>
        )} */}

        <Box sx={{ display: "flex", gap: "16px", mb: 3 }}>

          {/* Admins - Commented out for super_admin role */}
          {/* {user_role === "super_admin" && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              <Typography
                sx={{
                  fontSize: "14PX",
                  fontWeight: "400",
                  lineHeight: "22px",
                }}
              >
                Admins
              </Typography>
              <MultiSelect
                onChange={handleChangeInventory}
                value={inventoryform.admin_ids}
                options={admins}
                name="admin_ids"
                optionLabel="name"
                style={{ zIndex: "1000" }}
                placeholder="Select admin"
                className="w-full md:w-20rem"
              />
              {errors.admin_ids && (
                <Typography
                  style={{
                    color: "red",
                    fontSize: "12PX",
                    fontWeight: "400",
                    lineHeight: "22px",
                  }}
                >
                  {errors.admin_ids}
                </Typography>
              )}
            </Box>
          )} */}

          {/* Customer Engagement Admins - Commented out for super_admin role */}
          {/* {user_role === "super_admin" && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              <Typography
                sx={{
                  fontSize: "14PX",
                  fontWeight: "400",
                  lineHeight: "22px",
                }}
              >
                Customer Engagement Admins
              </Typography>
              <MultiSelect
                onChange={handleChangeInventory}
                value={inventoryform.customer_engagement_admin_ids}
                options={customerEngagementAdmins}
                name="customer_engagement_admin_ids"
                optionLabel="name"
                style={{ zIndex: "1000" }}
                placeholder="Select customer engagement admin"
                className="w-full md:w-20rem"
              />
              {errors.customer_engagement_admin_ids && (
                <Typography
                  style={{
                    color: "red",
                    fontSize: "12PX",
                    fontWeight: "400",
                    lineHeight: "22px",
                  }}
                >
                  {errors.customer_engagement_admin_ids}
                </Typography>
              )}
            </Box>
          )} */}

          {/* Cashiers - Commented out for super_admin role */}
          {/* {user_role === "super_admin" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                width: "220px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14PX",
                  fontWeight: "400",
                  lineHeight: "22px",
                }}
              >
                Cashiers (Optional)
              </Typography>
              <MultiSelect
                onChange={handleChangeInventory}
                value={inventoryform.cashier_ids}
                options={cashiers}
                name="cashier_ids"
                optionLabel="name"
                style={{ zIndex: "1000" }}
                placeholder="Assign cashiers for order management hubs"
                className="w-full md:w-20rem"
              />
              {errors.cashier_ids && (
                <Typography
                  style={{
                    color: "red",
                    fontSize: "12PX",
                    fontWeight: "400",
                    lineHeight: "22px",
                  }}
                >
                  {errors.cashier_ids}
                </Typography>
              )}
              <Typography
                sx={{
                  fontSize: "12PX",
                  fontStyle: "italic",
                  color: "text.secondary",
                }}
              >
                Only needed for hubs that use Order Management
              </Typography>
            </Box>
          )} */}
        </Box>

        {/* Self Pickup Offer - Commented Out */}
        {/* <Box sx={{ display: "flex", gap: "16px", width: "100%", mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              width: "100%",
            }}
          >
            <Typography
              sx={{ fontSize: "14PX", fontWeight: "400", lineHeight: "22px" }}
            >
              Self Pickup Offer
            </Typography>
            <Dropdown
              onChange={handleChangeInventory}
              value={inventoryform.self_pickup_waiver}
              options={availability}
              name="self_pickup_waiver"
              optionLabel="name"
              style={{ zIndex: "1000" }}
              placeholder="Select Availablity"
              className="w-full md:w-20rem"
            />
          </Box>
          {inventoryform.self_pickup_waiver === true && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14PX",
                  fontWeight: "400",
                  lineHeight: "22px",
                }}
              >
                Offer %
              </Typography>
              <InputBase
                type="number"
                step="0.01"
                value={inventoryform.self_pickup_waiver_percentage}
                sx={{
                  backgroundColor: "white",
                  width: "100%",
                  height: "32px",
                  borderRadius: "2px",
                  border: "1px solid",
                  borderColor: "secondary.border",
                  padding: "5px 12px",
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "22px",
                  color: "black",
                }}
                placeholder="Enter Offer %"
                name="self_pickup_waiver_percentage"
                onChange={handleChangeInventory}
              />
              {errors.self_pickup_waiver_percentage && (
                <Typography
                  style={{
                    color: "red",
                    fontSize: "12PX",
                    fontWeight: "400",
                    lineHeight: "22px",
                  }}
                >
                  {errors.self_pickup_waiver_percentage}
                </Typography>
              )}
            </Box>
          )}
        </Box> */}

        {/* Pincode Details Section - Commented Out */}
        {/* <Typography
          sx={{
            fontSize: "16PX",
            fontWeight: "600",
            lineHeight: "24px",
            mt: 2,
            mb: 2,
          }}
        >
          Pincode Details
        </Typography> */}
        {/* {inventoryform?.pincodes?.map((item, index) => (
          <Box
            key={index}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              padding: "16px",
              mb: 2,
              position: "relative",
            }}
          >
            <Typography sx={{ fontSize: "14px", fontWeight: "600", mb: 2 }}>
              Location {index + 1}
            </Typography>

            {((inventoryform?.franchise_id !== 2 &&
              (inventoryform?.hub_setup === "pincode" ||
                inventoryform?.hub_setup === "radius_and_pincode")) ||
              inventoryform?.franchise_id === 2) && (
              <Box
                sx={{
                  display: "flex",
                  gap: "16px",
                  mb: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ minWidth: "150px", flex: 1 }}>
                  <Typography sx={{ fontSize: "12px", mb: 1 }}>
                    Pincode
                  </Typography>
                  <InputBase
                    value={item.pincode}
                    name="pincode"
                    onChange={(e) => handlePincodeChange(e, index)}
                    placeholder="Pincode"
                    sx={inputStyles}
                  />
                </Box>
                <Box sx={{ minWidth: "150px", flex: 1 }}>
                  <Typography sx={{ fontSize: "12px", mb: 1 }}>
                    Latitude
                  </Typography>
                  <InputBase
                    type="number"
                    step="any"
                    value={item.latitude}
                    name="latitude"
                    onChange={(e) => handlePincodeChange(e, index)}
                    placeholder="Latitude"
                    sx={inputStyles}
                  />
                </Box>
                <Box sx={{ minWidth: "150px", flex: 1 }}>
                  <Typography sx={{ fontSize: "12px", mb: 1 }}>
                    Longitude
                  </Typography>
                  <InputBase
                    type="number"
                    step="any"
                    value={item.longitude}
                    name="longitude"
                    onChange={(e) => handlePincodeChange(e, index)}
                    placeholder="Longitude"
                    sx={inputStyles}
                  />
                </Box>
              </Box>
            )}

            {inventoryform?.franchise_id !== 2 &&
              (inventoryform?.hub_setup === "radius" ||
                inventoryform?.hub_setup === "boundary") && (
                <Box
                  sx={{
                    display: "flex",
                    gap: "16px",
                    mb: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ minWidth: "150px", flex: 1 }}>
                    <Typography sx={{ fontSize: "12px", mb: 1 }}>
                      Latitude
                    </Typography>
                    <InputBase
                      type="number"
                      step="any"
                      value={item.latitude}
                      name="latitude"
                      onChange={(e) => handlePincodeChange(e, index)}
                      placeholder="Latitude"
                      sx={inputStyles}
                    />
                  </Box>
                  <Box sx={{ minWidth: "150px", flex: 1 }}>
                    <Typography sx={{ fontSize: "12px", mb: 1 }}>
                      Longitude
                    </Typography>
                    <InputBase
                      type="number"
                      step="any"
                      value={item.longitude}
                      name="longitude"
                      onChange={(e) => handlePincodeChange(e, index)}
                      placeholder="Longitude"
                      sx={inputStyles}
                    />
                  </Box>
                </Box>
              )}

            {(inventoryform?.franchise_id === 2 ||
              ["pincode", "radius_and_pincode", "radius", "boundary"].includes(
                inventoryform?.hub_setup
              )) && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: "12px", mb: 1 }}>
                  Hub Address
                </Typography>
                <InputBase
                  value={item.hub_address}
                  name="hub_address"
                  onChange={(e) => handlePincodeChange(e, index)}
                  placeholder="Hub Address"
                  sx={{ ...inputStyles, width: "100%" }}
                />
              </Box>
            )}

            {inventoryform?.franchise_id !== 2 &&
              (inventoryform?.hub_setup === "radius" ||
                inventoryform?.hub_setup === "radius_and_pincode") && (
                <>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: "500",
                      mb: 1,
                      color: "#666",
                    }}
                  >
                    Radius Configuration
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "16px",
                      mb: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ minWidth: "150px", flex: 1 }}>
                      <Typography sx={{ fontSize: "12px", mb: 1 }}>
                        Radius (km)
                      </Typography>
                      <InputBase
                        type="number"
                        step="0.01"
                        value={item.inner_radius}
                        name="inner_radius"
                        onChange={(e) => handlePincodeChange(e, index)}
                        placeholder="Inner Radius"
                        sx={inputStyles}
                      />
                    </Box>
                  </Box>
                </>
              )}

            {inventoryform?.franchise_id !== 2 &&
              inventoryform?.hub_setup === "boundary" && (
                <>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: "500",
                      mb: 1,
                      color: "#666",
                    }}
                  >
                    Boundary Coordinates
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      gap: "16px",
                      mb: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ minWidth: "150px", flex: 1 }}>
                      <Typography sx={{ fontSize: "12px", mb: 1 }}>
                        North Latitude
                      </Typography>
                      <InputBase
                        type="number"
                        step="any"
                        value={item.north_latitude}
                        name="north_latitude"
                        onChange={(e) => handlePincodeChange(e, index)}
                        placeholder="North Lat"
                        sx={inputStyles}
                      />
                    </Box>
                    <Box sx={{ minWidth: "150px", flex: 1 }}>
                      <Typography sx={{ fontSize: "12px", mb: 1 }}>
                        North Longitude
                      </Typography>
                      <InputBase
                        type="number"
                        step="any"
                        value={item.north_longitude}
                        name="north_longitude"
                        onChange={(e) => handlePincodeChange(e, index)}
                        placeholder="North Long"
                        sx={inputStyles}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: "16px",
                      mb: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ minWidth: "150px", flex: 1 }}>
                      <Typography sx={{ fontSize: "12px", mb: 1 }}>
                        South Latitude
                      </Typography>
                      <InputBase
                        type="number"
                        step="any"
                        value={item.south_latitude}
                        name="south_latitude"
                        onChange={(e) => handlePincodeChange(e, index)}
                        placeholder="South Lat"
                        sx={inputStyles}
                      />
                    </Box>
                    <Box sx={{ minWidth: "150px", flex: 1 }}>
                      <Typography sx={{ fontSize: "12px", mb: 1 }}>
                        South Longitude
                      </Typography>
                      <InputBase
                        type="number"
                        step="any"
                        value={item.south_longitude}
                        name="south_longitude"
                        onChange={(e) => handlePincodeChange(e, index)}
                        placeholder="South Long"
                        sx={inputStyles}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: "16px",
                      mb: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ minWidth: "150px", flex: 1 }}>
                      <Typography sx={{ fontSize: "12px", mb: 1 }}>
                        East Latitude
                      </Typography>
                      <InputBase
                        type="number"
                        step="any"
                        value={item.east_latitude}
                        name="east_latitude"
                        onChange={(e) => handlePincodeChange(e, index)}
                        placeholder="East Lat"
                        sx={inputStyles}
                      />
                    </Box>
                    <Box sx={{ minWidth: "150px", flex: 1 }}>
                      <Typography sx={{ fontSize: "12px", mb: 1 }}>
                        East Longitude
                      </Typography>
                      <InputBase
                        type="number"
                        step="any"
                        value={item.east_longitude}
                        name="east_longitude"
                        onChange={(e) => handlePincodeChange(e, index)}
                        placeholder="East Long"
                        sx={inputStyles}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: "16px",
                      mb: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ minWidth: "150px", flex: 1 }}>
                      <Typography sx={{ fontSize: "12px", mb: 1 }}>
                        West Latitude
                      </Typography>
                      <InputBase
                        type="number"
                        step="any"
                        value={item.west_latitude}
                        name="west_latitude"
                        onChange={(e) => handlePincodeChange(e, index)}
                        placeholder="West Lat"
                        sx={inputStyles}
                      />
                    </Box>
                    <Box sx={{ minWidth: "150px", flex: 1 }}>
                      <Typography sx={{ fontSize: "12px", mb: 1 }}>
                        West Longitude
                      </Typography>
                      <InputBase
                        type="number"
                        step="any"
                        value={item.west_longitude}
                        name="west_longitude"
                        onChange={(e) => handlePincodeChange(e, index)}
                        placeholder="West Long"
                        sx={inputStyles}
                      />
                    </Box>
                  </Box>
                </>
              )}

            <Button
              variant="text"
              color="error"
              sx={{ position: "absolute", top: 8, right: 8, textTransform: "none" }}
              onClick={() => handleRemoveItem(index)}
            >
              Remove
            </Button>
          </Box>
        ))} */}

        {/* <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            sx={{
              border: "2px dotted",
              borderColor: "secondary.border",
              width: "100%",
              textTransform: "none",
            }}
            onClick={handleAddPincode}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "22px",
                color: "black",
              }}
            >
              Add Pincode
            </Typography>
          </Button>
        </Box> */}

          {/* Bank Details Section - Commented Out */}
          {/* <Typography
            sx={{
              fontSize: "16PX",
              fontWeight: "600",
              lineHeight: "24px",
              mt: 2,
              mb: 2,
              width: "100%",
            }}
          >
            Bank Details
          </Typography>
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              padding: "16px",
              mb: 2,
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: "16px",
                mb: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ minWidth: "150px", flex: 1 }}>
                <Typography sx={{ fontSize: "12px", mb: 1 }}>
                  Account Holder Name (Optional)
                </Typography>
                <InputBase
                  value={bankDetails.account_holder_name}
                  name="account_holder_name"
                  onChange={handleBankDetailsChange}
                  placeholder="Account Holder Name"
                  sx={inputStyles}
                />
                {bankDetailsErrors.account_holder_name && (
                  <Typography sx={{ fontSize: "12px", color: "red", mt: 0.5 }}>
                    {bankDetailsErrors.account_holder_name}
                  </Typography>
                )}
              </Box>
              <Box sx={{ minWidth: "150px", flex: 1 }}>
                <Typography sx={{ fontSize: "12px", mb: 1 }}>
                  Account Number (Optional)
                </Typography>
                <InputBase
                  value={bankDetails.account_number}
                  name="account_number"
                  onChange={handleBankDetailsChange}
                  placeholder="Account Number"
                  sx={inputStyles}
                />
                {bankDetailsErrors.account_number && (
                  <Typography sx={{ fontSize: "12px", color: "red", mt: 0.5 }}>
                    {bankDetailsErrors.account_number}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: "16px",
                mb: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ minWidth: "150px", flex: 1 }}>
                <Typography sx={{ fontSize: "12px", mb: 1 }}>
                  Confirm Account Number (Optional)
                </Typography>
                <InputBase
                  value={bankDetails.confirm_account_number}
                  name="confirm_account_number"
                  onChange={handleBankDetailsChange}
                  placeholder="Confirm Account Number"
                  sx={inputStyles}
                />
                {bankDetailsErrors.confirm_account_number && (
                  <Typography sx={{ fontSize: "12px", color: "red", mt: 0.5 }}>
                    {bankDetailsErrors.confirm_account_number}
                  </Typography>
                )}
              </Box>
              <Box sx={{ minWidth: "150px", flex: 1 }}>
                <Typography sx={{ fontSize: "12px", mb: 1 }}>
                  IFSC Code (Optional)
                </Typography>
                <InputBase
                  value={bankDetails.ifsc_code}
                  name="ifsc_code"
                  onChange={handleBankDetailsChange}
                  placeholder="IFSC Code"
                  sx={inputStyles}
                />
                {bankDetailsErrors.ifsc_code && (
                  <Typography sx={{ fontSize: "12px", color: "red", mt: 0.5 }}>
                    {bankDetailsErrors.ifsc_code}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box> */}

          {/* Merchant Documents Section - Commented Out */}
          {/* <Typography
            sx={{
              fontSize: "16PX",
              fontWeight: "600",
              lineHeight: "24px",
              mt: 2,
              mb: 2,
              width: "100%",
            }}
          >
            Merchant Documents
          </Typography>
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              padding: "16px",
              mb: 2,
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: "16px",
                mb: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ minWidth: "150px", flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: "12px", mb: 1 }}>
                    Aadhaar Card Upload (Optional)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: "center" }}>
                    <Box
                      sx={{ 
                        width: '104px', 
                        height: '104px', 
                        border: '1px solid', 
                        borderColor: 'secondary.border', 
                        borderRadius: '8px', 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        position: 'relative', 
                        cursor: 'pointer', 
                        backgroundColor: 'background.upload', 
                        overflow: 'hidden',
                        '&:hover .overlay': { display: 'block', }, 
                        '&:hover .delete-button': { display: 'flex', },
                      }}>
                      {merchantDocumentPreviews.aadhaar_card_upload ? (
                        <>
                          <img 
                            src={merchantDocumentPreviews.aadhaar_card_upload} 
                            alt="Aadhaar card" 
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', transition: 'opacity 0.3s', }} 
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <Box className="overlay" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#00000080', display: 'none', borderRadius: '8px', }} />
                          <Tooltip title="Delete" arrow>
                            <IconButton className="delete-button" onClick={() => handleRemoveMerchantDocument('aadhaar_card_file')} sx={{ position: 'absolute', top: "50%", left: "50%", display: 'none', transform: 'translate(-50%, -50%)', }}>
                              <DeleteOutlineIcon sx={{ color: "white" }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <input 
                            type="file" 
                            accept="image/*,.pdf" 
                            id="upload-aadhaar" 
                            style={{ position: 'absolute', width: '104px', height: '104px', opacity: 0, cursor: 'pointer', top: 0, left: 0, zIndex: 1 }} 
                            onChange={(e) => handleMerchantDocumentFileChange(e, 'aadhaar_card_file')} 
                          />
                          <label htmlFor="upload-aadhaar" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', zIndex: 0 }}>
                            <AddIcon fontSize="large" />
                            <Typography sx={{ fontSize: "12px", fontWeight: "400", lineHeight: "22px" }}>Upload</Typography>
                          </label>
                        </>
                      )}
                    </Box>
                  </Box>
                  {merchantDocumentsErrors.aadhaar_card_file && (
                    <Typography sx={{ fontSize: "12px", color: "red", mt: 0.5 }}>
                      {merchantDocumentsErrors.aadhaar_card_file}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ minWidth: "150px", flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ height: "54px" }}></Box>
                <Box>
                  <Typography sx={{ fontSize: "12px", mb: 1 }}>
                    PAN Card Upload (Optional)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: "center" }}>
                    <Box
                      sx={{ 
                        width: '104px', 
                        height: '104px', 
                        border: '1px solid', 
                        borderColor: 'secondary.border', 
                        borderRadius: '8px', 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        position: 'relative', 
                        cursor: 'pointer', 
                        backgroundColor: 'background.upload', 
                        overflow: 'hidden',
                        '&:hover .overlay': { display: 'block', }, 
                        '&:hover .delete-button': { display: 'flex', },
                      }}>
                      {merchantDocumentPreviews.pan_card_upload ? (
                        <>
                          <img 
                            src={merchantDocumentPreviews.pan_card_upload} 
                            alt="PAN card" 
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', transition: 'opacity 0.3s', }} 
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <Box className="overlay" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#00000080', display: 'none', borderRadius: '8px', }} />
                          <Tooltip title="Delete" arrow>
                            <IconButton className="delete-button" onClick={() => handleRemoveMerchantDocument('pan_card_file')} sx={{ position: 'absolute', top: "50%", left: "50%", display: 'none', transform: 'translate(-50%, -50%)', }}>
                              <DeleteOutlineIcon sx={{ color: "white" }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <input 
                            type="file" 
                            accept="image/*,.pdf" 
                            id="upload-pan" 
                            style={{ position: 'absolute', width: '104px', height: '104px', opacity: 0, cursor: 'pointer', top: 0, left: 0, zIndex: 1 }} 
                            onChange={(e) => handleMerchantDocumentFileChange(e, 'pan_card_file')} 
                          />
                          <label htmlFor="upload-pan" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', zIndex: 0 }}>
                            <AddIcon fontSize="large" />
                            <Typography sx={{ fontSize: "12px", fontWeight: "400", lineHeight: "22px" }}>Upload</Typography>
                          </label>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ minWidth: "150px", flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ height: "54px" }}></Box>
                <Box>
                  <Typography sx={{ fontSize: "12px", mb: 1 }}>
                    FSSAI Expiry Date (Optional)
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={
                        merchantDocuments.fssai_expiry_date
                          ? dayjs(merchantDocuments.fssai_expiry_date)
                          : null
                      }
                      onChange={(newValue) =>
                        handleMerchantDocumentsChange({
                          target: {
                            name: "fssai_expiry_date",
                            value: newValue ? (dayjs.isDayjs(newValue) ? newValue.format("YYYY-MM-DD") : dayjs(newValue).format("YYYY-MM-DD")) : "",
                          },
                        })
                      }
                      slotProps={{
                        textField: {
                          sx: {
                            height: "38px",
                            "& .MuiInputBase-root": {
                              height: "38px",
                              fontSize: "14px",
                              padding: "0 10px",
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  {merchantDocumentsErrors.fssai_expiry_date && (
                    <Typography sx={{ fontSize: "12px", color: "red", mt: 0.5 }}>
                      {merchantDocumentsErrors.fssai_expiry_date}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "12px", mb: 1 }}>
                    FSSAI Document Upload (Optional)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: "center" }}>
                    <Box
                      sx={{ 
                        width: '104px', 
                        height: '104px', 
                        border: '1px solid', 
                        borderColor: 'secondary.border', 
                        borderRadius: '8px', 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        position: 'relative', 
                        cursor: 'pointer', 
                        backgroundColor: 'background.upload', 
                        overflow: 'hidden',
                        '&:hover .overlay': { display: 'block', }, 
                        '&:hover .delete-button': { display: 'flex', },
                      }}>
                      {merchantDocumentPreviews.fssai_document_upload ? (
                        <>
                          <img 
                            src={merchantDocumentPreviews.fssai_document_upload} 
                            alt="FSSAI document" 
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', transition: 'opacity 0.3s', }} 
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <Box className="overlay" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#00000080', display: 'none', borderRadius: '8px', }} />
                          <Tooltip title="Delete" arrow>
                            <IconButton className="delete-button" onClick={() => handleRemoveMerchantDocument('fssai_document_file')} sx={{ position: 'absolute', top: "50%", left: "50%", display: 'none', transform: 'translate(-50%, -50%)', }}>
                              <DeleteOutlineIcon sx={{ color: "white" }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <input 
                            type="file" 
                            accept="image/*,.pdf" 
                            id="upload-fssai" 
                            style={{ position: 'absolute', width: '104px', height: '104px', opacity: 0, cursor: 'pointer', top: 0, left: 0, zIndex: 1 }} 
                            onChange={(e) => handleMerchantDocumentFileChange(e, 'fssai_document_file')} 
                          />
                          <label htmlFor="upload-fssai" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', zIndex: 0 }}>
                            <AddIcon fontSize="large" />
                            <Typography sx={{ fontSize: "12px", fontWeight: "400", lineHeight: "22px" }}>Upload</Typography>
                          </label>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box> */}

          {/* Button logic based on role and approval status */}
          
          {/* For merchant: Show button for draft, phase1_submitted, and phase1_approved. Hide for phase2_submitted and published */}
          {user_role === 'merchant' && 
           currentOnboardingStatus !== 'phase2_submitted' && 
           currentOnboardingStatus !== 'published' && (
            <Button
              type="button"
              variant="contained"
              sx={{ textTransform: "none", height: "32px", minWidth: "90px" }}
              onClick={async () => {
                if (currentOnboardingStatus === 'phase1_approved') {
                  // Validate hub setup and pincode configuration first
                  const hubSetupValidationErrors = {};

                  // Validate Hub Setup (required field)
                  if (!inventoryform.hub_setup) {
                    hubSetupValidationErrors.hub_setup = "Hub Setup is required";
                  }

                  // Validate Pincode Details based on hub_setup type
                  if (inventoryform.pincodes && inventoryform.pincodes.length > 0) {
                    inventoryform.pincodes.forEach((pincode, index) => {
                      // Common validations for latitude and longitude
                      if (!pincode.latitude || pincode.latitude === "") {
                        hubSetupValidationErrors[`pincodes[${index}].latitude`] = "Latitude is required";
                      } else {
                        const lat = parseFloat(pincode.latitude);
                        if (isNaN(lat) || lat < -90 || lat > 90) {
                          hubSetupValidationErrors[`pincodes[${index}].latitude`] = "Latitude must be between -90 and 90";
                        }
                      }

                      if (!pincode.longitude || pincode.longitude === "") {
                        hubSetupValidationErrors[`pincodes[${index}].longitude`] = "Longitude is required";
                      } else {
                        const lng = parseFloat(pincode.longitude);
                        if (isNaN(lng) || lng < -180 || lng > 180) {
                          hubSetupValidationErrors[`pincodes[${index}].longitude`] = "Longitude must be between -180 and 180";
                        }
                      }

                      // Hub Address validation (required for all hub setups)
                      if (!pincode.hub_address || pincode.hub_address.trim() === "") {
                        hubSetupValidationErrors[`pincodes[${index}].hub_address`] = "Hub Address is required";
                      }

                      // Pincode-specific validations
                      if (inventoryform.hub_setup === "pincode" || inventoryform.hub_setup === "radius_and_pincode") {
                        if (!pincode.pincode || pincode.pincode.trim() === "") {
                          hubSetupValidationErrors[`pincodes[${index}].pincode`] = "Pincode is required";
                        } else if (!/^\d{6}$/.test(pincode.pincode.trim())) {
                          hubSetupValidationErrors[`pincodes[${index}].pincode`] = "Pincode must be exactly 6 digits";
                        }
                      }

                      // Radius-specific validations
                      if (inventoryform.hub_setup === "radius" || inventoryform.hub_setup === "radius_and_pincode") {
                        if (!pincode.inner_radius || pincode.inner_radius === "") {
                          hubSetupValidationErrors[`pincodes[${index}].inner_radius`] = "Radius (km) is required";
                        } else {
                          const radius = parseFloat(pincode.inner_radius);
                          if (isNaN(radius) || radius <= 0) {
                            hubSetupValidationErrors[`pincodes[${index}].inner_radius`] = "Radius must be a positive number";
                          }
                        }
                      }

                      // Boundary-specific validations
                      if (inventoryform.hub_setup === "boundary") {
                        const boundaryFields = [
                          { key: 'north_latitude', name: 'North Latitude' },
                          { key: 'north_longitude', name: 'North Longitude' },
                          { key: 'south_latitude', name: 'South Latitude' },
                          { key: 'south_longitude', name: 'South Longitude' },
                          { key: 'east_latitude', name: 'East Latitude' },
                          { key: 'east_longitude', name: 'East Longitude' },
                          { key: 'west_latitude', name: 'West Latitude' },
                          { key: 'west_longitude', name: 'West Longitude' }
                        ];

                        boundaryFields.forEach(field => {
                          if (!pincode[field.key] || pincode[field.key] === "") {
                            hubSetupValidationErrors[`pincodes[${index}].${field.key}`] = `${field.name} is required`;
                          } else {
                            const value = parseFloat(pincode[field.key]);
                            if (isNaN(value)) {
                              hubSetupValidationErrors[`pincodes[${index}].${field.key}`] = `${field.name} must be a valid number`;
                            } else if (field.key.includes('latitude') && (value < -90 || value > 90)) {
                              hubSetupValidationErrors[`pincodes[${index}].${field.key}`] = `${field.name} must be between -90 and 90`;
                            } else if (field.key.includes('longitude') && (value < -180 || value > 180)) {
                              hubSetupValidationErrors[`pincodes[${index}].${field.key}`] = `${field.name} must be between -180 and 180`;
                            }
                          }
                        });
                      }
                    });
                  } else {
                    // If no pincodes are added, that's also an error
                    hubSetupValidationErrors.pincodes = "At least one location must be added";
                  }

                  // If there are hub setup validation errors, set them and return
                  if (Object.keys(hubSetupValidationErrors).length > 0) {
                    setErrors(hubSetupValidationErrors);
                    toast.current?.show({
                      severity: "error",
                      summary: "Validation Error",
                      detail: "Please fill in all required hub setup and pincode details before continuing",
                      life: 4000,
                    });
                    return;
                  }

                  // Validate bank details before navigating to App Configuration tab
                  const bankDetailsValidationErrors = {};
                  
                  // Validate Account Holder Name
                  if (!bankDetails.account_holder_name?.trim()) {
                    bankDetailsValidationErrors.account_holder_name = "Account Holder Name is required";
                  } else {
                    const accountHolderName = bankDetails.account_holder_name.trim();
                    // Should contain only letters, spaces, dots, and hyphens
                    if (!/^[a-zA-Z\s.\-]+$/.test(accountHolderName)) {
                      bankDetailsValidationErrors.account_holder_name = "Account Holder Name must contain only letters, spaces, dots, and hyphens";
                    } else if (accountHolderName.length < 2) {
                      bankDetailsValidationErrors.account_holder_name = "Account Holder Name must be at least 2 characters";
                    } else if (accountHolderName.length > 100) {
                      bankDetailsValidationErrors.account_holder_name = "Account Holder Name must not exceed 100 characters";
                    }
                  }
                  
                  // Validate Account Number
                  if (!bankDetails.account_number?.trim()) {
                    bankDetailsValidationErrors.account_number = "Account Number is required";
                  } else {
                    const accountNumber = bankDetails.account_number.trim();
                    // Should contain only digits
                    if (!/^\d+$/.test(accountNumber)) {
                      bankDetailsValidationErrors.account_number = "Account Number must contain only digits";
                    } else if (accountNumber.length < 9) {
                      bankDetailsValidationErrors.account_number = "Account Number must be at least 9 digits";
                    } else if (accountNumber.length > 18) {
                      bankDetailsValidationErrors.account_number = "Account Number must not exceed 18 digits";
                    }
                  }
                  
                  // Validate Confirm Account Number
                  if (!bankDetails.confirm_account_number?.trim()) {
                    bankDetailsValidationErrors.confirm_account_number = "Confirm Account Number is required";
                  } else {
                    const confirmAccountNumber = bankDetails.confirm_account_number.trim();
                    // Should contain only digits
                    if (!/^\d+$/.test(confirmAccountNumber)) {
                      bankDetailsValidationErrors.confirm_account_number = "Confirm Account Number must contain only digits";
                    } else if (confirmAccountNumber.length < 9) {
                      bankDetailsValidationErrors.confirm_account_number = "Confirm Account Number must be at least 9 digits";
                    } else if (confirmAccountNumber.length > 18) {
                      bankDetailsValidationErrors.confirm_account_number = "Confirm Account Number must not exceed 18 digits";
                    }
                  }
                  
                  // Check if account numbers match
                  if (
                    bankDetails.account_number?.trim() &&
                    bankDetails.confirm_account_number?.trim() &&
                    bankDetails.account_number.trim() !== bankDetails.confirm_account_number.trim()
                  ) {
                    bankDetailsValidationErrors.confirm_account_number = "Account numbers do not match";
                  }
                  
                  // Validate IFSC Code
                  if (!bankDetails.ifsc_code?.trim()) {
                    bankDetailsValidationErrors.ifsc_code = "IFSC Code is required";
                  } else {
                    const ifsc = bankDetails.ifsc_code.trim().toUpperCase();
                    if (ifsc.length !== 11 || !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
                      bankDetailsValidationErrors.ifsc_code = "Invalid IFSC code format. Expected format: ABCD0123456";
                    }
                  }
                  
                  // If there are validation errors, set them and return
                  if (Object.keys(bankDetailsValidationErrors).length > 0) {
                    setBankDetailsErrors(bankDetailsValidationErrors);
                    toast.current?.show({
                      severity: "error",
                      summary: "Error",
                      detail: "Please fill in all bank details before continuing",
                      life: 4000,
                    });
                    return;
                  }
                  
                  // Save all fields: inventory (hub setup, pincodes), bank details, then navigate to App Configuration tab
                  if (currentInventoryId) {
                    try {
                      setLoading(true);
                      // First, save inventory with all fields (hub_setup, pincodes, etc.)
                      const mapHubSetupForBackend = (value) => {
                        if (!value) return "";
                        return value;
                      };

                      const payload = {
                        ...inventoryform,
                        hub_setup:
                          inventoryform?.franchise_id === 2
                            ? "pincode"
                            : mapHubSetupForBackend(inventoryform?.hub_setup),
                      };

                      await CustomAxios.put(
                        `inventory/update/${currentInventoryId}/`,
                        payload,
                        {}
                      );
                      console.log("✅ Inventory updated with all fields");

                      // Then save bank details
                      const bankDetailsSaved = await handleSaveBankDetails(currentInventoryId);
                      if (!bankDetailsSaved) {
                        setLoading(false);
                        return;
                      }
                      console.log("✅ Bank details saved");

                      // Mark prerequisites as completed so App Configuration tab becomes enabled
                      setPrerequisitesCompleted(true);

                      setLoading(false);
                      setCurrentTab("appConfig");
                    } catch (error) {
                      console.error("Error saving inventory or bank details:", error);
                      setLoading(false);
                      toast.current?.show({
                        severity: "error",
                        summary: "Error",
                        detail: "Failed to save changes. Please try again.",
                        life: 4000,
                      });
                    }
                  } else {
                    setCurrentTab("appConfig");
                  }
                } else {
                  // Save inventory (create or update)
                  handleSaveInventory();
                }
              }}
              disabled={loading || currentOnboardingStatus === 'phase1_submitted' || (currentOnboardingStatus !== 'phase1_approved' && isEditMode && isHubApproved && shouldDisableFields)}
            >
              {loading ? "Saving..." : 
               currentOnboardingStatus === 'phase1_submitted' ? "Phase 1 Submitted" :
               currentOnboardingStatus === 'phase1_approved' ? "Continue" : 
               "Submit and Pay"}
            </Button>
          )}
          
          {/* For super_admin: Show buttons aligned in a row */}
          {user_role === 'super_admin' && isEditMode && (
            <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Cashfree Vendor Management Buttons - Show after phase1_approved */}
              {(currentOnboardingStatus === 'phase1_approved' || 
                currentOnboardingStatus === 'phase2_submitted' || 
                currentOnboardingStatus === 'published') && (
                <>
                  {/* Create Vendor Button */}
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    sx={{ textTransform: "none", height: "32px", minWidth: "120px" }}
                    onClick={handleCreateVendor}
                    disabled={vendorLoading || !id || easysplitVendorStatus === 'ACTIVE' || 
                             (currentOnboardingStatus !== 'phase2_submitted' && currentOnboardingStatus !== 'published')}
                  >
                    {vendorLoading ? "Creating..." : 
                     easysplitVendorStatus === 'ACTIVE' ? "Vendor Active" : 
                     "Create Vendor"}
                  </Button>
                  
                  {/* Get Status Button */}
                  <Button
                    type="button"
                    variant="outlined"
                    sx={{ textTransform: "none", height: "32px", minWidth: "100px" }}
                    onClick={handleGetVendorStatus}
                    disabled={vendorLoading || !id || 
                             (currentOnboardingStatus !== 'phase2_submitted' && currentOnboardingStatus !== 'published')}
                  >
                    {vendorLoading ? "Loading..." : "Get Status"}
                  </Button>
                  
                  {/* Status Display */}
                    {easysplitVendorStatus && (
                      <Typography 
                        sx={{ 
                          fontSize: "12px", 
                          fontWeight: "500",
                          color: easysplitVendorStatus === 'ACTIVE' ? 'green' : 'orange',
                          height: "32px",
                          minWidth: "100px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: '4px',
                          backgroundColor: easysplitVendorStatus === 'ACTIVE' ? '#e8f5e9' : '#fff3e0'
                        }}
                      >
                        Status: {easysplitVendorStatus}
                      </Typography>
                    )}
                </>
              )}
              
              {/* Approve/Publish button */}
              <Button
                type="button"
                variant="contained"
                sx={{ textTransform: "none", height: "32px", minWidth: "90px" }}
                onClick={handleSaveInventory}
                disabled={
                  loading || 
                  currentOnboardingStatus === 'phase1_approved' || 
                  currentOnboardingStatus === 'published' ||
                  (currentOnboardingStatus === 'phase2_submitted' && easysplitVendorStatus !== 'ACTIVE')
                }
              >
                {loading ? (
                  currentOnboardingStatus === 'phase2_submitted' ? "Publishing..." : "Approving..."
                ) : (
                  currentOnboardingStatus === 'published' ? "Published" :
                  currentOnboardingStatus === 'phase2_submitted' ? "Publish" :
                  currentOnboardingStatus === 'phase1_submitted' ? "Approve Phase 1" :
                  "Phase 1 Approved"
                )}
              </Button>
            </Box>
          )}
          
          {/* For other roles (not merchant, not super_admin viewing unapproved): Show default button */}
          {user_role !== 'merchant' && 
           !(user_role === 'super_admin' && isEditMode && !isHubApproved) && (
            <Button
              type="button"
              variant="contained"
              sx={{ textTransform: "none", height: "32px", minWidth: "90px" }}
              onClick={handleSaveInventory}
              disabled={loading || shouldDisableFields || (isEditMode && user_role === 'super_admin' && isHubApproved)}
            >
              {loading ? (isEditMode ? "Approving..." : "Saving...") : (isEditMode ? "Approve for onboarding" : "Submit and Pay")}
            </Button>
          )}
          
          {/* Status Display - Show for merchants based on onboarding status */}
          {user_role === 'merchant' && (
            <Box
              sx={{
                width: "100%",
                backgroundColor: currentOnboardingStatus === 'draft' ? "#f5f5f5" : 
                                currentOnboardingStatus === 'phase1_submitted' ? "#fef3c7" : 
                                currentOnboardingStatus === 'phase1_approved' ? "#dbeafe" :
                                currentOnboardingStatus === 'phase2_submitted' ? "#fef3c7" :
                                currentOnboardingStatus === 'published' ? "#f0fdf4" :
                                "#fee2e2",
                borderRadius: "8px",
                padding: "16px",
                marginTop: "16px",
                boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                border: currentOnboardingStatus === 'draft' ? "none" : 
                       currentOnboardingStatus === 'phase1_submitted' ? "1px solid #fbbf24" : 
                       currentOnboardingStatus === 'phase1_approved' ? "1px solid #3b82f6" :
                       currentOnboardingStatus === 'phase2_submitted' ? "1px solid #fbbf24" :
                       currentOnboardingStatus === 'published' ? "1px solid #86efac" :
                       "1px solid #ef4444",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "600",
                  lineHeight: "22px",
                  color: currentOnboardingStatus === 'draft' ? "#059669" : 
                         currentOnboardingStatus === 'phase1_submitted' ? "#d97706" : 
                         currentOnboardingStatus === 'phase1_approved' ? "#2563eb" :
                         currentOnboardingStatus === 'phase2_submitted' ? "#d97706" :
                         currentOnboardingStatus === 'published' ? "#16a34a" :
                         "#dc2626",
                }}
              >
                Status: {
                  currentOnboardingStatus === 'draft' ? 'Draft' :
                  currentOnboardingStatus === 'phase1_submitted' ? 'Phase 1 Submitted' :
                  currentOnboardingStatus === 'phase1_approved' ? 'Phase 1 Approved' :
                  currentOnboardingStatus === 'phase2_submitted' ? 'Phase 2 Submitted' :
                  currentOnboardingStatus === 'published' ? 'Published' :
                  currentOnboardingStatus === 'rejected' ? 'Rejected' :
                  'Draft'
                }
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "400",
                  lineHeight: "20px",
                  color: currentOnboardingStatus === 'draft' ? "#059669" : 
                         currentOnboardingStatus === 'phase1_submitted' ? "#92400e" : 
                         currentOnboardingStatus === 'phase1_approved' ? "#1e40af" :
                         currentOnboardingStatus === 'phase2_submitted' ? "#92400e" :
                         currentOnboardingStatus === 'published' ? "#15803d" :
                         "#991b1b",
                }}
              >
                {currentOnboardingStatus === 'draft' && "Fill in the hub details and click 'Submit and Pay' to submit your hub for Phase 1 approval."}
                {currentOnboardingStatus === 'phase1_submitted' && "Your hub has been submitted for Phase 1 approval and is pending review from super admin. You will be notified once it's approved."}
                {currentOnboardingStatus === 'phase1_approved' && "Phase 1 has been approved! Please fill in your bank details and click 'Continue' to proceed with App Configuration for Phase 2 approval."}
                {currentOnboardingStatus === 'phase2_submitted' && "Your app configuration has been submitted for Phase 2 approval and is pending review from super admin. You will be notified once it's approved."}
                {currentOnboardingStatus === 'published' && "Congratulations! Your hub has been fully approved and published. You can now start using your hub."}
                {currentOnboardingStatus === 'rejected' && "Your hub submission has been rejected. Please contact super admin for more details."}
              </Typography>
              
              {/* Pending Actions - Show in red when bank details or app config is empty (only for phase1_approved) */}
              {currentOnboardingStatus === 'phase1_approved' && (() => {
                const isBankDetailsEmpty = !bankDetails.id && 
                  (!bankDetails.account_holder_name?.trim() || 
                   !bankDetails.account_number?.trim() || 
                   !bankDetails.confirm_account_number?.trim() || 
                   !bankDetails.ifsc_code?.trim());
                
                const isAppConfigEmpty = !appConfigId || 
                  !appConfig.app_title?.trim() || 
                  !appConfig.app_short_name?.trim() || 
                  !appConfig.app_description?.trim() || 
                  !appConfig.theme_color?.trim() || 
                  !appConfig.background_color?.trim();
                
                const hasPendingActions = isBankDetailsEmpty || isAppConfigEmpty;
                
                if (hasPendingActions) {
                  return (
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: "600",
                          lineHeight: "20px",
                          color: "#dc2626",
                          mb: 0.5,
                        }}
                      >
                        Pending Actions:
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 1 }}>
                        {isBankDetailsEmpty && (
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: "400",
                              lineHeight: "20px",
                              color: "#dc2626",
                            }}
                          >
                            • Bank Details: Please fill in all bank details (Account Holder Name, Account Number, Confirm Account Number, IFSC Code)
                          </Typography>
                        )}
                        {isAppConfigEmpty && (
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: "400",
                              lineHeight: "20px",
                              color: "#dc2626",
                            }}
                          >
                            • App Configuration: Please complete the App Configuration (App Title, App Short Name, App Description, Theme Color, Background Color, and Icons)
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                }
                return null;
              })()}
            </Box>
          )}
          
          {/* Status Display - Show for other roles before hub is created */}
          {user_role !== 'merchant' && !isEditMode && (
            <Box
              sx={{
                width: "100%",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
                padding: "16px",
                marginTop: "16px",
                boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "22px",
                  color: "#059669",
                }}
              >
                Status: Draft
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "22px",
                  color: "#059669",
                }}
              >
                Bank Details: Not Available
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "22px",
                  color: "#059669",
                }}
              >
                App Configuration: Not Available
              </Typography>
            </Box>
          )}
        </Box>

        {inventoryform?.hub_link && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "12px",
            mt: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "22px",
            }}
          >
            Hub Link:&nbsp;
            <a
              href={inventoryform.hub_link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", textDecoration: "underline" }}
            >
              {inventoryform.hub_link}
            </a>
          </Typography>
          <Button
            variant="outlined"
            sx={{ textTransform: "none", height: "28px" }}
            onClick={() => {
              if (inventoryform.hub_link) {
                window.open(inventoryform.hub_link, "_blank", "noopener");
              }
            }}
          >
            Open
          </Button>
        </Box>
      )}

          </TabPanel>

          <TabPanel value="appConfig" sx={{ p: 0, pt: 3 }}>
            <Typography
              sx={{
                fontSize: "16PX",
                fontWeight: "600",
                lineHeight: "24px",
                mb: 2,
              }}
            >
              App Configuration
            </Typography>

        <Box sx={{ display: "flex", gap: "16px", mb: 3 }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <Typography sx={{ fontSize: "14PX", fontWeight: "400" }}>
              Hub (Inventory)
            </Typography>
            <InputBase
              value={inventoryform.place || ""}
              disabled
              placeholder={isEditMode ? "Hub name" : "Save hub details first"}
              sx={inputStyles}
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: "16px", mb: 3 }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <Typography sx={{ fontSize: "14PX", fontWeight: "400" }}>
              App Title
            </Typography>
            <InputBase
              name="app_title"
              value={appConfig.app_title}
              onChange={handleAppConfigChange}
              placeholder="Browser tab title"
              disabled={isAppConfigReadOnly}
              sx={inputStyles}
            />
            {errors?.app_title && (
              <Typography sx={{ color: "red", fontSize: "12px", mt: 0.5 }}>
                {errors.app_title}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <Typography sx={{ fontSize: "14PX", fontWeight: "400" }}>
              App Short Name
            </Typography>
            <InputBase
              name="app_short_name"
              value={appConfig.app_short_name}
              onChange={handleAppConfigChange}
              placeholder="Short name (PWA)"
              disabled={isAppConfigReadOnly}
              sx={inputStyles}
            />
            {errors?.app_short_name && (
              <Typography sx={{ color: "red", fontSize: "12px", mt: 0.5 }}>
                {errors.app_short_name}
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            mb: 3,
          }}
        >
          <Typography sx={{ fontSize: "14PX", fontWeight: "400" }}>
            App Description
          </Typography>
          <InputBase
            multiline
            minRows={2}
            name="app_description"
            value={appConfig.app_description}
            onChange={handleAppConfigChange}
            placeholder="Short description for manifest and SEO"
            disabled={isAppConfigReadOnly}
            sx={{ ...inputStyles, height: "auto" }}
          />
          {errors?.app_description && (
            <Typography sx={{ color: "red", fontSize: "12px", mt: 0.5 }}>
              {errors.app_description}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap", mb: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography sx={{ fontSize: "14PX", fontWeight: "400" }}>
              Favicon
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: "center" }}>
              <Box
                sx={{ 
                  width: '104px', 
                  height: '104px', 
                  border: '1px solid', 
                  borderColor: 'secondary.border', 
                  borderRadius: '8px', 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  position: 'relative', 
                  cursor: isAppConfigReadOnly ? 'default' : 'pointer', 
                  backgroundColor: 'background.upload', 
                  overflow: 'hidden',
                  '&:hover .overlay': { display: isAppConfigReadOnly ? 'none' : 'block', }, 
                  '&:hover .delete-button': { display: isAppConfigReadOnly ? 'none' : 'flex', },
                }}>
                {((imagePreviews.favicon_url && imagePreviews.favicon_url.trim() !== '') || (appConfig.favicon_url && appConfig.favicon_url.trim() !== '')) ? (
                  <>
                    <img 
                      src={imagePreviews.favicon_url || appConfig.favicon_url} 
                      alt="Favicon" 
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', transition: 'opacity 0.3s', }} 
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <Box className="overlay" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#00000080', display: 'none', borderRadius: '8px', }} />
                    {!isAppConfigReadOnly && (
                      <Tooltip title="Delete" arrow>
                        <IconButton className="delete-button" onClick={() => handleRemoveIconImage('favicon_url')} sx={{ position: 'absolute', top: "50%", left: "50%", display: 'none', transform: 'translate(-50%, -50%)', }}>
                          <DeleteOutlineIcon sx={{ color: "white" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                ) : (
                  <>
                    <input 
                      type="file" 
                      accept="image/*" 
                      id="upload-favicon" 
                      style={{ position: 'absolute', width: '104px', height: '104px', opacity: 0, cursor: 'pointer', top: 0, left: 0, zIndex: 1 }} 
                      onChange={(e) => handleIconImageChange(e, 'favicon_url')} 
                      disabled={themingUploading || isAppConfigReadOnly}
                    />
                    <label htmlFor="upload-favicon" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: isAppConfigReadOnly ? 'default' : 'pointer', zIndex: 0 }}>
                      <AddIcon fontSize="large" />
                      <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}>Upload</Typography>
                    </label>
                  </>
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography sx={{ fontSize: "14PX", fontWeight: "400" }}>
              Brand Icon (Logo)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: "center" }}>
              <Box
                sx={{ 
                  width: '104px', 
                  height: '104px', 
                  border: '1px solid', 
                  borderColor: 'secondary.border', 
                  borderRadius: '8px', 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  position: 'relative', 
                  cursor: isAppConfigReadOnly ? 'default' : 'pointer', 
                  backgroundColor: 'background.upload', 
                  overflow: 'hidden',
                  '&:hover .overlay': { display: isAppConfigReadOnly ? 'none' : 'block', }, 
                  '&:hover .delete-button': { display: isAppConfigReadOnly ? 'none' : 'flex', },
                }}>
                {((imagePreviews.brand_icon_url && imagePreviews.brand_icon_url.trim() !== '') || (appConfig.brand_icon_url && appConfig.brand_icon_url.trim() !== '')) ? (
                  <>
                    <img 
                      src={imagePreviews.brand_icon_url || appConfig.brand_icon_url} 
                      alt="Brand icon" 
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', transition: 'opacity 0.3s', }} 
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <Box className="overlay" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#00000080', display: 'none', borderRadius: '8px', }} />
                    {!isAppConfigReadOnly && (
                      <Tooltip title="Delete" arrow>
                        <IconButton className="delete-button" onClick={() => handleRemoveIconImage('brand_icon_url')} sx={{ position: 'absolute', top: "50%", left: "50%", display: 'none', transform: 'translate(-50%, -50%)', }}>
                          <DeleteOutlineIcon sx={{ color: "white" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                ) : (
                  <>
                    <input 
                      type="file" 
                      accept="image/*" 
                      id="upload-brand-icon" 
                      style={{ position: 'absolute', width: '104px', height: '104px', opacity: 0, cursor: 'pointer', top: 0, left: 0, zIndex: 1 }} 
                      onChange={(e) => handleIconImageChange(e, 'brand_icon_url')} 
                      disabled={themingUploading || isAppConfigReadOnly}
                    />
                    <label htmlFor="upload-brand-icon" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: isAppConfigReadOnly ? 'default' : 'pointer', zIndex: 0 }}>
                      <AddIcon fontSize="large" />
                      <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}>Upload</Typography>
                    </label>
                  </>
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography sx={{ fontSize: "14PX", fontWeight: "400" }}>
              App Icon
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: "center" }}>
              <Box
                sx={{ 
                  width: '104px', 
                  height: '104px', 
                  border: '1px solid', 
                  borderColor: 'secondary.border', 
                  borderRadius: '8px', 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  position: 'relative', 
                  cursor: isAppConfigReadOnly ? 'default' : 'pointer', 
                  backgroundColor: 'background.upload', 
                  overflow: 'hidden',
                  '&:hover .overlay': { display: isAppConfigReadOnly ? 'none' : 'block', }, 
                  '&:hover .delete-button': { display: isAppConfigReadOnly ? 'none' : 'flex', },
                }}>
                {((imagePreviews.app_icon_url && imagePreviews.app_icon_url.trim() !== '') || (appConfig.app_icon_url && appConfig.app_icon_url.trim() !== '')) ? (
                  <>
                    <img 
                      src={imagePreviews.app_icon_url || appConfig.app_icon_url} 
                      alt="App icon" 
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', transition: 'opacity 0.3s', }} 
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <Box className="overlay" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#00000080', display: 'none', borderRadius: '8px', }} />
                    {!isAppConfigReadOnly && (
                      <Tooltip title="Delete" arrow>
                        <IconButton className="delete-button" onClick={() => handleRemoveIconImage('app_icon_url')} sx={{ position: 'absolute', top: "50%", left: "50%", display: 'none', transform: 'translate(-50%, -50%)', }}>
                          <DeleteOutlineIcon sx={{ color: "white" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                ) : (
                  <>
                    <input 
                      type="file" 
                      accept="image/*" 
                      id="upload-app-icon" 
                      style={{ position: 'absolute', width: '104px', height: '104px', opacity: 0, cursor: 'pointer', top: 0, left: 0, zIndex: 1 }} 
                      onChange={(e) => handleIconImageChange(e, 'app_icon_url')} 
                      disabled={themingUploading || isAppConfigReadOnly}
                    />
                    <label htmlFor="upload-app-icon" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: isAppConfigReadOnly ? 'default' : 'pointer', zIndex: 0 }}>
                      <AddIcon fontSize="large" />
                      <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}>Upload</Typography>
                    </label>
                  </>
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography sx={{ fontSize: "14PX", fontWeight: "400" }}>
              Apple Touch Icon
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: "center" }}>
              <Box
                sx={{ 
                  width: '104px', 
                  height: '104px', 
                  border: '1px solid', 
                  borderColor: 'secondary.border', 
                  borderRadius: '8px', 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  position: 'relative', 
                  cursor: isAppConfigReadOnly ? 'default' : 'pointer', 
                  backgroundColor: 'background.upload', 
                  overflow: 'hidden',
                  '&:hover .overlay': { display: isAppConfigReadOnly ? 'none' : 'block', }, 
                  '&:hover .delete-button': { display: isAppConfigReadOnly ? 'none' : 'flex', },
                }}>
                {((imagePreviews.apple_touch_icon_url && imagePreviews.apple_touch_icon_url.trim() !== '') || (appConfig.apple_touch_icon_url && appConfig.apple_touch_icon_url.trim() !== '')) ? (
                  <>
                    <img 
                      src={imagePreviews.apple_touch_icon_url || appConfig.apple_touch_icon_url} 
                      alt="Apple touch icon" 
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', transition: 'opacity 0.3s', }} 
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <Box className="overlay" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#00000080', display: 'none', borderRadius: '8px', }} />
                    {!isAppConfigReadOnly && (
                      <Tooltip title="Delete" arrow>
                        <IconButton className="delete-button" onClick={() => handleRemoveIconImage('apple_touch_icon_url')} sx={{ position: 'absolute', top: "50%", left: "50%", display: 'none', transform: 'translate(-50%, -50%)', }}>
                          <DeleteOutlineIcon sx={{ color: "white" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                ) : (
                  <>
                    <input 
                      type="file" 
                      accept="image/*" 
                      id="upload-apple-touch-icon" 
                      style={{ position: 'absolute', width: '104px', height: '104px', opacity: 0, cursor: 'pointer', top: 0, left: 0, zIndex: 1 }} 
                      onChange={(e) => handleIconImageChange(e, 'apple_touch_icon_url')} 
                      disabled={themingUploading || isAppConfigReadOnly}
                    />
                    <label htmlFor="upload-apple-touch-icon" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: isAppConfigReadOnly ? 'default' : 'pointer', zIndex: 0 }}>
                      <AddIcon fontSize="large" />
                      <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}>Upload</Typography>
                    </label>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: "32px", alignItems: "flex-start" }}>
          <Box sx={{ flex: 1 }}>

        <Box sx={{ display: "flex", gap: "16px", mb: 3 }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <Typography sx={{ fontSize: "14px", fontWeight: "400" }}>
              Theme Color
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Box
                onClick={() => { if (!isAppConfigReadOnly) setShowThemeColorPicker(prev => !prev); }}
                sx={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "4px",
                  backgroundColor: appConfig.theme_color || "#ffffff",
                  border: "1px solid #d9d9d9",
                  cursor: isAppConfigReadOnly ? "not-allowed" : "pointer",
                  opacity: isAppConfigReadOnly ? 0.5 : 1,
                }}
              />
              <InputBase
                name="theme_color"
                value={appConfig.theme_color}
                onChange={handleAppConfigChange}
                placeholder="#ffffff"
                disabled={isAppConfigReadOnly}
                sx={{ ...inputStyles, flex: 1 }}
              />
            </Box>
            {showThemeColorPicker && (
              <Box sx={{ position: "relative", zIndex: 2 }}>
                <Box
                  sx={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0 }}
                  onClick={() => setShowThemeColorPicker(false)}
                />
                <Box sx={{ position: "absolute", zIndex: 3 }}>
                  <SketchPicker
                    color={appConfig.theme_color || "#ffffff"}
                    onChangeComplete={(color) => {
                      handleAppConfigChange({ target: { name: "theme_color", value: color.hex } });
                    }}
                  />
                </Box>
              </Box>
            )}
            {errors?.theme_color && (
              <Typography sx={{ color: "red", fontSize: "12px", mt: 0.5 }}>
                {errors.theme_color}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: "16px", mb: 3 }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <Typography sx={{ fontSize: "14px", fontWeight: "400" }}>
              Background Color
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Box
                onClick={() => { if (!isAppConfigReadOnly) setShowBgColorPicker(prev => !prev); }}
                sx={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "4px",
                  backgroundColor: appConfig.background_color || "#f5f5f5",
                  border: "1px solid #d9d9d9",
                  cursor: isAppConfigReadOnly ? "not-allowed" : "pointer",
                  opacity: isAppConfigReadOnly ? 0.5 : 1,
                }}
              />
              <InputBase
                name="background_color"
                value={appConfig.background_color}
                onChange={handleAppConfigChange}
                placeholder="#f5f5f5"
                disabled={isAppConfigReadOnly}
                sx={{ ...inputStyles, flex: 1 }}
              />
            </Box>
            {showBgColorPicker && (
              <Box sx={{ position: "relative", zIndex: 2 }}>
                <Box
                  sx={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0 }}
                  onClick={() => setShowBgColorPicker(false)}
                />
                <Box sx={{ position: "absolute", zIndex: 3 }}>
                  <SketchPicker
                    color={appConfig.background_color || "#f5f5f5"}
                    onChangeComplete={(color) => {
                      handleAppConfigChange({ target: { name: "background_color", value: color.hex } });
                    }}
                  />
                </Box>
              </Box>
            )}
            {errors?.background_color && (
              <Typography sx={{ color: "red", fontSize: "12px", mt: 0.5 }}>
                {errors.background_color}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: "16px", mb: 3 }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <Typography sx={{ fontSize: "14PX", fontWeight: "400" }}>
              Footer Text
            </Typography>
            <InputBase
              name="footer_text"
              value={appConfig.footer_text}
              onChange={handleAppConfigChange}
              placeholder="Footer text"
              disabled={isAppConfigReadOnly}
              sx={inputStyles}
            />
          </Box>
        </Box>

        {/* Update Button - Only visible for merchants when published, or for other roles (hidden for super admin) */}
        {((user_role === 'merchant' && currentOnboardingStatus === 'published') || (user_role !== 'merchant' && user_role !== 'super_admin')) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              mt: 3,
              mb: 2,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleCombinedUpdate}
              disabled={loading || !(user_role === 'merchant' && currentOnboardingStatus === 'published' ? hasAllowedChanges : hasUnsavedChanges)}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                padding: '6px 16px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '&:hover': {
                  boxShadow: hasUnsavedChanges ? '0 6px 16px rgba(0,0,0,0.2)' : '0 4px 12px rgba(0,0,0,0.15)',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(63, 81, 181, 0.3)',
                  color: 'rgba(63, 81, 181, 0.6)',
                  opacity: 0.7,
                },
              }}
            >
              {loading ? 'Updating...' : 'Update'}
            </Button>
          </Box>
        )}

          </Box>

          {/* Right: Mobile Preview */}
          <Box sx={{ width: "260px", flexShrink: 0, mt: "-120px" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: "500", mb: "10px" }}>
              Preview
            </Typography>
            {/* Phone Frame */}
            <Box
              sx={{
                width: "220px",
                height: "420px",
                border: "4px solid #1a1a1a",
                borderRadius: "30px",
                overflow: "hidden",
                backgroundColor: appConfig.background_color || "#f5f5f5",
                boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                position: "relative",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Notch */}
              <Box
                sx={{
                  width: "70px",
                  height: "18px",
                  backgroundColor: "#1a1a1a",
                  borderRadius: "0 0 14px 14px",
                  margin: "0 auto",
                }}
              />

              {/* App Header */}
              <Box
                sx={{
                  backgroundColor: appConfig.theme_color || "#1976d2",
                  padding: "10px 14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    color: "#fff",
                    fontSize: "12px",
                    fontWeight: "600",
                    letterSpacing: "0.3px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {appConfig.app_title || "Your Store"}
                </Typography>
              </Box>

              {/* Content Area */}
              <Box
                sx={{
                  flex: 1,
                  padding: "10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  overflowY: "hidden",
                }}
              >
                {/* Product Card 1 */}
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    padding: "8px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    display: "flex",
                    gap: "8px",
                  }}
                >
                  <Box
                    sx={{
                      width: "50px",
                      height: "50px",
                      backgroundColor: "#e8e8e8",
                      borderRadius: "6px",
                      flexShrink: 0,
                    }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                  >
                    <Box sx={{ width: "80%", height: "7px", backgroundColor: "#ccc", borderRadius: "3px" }} />
                    <Box sx={{ width: "50%", height: "7px", backgroundColor: "#e0e0e0", borderRadius: "3px" }} />
                    <Box
                      sx={{
                        width: "fit-content",
                        backgroundColor: appConfig.theme_color || "#1976d2",
                        color: "#fff",
                        padding: "2px 10px",
                        borderRadius: "4px",
                        fontSize: "8px",
                        fontWeight: "600",
                        mt: "2px",
                      }}
                    >
                      Add
                    </Box>
                  </Box>
                </Box>

                {/* Product Card 2 */}
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    padding: "8px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    display: "flex",
                    gap: "8px",
                  }}
                >
                  <Box
                    sx={{
                      width: "50px",
                      height: "50px",
                      backgroundColor: "#e8e8e8",
                      borderRadius: "6px",
                      flexShrink: 0,
                    }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                  >
                    <Box sx={{ width: "65%", height: "7px", backgroundColor: "#ccc", borderRadius: "3px" }} />
                    <Box sx={{ width: "40%", height: "7px", backgroundColor: "#e0e0e0", borderRadius: "3px" }} />
                    <Box
                      sx={{
                        width: "fit-content",
                        backgroundColor: appConfig.theme_color || "#1976d2",
                        color: "#fff",
                        padding: "2px 10px",
                        borderRadius: "4px",
                        fontSize: "8px",
                        fontWeight: "600",
                        mt: "2px",
                      }}
                    >
                      Add
                    </Box>
                  </Box>
                </Box>

                {/* Product Card 3 */}
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    padding: "8px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    display: "flex",
                    gap: "8px",
                  }}
                >
                  <Box
                    sx={{
                      width: "50px",
                      height: "50px",
                      backgroundColor: "#e8e8e8",
                      borderRadius: "6px",
                      flexShrink: 0,
                    }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                  >
                    <Box sx={{ width: "75%", height: "7px", backgroundColor: "#ccc", borderRadius: "3px" }} />
                    <Box sx={{ width: "45%", height: "7px", backgroundColor: "#e0e0e0", borderRadius: "3px" }} />
                    <Box
                      sx={{
                        width: "fit-content",
                        backgroundColor: appConfig.theme_color || "#1976d2",
                        color: "#fff",
                        padding: "2px 10px",
                        borderRadius: "4px",
                        fontSize: "8px",
                        fontWeight: "600",
                        mt: "2px",
                      }}
                    >
                      Add
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Bottom Cart Button */}
              <Box
                sx={{
                  padding: "8px 10px",
                  borderTop: "1px solid #e0e0e0",
                  backgroundColor: "#fff",
                }}
              >
                <Box
                  sx={{
                    backgroundColor: appConfig.theme_color || "#1976d2",
                    color: "#fff",
                    padding: "7px 14px",
                    borderRadius: "6px",
                    textAlign: "center",
                    fontSize: "10px",
                    fontWeight: "600",
                  }}
                >
                  View Cart
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {user_role !== 'super_admin' && !isMerchantWithPublishedStatus && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              mt: 0,
            }}
          >
            <Button
              type="button"
              variant="contained"
              sx={{ textTransform: "none", height: "32px", minWidth: "120px" }}
              onClick={() => handleSaveAppConfig()}
              disabled={appConfigLoading || currentOnboardingStatus === 'phase2_submitted'}
            >
              {appConfigLoading 
                ? (currentOnboardingStatus === 'phase1_approved' ? "Submitting for Phase 2..." : (isEditMode ? "Submitting..." : "Saving..."))
                : (currentOnboardingStatus === 'phase1_approved' ? "Submit for Phase 2 Approval" : (isEditMode ? "Submit for Approval" : "Save App Config"))}
            </Button>
          </Box>
        )}
          </TabPanel>
        </TabContext>
        </Box>
        </>
      )}
        </>
      )}
    </Box>
  );
}


